/**
 * FikoImporter.STP.ts - STEP File Importer with Performance Optimizations
 *
 * PERFORMANCE OPTIMIZATIONS (Feb 2026):
 * =====================================
 * 1. **Buffer Array Caching**: Default alpha/color arrays are cached and reused
 *    - Reduces allocations in hot paths (per-mesh/per-edge operations)
 *    - Cache size limited to 50 most common sizes (LRU-style eviction)
 *    - Typical savings: 4-6 allocations per mesh imported
 *
 * 2. **Zero-Copy for Defaults**: Cached arrays are pre-filled with default values
 *    - Alpha arrays: pre-filled with 1.0
 *    - Color arrays: pre-filled with 0.0
 *    - Eliminates fill() calls and per-vertex assignments in loops
 *
 * 3. **Safe Copying**: Cached arrays are copied before use (geometry takes ownership)
 *    - Maintains safety while reducing allocation overhead
 *    - TypedArray copies are fast (native memcpy)
 *
 * To clear caches after large imports: FikoImporterSTP.clearCaches()
 */

import * as THREE from 'three';
import { FikoCizimYalin } from './FikoCizimYalin';
import { FikoObje3D } from './FikoObje3D';
import { FikoGrup } from './FikoGrup';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {
  parseStepTopologyWasm,
  resolveColorChainCached,

  clearColorCache,
} from '../stp-parser/stp-parser-wasm-bridge';
import { getGPUImportPipeline } from './fiko-performance/FikoGPUImportPipeline';
import { FikoStepCache } from './fiko-performance/FikoStepCache';
import { FikoTimeSlicedExecutor } from './fiko-performance/FikoTimeSlicedExecutor';
import { FikoMeshOptimizer } from './fiko-performance/FikoMeshOptimizer';
import { FikoBatchedMeshManager } from './fiko-performance/FikoBatchedMeshManager';
import {
    FikoOpenCascadeBridge,
    getOpenCascadeBridge,
    OCCTColoredResult,
    OCCTMeshData,
    TessellationParams
} from './FikoOpenCascadeBridge';
import { STPImportOptions } from './stp-import-dialog';
import { FikoWasmBridge } from './fiko-performance/FikoWasmBridge';

// ⚡ PERFORMANS: Pre-allocated buffers for STEP import (zero-allocation loops)
// These temp vectors are reused in hot paths to avoid allocations during geometry processing
const _stpTmpVec3A = new THREE.Vector3();
const _stpTmpVec3B = new THREE.Vector3();
const _stpTmpVec3C = new THREE.Vector3();
const _stpTmpVec3D = new THREE.Vector3();
const _stpTmpVec3E = new THREE.Vector3();
const _stpTmpVec3F = new THREE.Vector3();

/**
 * STEP dosyasından parse edilen topoloji bilgisi
 */
export interface StepTopologyInfo {
    /** Face ID → Surface bilgisi */
    faces: Map<string, StepFaceInfo>;
    /** Edge ID → Curve bilgisi */
    edges: Map<string, StepEdgeInfo>;
    /** Radius grupları: radius → edge ID listesi */
    radiusGroups: Map<number, string[]>;
}

/**
 * Radyüs gruplama bilgisi
 * OpenCascade'deki gibi aynı radyüse sahip face/edge'leri gruplar
 */
export interface RadiusGroupInfo {
    /** Radyüs değeri (mm cinsinden, 2 ondalık yuvarlanmış) */
    radius: number;
    /** Bu radyüse sahip face indeksleri */
    faceIndices: number[];
    /** Bu radyüse sahip edge indeksleri */
    edgeIndices: number[];
    /** Yüzey tipi (CYLINDER, SPHERE, TORUS vb.) */
    surfaceType: string;
    /** Renk (highlight için) */
    color?: { r: number; g: number; b: number };
}

export interface StepFaceInfo {
    id: string;
    surfaceType: 'PLANE' | 'CYLINDRICAL_SURFACE' | 'SPHERICAL_SURFACE' | 'TOROIDAL_SURFACE' | 'CONICAL_SURFACE' | 'B_SPLINE_SURFACE' | 'UNKNOWN';
    radius?: number;       // Silindir/küre için
    majorRadius?: number;  // Torus için
    minorRadius?: number;  // Torus için
    semiAngle?: number;    // Koni için
    normal?: THREE.Vector3; // Düzlem için
    center?: THREE.Vector3; // Merkez noktası
    axis?: THREE.Vector3;   // Eksen yönü
    adjacentFaces?: string[]; // Bu face'e bağlı diğer face'ler
}

export interface StepEdgeInfo {
    id: string;
    curveType: 'LINE' | 'CIRCLE' | 'ELLIPSE' | 'B_SPLINE_CURVE' | 'TRIMMED_CURVE' | 'UNKNOWN';
    radius?: number;       // Daire/elips için
    startPoint?: THREE.Vector3;
    endPoint?: THREE.Vector3;
    center?: THREE.Vector3; // Daire için merkez
    axis?: THREE.Vector3;   // Daire için eksen
    length?: number;
    /** B-spline kontrol noktaları (fillet/chamfer eğrileri için) */
    controlPoints?: THREE.Vector3[];
    /** B-spline derece (degree) */
    degree?: number;
    /** B-spline knot vektörü */
    knots?: number[];
    /** Bu edge hangi face'lere ait */
    adjacentFaces: string[];
}

/**
 * Progressive/streaming import progress info.
 * Fired after each batch of meshes is processed and rendered.
 */
export interface StpImportProgress {
    /** Current import phase */
    phase: 'parsing' | 'geometry' | 'materials' | 'assembly';
    /** Overall progress percentage (0-100) */
    percent: number;
    /** Number of meshes loaded so far */
    meshesLoaded: number;
    /** Total number of meshes in the file */
    totalMeshes: number;
}

/**
 * FikoImporterSTP - occt-import-js entegrasyon ve FikoSecililer seçim sistemi
 *
 * ✅ WRL İLE UYUMLU FORMAT:
 * grup attribute: [vertex_start_index, total_vertex_count, group_id]
 * Bu format FikoImporter.WRL ile 100% uyumludur!
 *
 * ✅ PER-FACE SEÇİM:
 *   - occt-import-js'den gelen brep_faces mapping'i kullanır
 *   - Her yüzey (face) için triangle index aralığı: face.first → face.last
 *   - Geometriye "grup" attribute ekler: [vertex_idx, vertexCount, faceId]
 *   - FikoSecililer.kesistiYuzey() ile face-by-face highlight oluşturur
 *
 * ✅ PER-EDGE SEÇİM:
 *   - extractEdgesFromMesh() ile sharp edge'leri tespit eder
 *   - Her edge line (2 vertex) için ayrı grup ID atar
 *   - Edge geometrisine "grup" attribute ekler: [vertex_idx, edgeVertexCount, edgeId]
 *   - FikoSecililer.kesistiRegularLineSegments() ile edge-by-edge highlight oluşturur
 *
 * 📌 FORMAT STANDARDI (WRL ile aynı):
 *   - grup.push(vertex_start_idx, total_vertex_count, group_id) per vertex
 *   - brep_faces metadata: cizim.brep_faces = mesh.brep_faces
 *   - FikoCizimYalin geometrisinde position, normal, color, alpha, grup attributes
 */
export class FikoImporterSTP {

    // ==================== PERFORMANCE OPTIMIZATION CACHES ====================
    // Static buffer caches to reduce allocations during import operations

    /** Cache for default alpha arrays (filled with 1.0) - indexed by vertex count */
    private static _alphaArrayCache = new Map<number, Float32Array>();

    /** Cache for default color arrays (filled with 0.0) - indexed by vertex count */
    private static _colorArrayCache = new Map<number, Float32Array>();

    /** Maximum cache size to prevent memory bloat (keep 50 most common sizes) */
    private static readonly MAX_CACHE_SIZE = 50;

    /**
     * Get or create a default alpha array (all 1.0 values)
     * Arrays are cached and reused for common vertex counts
     * NOTE: Returns a COPY for safety - caller can modify freely
     */
    private static getDefaultAlphaArray(vertexCount: number): Float32Array {
        let cached = this._alphaArrayCache.get(vertexCount);
        if (!cached) {
            cached = new Float32Array(vertexCount).fill(1.0);

            // LRU-style cache management: if cache is full, remove random entry
            if (this._alphaArrayCache.size >= this.MAX_CACHE_SIZE) {
                const firstKey = this._alphaArrayCache.keys().next().value;
                this._alphaArrayCache.delete(firstKey);
            }

            this._alphaArrayCache.set(vertexCount, cached);
        }

        // Return a copy for safety (geometry takes ownership)
        return new Float32Array(cached);
    }

    /**
     * Get or create a default color array (all 0.0 values)
     * Arrays are cached and reused for common vertex counts
     * NOTE: Returns a COPY for safety - caller can modify freely
     */
    private static getDefaultColorArray(vertexCount: number): Float32Array {
        let cached = this._colorArrayCache.get(vertexCount);
        if (!cached) {
            cached = new Float32Array(vertexCount * 3); // RGB = 3 components

            // LRU-style cache management
            if (this._colorArrayCache.size >= this.MAX_CACHE_SIZE) {
                const firstKey = this._colorArrayCache.keys().next().value;
                this._colorArrayCache.delete(firstKey);
            }

            this._colorArrayCache.set(vertexCount, cached);
        }

        // Return a copy for safety
        return new Float32Array(cached);
    }

    /**
     * Clear all static caches (useful for memory management after large imports)
     */
    public static clearCaches(): void {
        this._alphaArrayCache.clear();
        this._colorArrayCache.clear();
        // Also clear FikoCizimYalin material cache
        FikoCizimYalin.clearMaterialCache();
    }

    // ==================== END PERFORMANCE CACHES ====================

    private occtWasm: any = null;
    private occtWorker: Worker | null = null;
    private useOCCT: boolean = true; // OCCT kullanımı için flag (VARSAYILAN: AÇIK)
    private useWorker: boolean = true; // Web Worker kullanımı (performans için)

    // Performance optimization instances
    private timeSlicedExecutor = new FikoTimeSlicedExecutor();
    private meshOptimizer = new FikoMeshOptimizer();
    private batchedMeshManager = new FikoBatchedMeshManager();
    private stpOcctWorker: Worker | null = null;

    /**
     * Tessellation kalite parametreleri (occt-import-js icin).
     * Dialog'dan veya programatik olarak ayarlanabilir.
     * Varsayilan: yuksek kalite (linearDeflection: 0.01, angularDeflection: 0.1)
     */
    public tessellationKalitesi: {
        linearDeflection: number;
        angularDeflection: number;
    } = {
        linearDeflection: 0.01,
        angularDeflection: 0.1,
    };

    /**
     * Tessellation kalite seviyesini preset ile ayarla.
     * occt-import-js (ana import yontemi) icin gecerlidir.
     *
     * Preset'ler:
     * - 'ultra-hizli': On izleme, cok buyuk dosyalar (linear: 2.0, angular: 1.0)
     * - 'hizli': Buyuk dosyalar (linear: 0.5, angular: 0.8)
     * - 'normal': Standart kalite (linear: 0.1, angular: 0.5)
     * - 'yuksek': Detayli gorunum - varsayilan (linear: 0.01, angular: 0.1)
     * - 'ultra': Maksimum detay (linear: 0.005, angular: 0.05)
     * - 'adaptif': Dosya boyutuna gore otomatik secim
     */
    public tessellationKalitesiAyarla(
        preset: 'ultra-hizli' | 'hizli' | 'normal' | 'yuksek' | 'ultra' | 'adaptif',
        dosyaBoyutu?: number
    ): void {
        switch (preset) {
            case 'ultra-hizli':
                this.tessellationKalitesi = { linearDeflection: 2.0, angularDeflection: 1.0 };
                break;
            case 'hizli':
                this.tessellationKalitesi = { linearDeflection: 0.5, angularDeflection: 0.8 };
                break;
            case 'normal':
                this.tessellationKalitesi = { linearDeflection: 0.1, angularDeflection: 0.5 };
                break;
            case 'yuksek':
                this.tessellationKalitesi = { linearDeflection: 0.01, angularDeflection: 0.1 };
                break;
            case 'ultra':
                this.tessellationKalitesi = { linearDeflection: 0.005, angularDeflection: 0.05 };
                break;
            case 'adaptif':
                // Dosya boyutuna gore otomatik kalite secimi
                if (dosyaBoyutu) {
                    const mb = dosyaBoyutu / (1024 * 1024);
                    if (mb > 100) {
                        this.tessellationKalitesiAyarla('ultra-hizli');
                    } else if (mb > 30) {
                        this.tessellationKalitesiAyarla('hizli');
                    } else if (mb > 5) {
                        this.tessellationKalitesiAyarla('normal');
                    } else if (mb > 0.5) {
                        this.tessellationKalitesiAyarla('yuksek');
                    } else {
                        this.tessellationKalitesiAyarla('ultra');
                    }
                } else {
                    this.tessellationKalitesiAyarla('normal');
                }
                break;
        }
    }

    /** Progress callback - yukleme ilerlemesi icin */
    public onProgress?: (mesaj: string, progress: number) => void;

    /**
     * Streaming/progressive import progress callback.
     * Called after each batch of meshes is processed, providing structured phase info.
     */
    public onStreamingProgress?: (progress: StpImportProgress) => void;

    /**
     * Called after each batch of meshes is added to the scene during progressive import.
     * Use this to trigger a render / camera fit so partial geometry appears immediately.
     * @param newMeshes The FikoCizimYalin instances just added in this batch
     * @param allMeshes All FikoCizimYalin instances loaded so far
     */
    public onBatchRendered?: (newMeshes: FikoCizimYalin[], allMeshes: FikoCizimYalin[]) => void;

    /** Set to true to cancel an in-progress import */
    private _importCancelled = false;

    /**
     * Cancel an ongoing progressive import.
     * Already-rendered meshes will remain in the scene; remaining meshes are skipped.
     */
    public cancelImport(): void {
        this._importCancelled = true;
    }

    /** Batch size for progressive rendering (meshes per batch). Default 10. */
    public progressiveBatchSize = 10;

    /** Cross-session STEP tesselation cache (IndexedDB). Aynı dosyayı yeniden açarken OCCT'yi atlar. */
    public geometriCacheAktif = true;

    /** Dialog'dan gelen edge threshold açısı (derece). Default 30. */
    private seciliEdgeThresholdAngle: number = 30;

    constructor(public sahip: FikoObje3D = null) {

    }

    /**
     * Import sonrası grup/objeye dialog opsiyonlarını uygular.
     * scale/center/fit/material ayarlarını tek noktadan işler.
     */
    private uygulaImportAyarlari(obje: any, opts: Partial<STPImportOptions>): void {
        if (!obje) return;

        const scaleFactor = opts.scaleFactor;
        if (scaleFactor !== undefined && scaleFactor !== null && scaleFactor !== 1) {
            obje.scale?.multiplyScalar(scaleFactor);
            obje.updateMatrixWorld?.(true);
        }

        let bBox: THREE.Box3 | null = null;
        let bCenter: THREE.Vector3 | null = null;
        if (opts.autoCenter === true || opts.autoFit === true) {
            bBox = new THREE.Box3().setFromObject(obje);
            bCenter = bBox.getCenter(new THREE.Vector3());
        }

        if (opts.autoCenter === true && bCenter) {
            obje.position?.sub(bCenter);
            obje.updateMatrixWorld?.(true);
            bBox = new THREE.Box3().setFromObject(obje);
            bCenter = bBox.getCenter(new THREE.Vector3());
        }

        const doubleSided = opts.doubleSided === true;
        const smoothNormals = opts.smoothNormals === true;
        const opacity = opts.defaultOpacity ?? 1;
        const uygulanacakOpacity = opacity < 1;
        const varsayilanRenk = opts.defaultColor;

        if (doubleSided || smoothNormals || uygulanacakOpacity || varsayilanRenk) {
            obje.traverse?.((child: any) => {
                if (smoothNormals && child?.geometry?.attributes?.position) {
                    try { child.geometry.computeVertexNormals(); } catch (_) { /* ignore */ }
                }
                const materials = Array.isArray(child?.material)
                    ? child.material
                    : (child?.material ? [child.material] : []);
                for (const mat of materials) {
                    if (!mat) continue;
                    if (doubleSided) {
                        mat.side = THREE.DoubleSide;
                        mat.needsUpdate = true;
                    }
                    if (uygulanacakOpacity) {
                        mat.transparent = true;
                        mat.opacity = opacity;
                        mat.needsUpdate = true;
                    }
                    if (varsayilanRenk && mat.color && child?.userData?.xcafColor !== true) {
                        try { mat.color.set(varsayilanRenk); } catch (_) { /* ignore */ }
                    }
                }
            });
        }

        if (opts.autoFit === true && bBox && bCenter) {
            const anaGosterim = this.sahip?.sahne?.anaGosterim;
            const kamera = anaGosterim?.aktifKamera;
            const kontrol = anaGosterim?.aktifKontrol;
            if (kamera) {
                const size = bBox.getSize(new THREE.Vector3());
                const maxDim = Math.max(size.x, size.y, size.z) || 1;
                const fov = (kamera.fov ?? 45) * (Math.PI / 180);
                const distance = Math.abs(maxDim / Math.sin(fov / 2)) * 0.75;

                kamera.position.set(
                    bCenter.x + distance * 0.5,
                    bCenter.y + distance * 0.5,
                    bCenter.z + distance * 0.5
                );
                kamera.lookAt?.(bCenter);
                kamera.updateProjectionMatrix?.();

                if (kontrol) {
                    kontrol.target?.copy(bCenter);
                    kontrol.update?.();
                }
                anaGosterim?.ciz?.();
            }
        }
    }

    /**
     * UI thread'i bloke etmemek için async yield fonksiyonu
     * Her N iterasyonda bir event loop'a kontrol verir
     */
    private async yieldToMain(): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, 0));
    }

    /**
     * Batch işlemler için chunk'lı async iterator
     * @param items İşlenecek array
     * @param chunkSize Her chunk'ta kaç item işlenecek
     * @param processor Her item için çağrılacak fonksiyon
     * @param onProgress İlerleme callback'i
     */
    private async processInChunks<T, R>(
        items: T[],
        chunkSize: number,
        processor: (item: T, index: number) => R,
        onProgress?: (processed: number, total: number) => void
    ): Promise<R[]> {
        const results: R[] = [];
        const total = items.length;

        for (let i = 0; i < total; i += chunkSize) {
            const end = Math.min(i + chunkSize, total);

            // Bu chunk'ı işle
            for (let j = i; j < end; j++) {
                results.push(processor(items[j], j));
            }

            // Her chunk sonunda UI'a kontrol ver
            if (i + chunkSize < total) {
                onProgress?.(end, total);
                await this.yieldToMain();
            }
        }

        return results;
    }

    /**
     * OCCT-WASM kullanımını aktif/pasif yapar
     * @param enabled true: OCCT ile yükle, false: FreeCAD API ile yükle
     */
    public setOCCTEnabled(enabled: boolean) {
        this.useOCCT = enabled;
    }

    /**
     * Mesh'ten sharp edge'leri çıkarır (normal açısına göre) - Sync version
     * Per-edge seçim ve highlight için grup attribute'ü ile
     * @param geometry Mesh geometrisi
     * @param thresholdAngle Eşik açısı (derece) - varsayılan 30
     * @returns Edge geometrisi (grup attribute'ü ile per-edge seçimi destekli)
     */
    private extractEdgesFromMesh(geometry: THREE.BufferGeometry, thresholdAngle: number = 30): THREE.BufferGeometry {
        return this.extractEdgesFromMeshSyncInternal(geometry, thresholdAngle);
    }

    /**
     * Internal sync implementation for backward compatibility
     */
    private extractEdgesFromMeshSyncInternal(geometry: THREE.BufferGeometry, thresholdAngle: number = 30): THREE.BufferGeometry {
        const edgeGeometry = new THREE.BufferGeometry();

        if (!geometry.attributes['position']) {
            return edgeGeometry;
        }

        const positions = geometry.attributes['position'];
        const normals = geometry.attributes['normal'];

        if (!normals) {
            return edgeGeometry;
        }

        const vertexCount = positions.count;
        const triCount = Math.floor(vertexCount / 3);
        const thresholdCos = Math.cos(THREE.MathUtils.degToRad(thresholdAngle));

        interface EdgeInfoOpt {
            p1x: number; p1y: number; p1z: number;
            p2x: number; p2y: number; p2z: number;
            nx: number; ny: number; nz: number;
            count: number;
            isSharp: boolean;
        }
        const edgeMap = new Map<number, EdgeInfoOpt>();
        const PRECISION = 100000;

        const hashEdge = (p1x: number, p1y: number, p1z: number, p2x: number, p2y: number, p2z: number): number => {
            const h1 = Math.round(p1x * PRECISION) ^ (Math.round(p1y * PRECISION) << 10) ^ (Math.round(p1z * PRECISION) << 20);
            const h2 = Math.round(p2x * PRECISION) ^ (Math.round(p2y * PRECISION) << 10) ^ (Math.round(p2z * PRECISION) << 20);
            return h1 < h2 ? h1 * 1000000007 + h2 : h2 * 1000000007 + h1;
        };

        const posArray = positions.array;
        const edge1 = new THREE.Vector3();
        const edge2 = new THREE.Vector3();
        const normal = new THREE.Vector3();

        for (let t = 0; t < triCount; t++) {
            const i = t * 9;
            const v0x = posArray[i], v0y = posArray[i + 1], v0z = posArray[i + 2];
            const v1x = posArray[i + 3], v1y = posArray[i + 4], v1z = posArray[i + 5];
            const v2x = posArray[i + 6], v2y = posArray[i + 7], v2z = posArray[i + 8];

            edge1.set(v1x - v0x, v1y - v0y, v1z - v0z);
            edge2.set(v2x - v0x, v2y - v0y, v2z - v0z);
            normal.crossVectors(edge1, edge2).normalize();

            const nx = normal.x, ny = normal.y, nz = normal.z;

            const edges: [number, number, number, number, number, number][] = [
                [v0x, v0y, v0z, v1x, v1y, v1z],
                [v1x, v1y, v1z, v2x, v2y, v2z],
                [v2x, v2y, v2z, v0x, v0y, v0z]
            ];

            for (const [p1x, p1y, p1z, p2x, p2y, p2z] of edges) {
                const key = hashEdge(p1x, p1y, p1z, p2x, p2y, p2z);
                const existing = edgeMap.get(key);

                if (existing) {
                    existing.count++;
                    const dot = nx * existing.nx + ny * existing.ny + nz * existing.nz;
                    if (dot < thresholdCos && existing.count === 2) {
                        existing.isSharp = true;
                    }
                } else {
                    edgeMap.set(key, { p1x, p1y, p1z, p2x, p2y, p2z, nx, ny, nz, count: 1, isSharp: false });
                }
            }
        }

        const edgeLines: number[] = [];
        edgeMap.forEach((info) => {
            if (info.isSharp || info.count === 1) {
                if (isFinite(info.p1x) && isFinite(info.p1y) && isFinite(info.p1z) &&
                    isFinite(info.p2x) && isFinite(info.p2y) && isFinite(info.p2z)) {
                    edgeLines.push(info.p1x, info.p1y, info.p1z, info.p2x, info.p2y, info.p2z);
                }
            }
        });

        if (edgeLines.length > 0) {
            const edgeVertexCount = edgeLines.length / 3;
            edgeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(edgeLines, 3));
            const totalEdges = edgeVertexCount / 2;

            // OPTIMIZED: Use cached default arrays (zero allocations for defaults)
            const edgeColors = FikoImporterSTP.getDefaultColorArray(edgeVertexCount);
            const edgeAlphas = FikoImporterSTP.getDefaultAlphaArray(edgeVertexCount);
            const edgeGroups = new Float32Array(edgeVertexCount * 3);

            for (let edgeId = 0; edgeId < totalEdges; edgeId++) {
                const v1 = edgeId * 2;
                const v2 = v1 + 1;
                edgeGroups[v1 * 3] = v1; edgeGroups[v1 * 3 + 1] = 2; edgeGroups[v1 * 3 + 2] = edgeId;
                edgeGroups[v2 * 3] = v1; edgeGroups[v2 * 3 + 1] = 2; edgeGroups[v2 * 3 + 2] = edgeId;
            }

            edgeGeometry.setAttribute('color', new THREE.BufferAttribute(edgeColors, 3));
            edgeGeometry.setAttribute('alpha', new THREE.BufferAttribute(edgeAlphas, 1));
            edgeGeometry.setAttribute('grup', new THREE.BufferAttribute(edgeGroups, 3));
        }

        return edgeGeometry;
    }

    private async extractEdgesFromMeshSyncAsync(geometry: THREE.BufferGeometry, thresholdAngle: number = 30): Promise<THREE.BufferGeometry> {
        const edgeGeometry = new THREE.BufferGeometry();

        if (!geometry.attributes['position']) {
            return edgeGeometry;
        }

        const positions = geometry.attributes['position'];
        const normals = geometry.attributes['normal'];

        if (!normals) {
            return edgeGeometry;
        }

        const vertexCount = positions.count;
        const triCount = Math.floor(vertexCount / 3);
        const thresholdCos = Math.cos(THREE.MathUtils.degToRad(thresholdAngle));

        const TRI_CHUNK_SIZE = 2000;

        interface EdgeInfoOpt {
            p1x: number; p1y: number; p1z: number;
            p2x: number; p2y: number; p2z: number;
            nx: number; ny: number; nz: number;
            count: number;
            isSharp: boolean;
        }
        const edgeMap = new Map<number, EdgeInfoOpt>();
        const PRECISION = 100000;

        const hashEdge = (p1x: number, p1y: number, p1z: number, p2x: number, p2y: number, p2z: number): number => {
            const h1 = Math.round(p1x * PRECISION) ^ (Math.round(p1y * PRECISION) << 10) ^ (Math.round(p1z * PRECISION) << 20);
            const h2 = Math.round(p2x * PRECISION) ^ (Math.round(p2y * PRECISION) << 10) ^ (Math.round(p2z * PRECISION) << 20);
            return h1 < h2 ? h1 * 1000000007 + h2 : h2 * 1000000007 + h1;
        };

        const posArray = positions.array;
        const edge1 = new THREE.Vector3();
        const edge2 = new THREE.Vector3();
        const normal = new THREE.Vector3();

        for (let t = 0; t < triCount; t++) {
            const i = t * 9;
            const v0x = posArray[i], v0y = posArray[i + 1], v0z = posArray[i + 2];
            const v1x = posArray[i + 3], v1y = posArray[i + 4], v1z = posArray[i + 5];
            const v2x = posArray[i + 6], v2y = posArray[i + 7], v2z = posArray[i + 8];

            edge1.set(v1x - v0x, v1y - v0y, v1z - v0z);
            edge2.set(v2x - v0x, v2y - v0y, v2z - v0z);
            normal.crossVectors(edge1, edge2).normalize();

            const nx = normal.x, ny = normal.y, nz = normal.z;

            const edges: [number, number, number, number, number, number][] = [
                [v0x, v0y, v0z, v1x, v1y, v1z],
                [v1x, v1y, v1z, v2x, v2y, v2z],
                [v2x, v2y, v2z, v0x, v0y, v0z]
            ];

            for (const [p1x, p1y, p1z, p2x, p2y, p2z] of edges) {
                const key = hashEdge(p1x, p1y, p1z, p2x, p2y, p2z);
                const existing = edgeMap.get(key);

                if (existing) {
                    existing.count++;
                    const dot = nx * existing.nx + ny * existing.ny + nz * existing.nz;
                    if (dot < thresholdCos && existing.count === 2) {
                        existing.isSharp = true;
                    }
                } else {
                    edgeMap.set(key, { p1x, p1y, p1z, p2x, p2y, p2z, nx, ny, nz, count: 1, isSharp: false });
                }
            }

            if ((t + 1) % TRI_CHUNK_SIZE === 0 && t < triCount - 1) {
                await this.yieldToMain();
            }
        }

        // Collect sharp and boundary edges
        const edgeLines: number[] = [];

        edgeMap.forEach((info) => {
            if (info.isSharp || info.count === 1) {
                // Validate coordinates
                if (isFinite(info.p1x) && isFinite(info.p1y) && isFinite(info.p1z) &&
                    isFinite(info.p2x) && isFinite(info.p2y) && isFinite(info.p2z)) {
                    edgeLines.push(
                        info.p1x, info.p1y, info.p1z,
                        info.p2x, info.p2y, info.p2z
                    );
                }
            }
        });

        if (edgeLines.length > 0) {
            const edgeVertexCount = edgeLines.length / 3;
            edgeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(edgeLines, 3));

            // WRL ile uyumlu format: Her edge için grup bilgisi
            const totalEdges = edgeVertexCount / 2;

            // OPTIMIZED: Pre-allocate arrays using cached defaults (faster than push)
            const edgeColors = FikoImporterSTP.getDefaultColorArray(edgeVertexCount);
            const edgeAlphas = FikoImporterSTP.getDefaultAlphaArray(edgeVertexCount);
            const edgeGroups = new Float32Array(edgeVertexCount * 3);

            // Fill grup array (vectorized)
            for (let edgeId = 0; edgeId < totalEdges; edgeId++) {
                const v1 = edgeId * 2;
                const v2 = v1 + 1;

                // Grup: [startVertex, vertexCount, edgeId]
                edgeGroups[v1 * 3] = v1; edgeGroups[v1 * 3 + 1] = 2; edgeGroups[v1 * 3 + 2] = edgeId;
                edgeGroups[v2 * 3] = v1; edgeGroups[v2 * 3 + 1] = 2; edgeGroups[v2 * 3 + 2] = edgeId;
            }

            edgeGeometry.setAttribute('color', new THREE.BufferAttribute(edgeColors, 3));
            edgeGeometry.setAttribute('alpha', new THREE.BufferAttribute(edgeAlphas, 1));
            edgeGeometry.setAttribute('grup', new THREE.BufferAttribute(edgeGroups, 3));
        }

        return edgeGeometry;
    }

    /**
     * Grup attribute'u kullanarak boundary edge'lerini çıkarır (async - UI blokajı önlenir)
     * @param geometry Mesh geometrisi
     * @returns Boundary edge geometrisi (grup attribute'una göre)
     */
    private async extractGroupBoundaryEdgesAsync(
        geometry: THREE.BufferGeometry,
        topology?: StepTopologyInfo
    ): Promise<THREE.BufferGeometry> {
        const edgeGeometry = new THREE.BufferGeometry();
        const positions = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
        const groupAttr = geometry.getAttribute('grup') as THREE.BufferAttribute | undefined;

        if (!positions || !groupAttr) {
            return edgeGeometry;
        }

        if (positions.count === 0 || groupAttr.count < positions.count || groupAttr.itemSize < 3) {
            return edgeGeometry;
        }

        const triCount = Math.floor(positions.count / 3);
        const TRI_CHUNK_SIZE = 2000;

        // 🔧 FIX: Edge gruplama düzeltmesi
        // Önceki hata: Edge hash'i groupId içeriyordu, bu yüzden aynı edge
        // farklı face'lerden gelince iki kez ekleniyordu.
        // Düzeltme: Edge hash'i groupId İÇERMEZ, ancak hangi face'lere ait olduğu takip edilir.
        // Face sınırı = edge ya farklı face'lere aittir ya da mesh sınırındadır.

        interface BoundaryEdgeInfo {
            count: number;
            p1x: number; p1y: number; p1z: number;
            p2x: number; p2y: number; p2z: number;
            groupIds: Set<number>; // Edge'in ait olduğu tüm face ID'leri
        }

        const edgeMap = new Map<number, BoundaryEdgeInfo>();
        const PRECISION = 100000;

        // 🔧 FIX: Hash artık groupId İÇERMİYOR - sadece geometrik pozisyon
        const hashEdge = (p1x: number, p1y: number, p1z: number, p2x: number, p2y: number, p2z: number): number => {
            const h1 = Math.round(p1x * PRECISION) ^ (Math.round(p1y * PRECISION) << 10) ^ (Math.round(p1z * PRECISION) << 20);
            const h2 = Math.round(p2x * PRECISION) ^ (Math.round(p2y * PRECISION) << 10) ^ (Math.round(p2z * PRECISION) << 20);
            // Order-independent hash
            return h1 < h2 ? h1 * 1000000007 + h2 : h2 * 1000000007 + h1;
        };

        const posArray = positions.array;
        const groupArray = groupAttr.array;

        for (let triIdx = 0; triIdx < triCount; triIdx++) {
            const baseIdx = triIdx * 3;
            const groupIdx = baseIdx * 3;
            const groupId = groupArray[groupIdx + 2];
            const groupId2 = groupArray[groupIdx + 5];
            const groupId3 = groupArray[groupIdx + 8];

            if (!isFinite(groupId) || groupId !== groupId2 || groupId !== groupId3) {
                continue;
            }

            const posIdx = baseIdx * 3;
            const v1x = posArray[posIdx], v1y = posArray[posIdx + 1], v1z = posArray[posIdx + 2];
            const v2x = posArray[posIdx + 3], v2y = posArray[posIdx + 4], v2z = posArray[posIdx + 5];
            const v3x = posArray[posIdx + 6], v3y = posArray[posIdx + 7], v3z = posArray[posIdx + 8];

            const edges: [number, number, number, number, number, number][] = [
                [v1x, v1y, v1z, v2x, v2y, v2z],
                [v2x, v2y, v2z, v3x, v3y, v3z],
                [v3x, v3y, v3z, v1x, v1y, v1z]
            ];

            for (const [p1x, p1y, p1z, p2x, p2y, p2z] of edges) {
                // 🔧 FIX: Hash artık groupId içermiyor
                const key = hashEdge(p1x, p1y, p1z, p2x, p2y, p2z);
                const existing = edgeMap.get(key);

                if (existing) {
                    existing.count++;
                    existing.groupIds.add(groupId); // Bu face'i de ekle
                } else {
                    edgeMap.set(key, {
                        count: 1,
                        p1x, p1y, p1z,
                        p2x, p2y, p2z,
                        groupIds: new Set([groupId])
                    });
                }
            }

            if ((triIdx + 1) % TRI_CHUNK_SIZE === 0 && triIdx < triCount - 1) {
                await this.yieldToMain();
            }
        }

        const edgePositions: number[] = [];
        const edgeColors: number[] = [];
        const edgeAlphas: number[] = [];
        const edgeGroups: number[] = [];
        const edgeFaceIds: number[] = [];
        const edgeColor = new THREE.Color(0x000000);

        let edgeCounter = 0;
        edgeMap.forEach((info) => {
            // 🔧 FIX: Boundary edge tespiti düzeltildi
            // Bir edge şu durumlarda sınır (boundary) edge'idir:
            // 1. Mesh sınırında (count === 1) - edge sadece bir üçgende görünür
            // 2. Face sınırında (groupIds.size > 1) - edge birden fazla face'e ait
            // Internal edge = count === 2 VE tek face'e ait (aynı face içinde paylaşılan)
            const isMeshBoundary = info.count === 1;
            const isFaceBoundary = info.groupIds.size > 1;

            if (!isMeshBoundary && !isFaceBoundary) {
                return; // Internal edge - atla
            }

            const { p1x, p1y, p1z, p2x, p2y, p2z, groupIds } = info;
            if (!isFinite(p1x) || !isFinite(p1y) || !isFinite(p1z) ||
                !isFinite(p2x) || !isFinite(p2y) || !isFinite(p2z)) {
                return;
            }

            edgePositions.push(p1x, p1y, p1z, p2x, p2y, p2z);
            const startVertex = edgeCounter * 2;
            const uniqueEdgeId = edgeCounter;
            // İlk face ID'yi kullan (birden fazla varsa bile)
            const firstGroupId = groupIds.values().next().value;
            const faceId = firstGroupId !== undefined && isFinite(firstGroupId) ? firstGroupId : -1;

            for (let i = 0; i < 2; i++) {
                edgeColors.push(edgeColor.r, edgeColor.g, edgeColor.b);
                edgeAlphas.push(1.0);
                edgeGroups.push(startVertex, 2, uniqueEdgeId);
                edgeFaceIds.push(faceId);
            }

            edgeCounter++;
        });

        if (edgePositions.length > 0) {
            edgeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(edgePositions, 3));
            edgeGeometry.setAttribute('color', new THREE.Float32BufferAttribute(edgeColors, 3));
            edgeGeometry.setAttribute('alpha', new THREE.Float32BufferAttribute(edgeAlphas, 1));
            edgeGeometry.setAttribute('grup', new THREE.Float32BufferAttribute(edgeGroups, 3));
            edgeGeometry.setAttribute('faceId', new THREE.Float32BufferAttribute(edgeFaceIds, 1));

            if (topology && topology.edges?.size > 0) {
                this.addTopologyToEdges(edgeGeometry, topology);
                this.tessellateCurvedEdges(edgeGeometry, topology);
                this.regroupEdgesByRadius(edgeGeometry);
            }
        }

        return edgeGeometry;
    }

    /**
     * Edge geometry'sine topoloji bilgilerini attribute olarak ekler
     * @param edgeGeometry Edge BufferGeometry
     * @param topology STEP'ten parse edilen topoloji bilgisi
     * @returns Güncellenmiş edge geometry (topoloji attribute'ları ile)
     */
    private addTopologyToEdges(edgeGeometry: THREE.BufferGeometry, topology: StepTopologyInfo): THREE.BufferGeometry {
        if (!edgeGeometry.attributes['position'] || topology.edges.size === 0) {
            console.warn('🔷 addTopologyToEdges: No position or empty topology.edges', {
                hasPosition: !!edgeGeometry.attributes['position'],
                edgesSize: topology.edges.size
            });
            return edgeGeometry;
        }

        const positions = edgeGeometry.attributes['position'];
        const vertexCount = positions.count;
        const edgeCount = Math.floor(vertexCount / 2);
        const groupAttr = edgeGeometry.getAttribute('grup') as THREE.BufferAttribute | undefined;

        // 🔷 DEBUG: Topoloji bilgilerini logla
        const circleEdges = Array.from(topology.edges.values()).filter(e => e.curveType === 'CIRCLE' && e.center && e.radius);
        // console.log(`🔷 addTopologyToEdges: ${edgeCount} mesh edge, ${topology.edges.size} STEP edge, ${circleEdges.length} CIRCLE with center/radius`);
        // if (circleEdges.length > 0) {
        //     console.log('🔷 First CIRCLE edge:', {
        //         id: circleEdges[0].id,
        //         radius: circleEdges[0].radius,
        //         center: circleEdges[0].center?.toArray()
        //     });
        // }
        // console.log(`🔷 RadiusGroups: ${topology.radiusGroups.size} farklı radius`);

        // Yeni attribute'lar
        // curveType: 0=UNKNOWN, 1=LINE, 2=CIRCLE, 3=ELLIPSE, 4=B_SPLINE, 5=TRIMMED
        const curveTypes = new Float32Array(vertexCount);
        // radius: Edge radius'u (daire/elips için)
        const radii = new Float32Array(vertexCount);
        // radiusGroup: Aynı radius'a sahip edge'lerin grup ID'si
        const radiusGroups = new Float32Array(vertexCount);
        // edgeId: STEP edge entity ID'si (#id -> numeric)
        const edgeIds = new Float32Array(vertexCount);
        edgeIds.fill(-1);
        // adjacent face bilgileri (STEP face id)
        const adjacentFaceA = new Float32Array(vertexCount);
        const adjacentFaceB = new Float32Array(vertexCount);
        adjacentFaceA.fill(-1);
        adjacentFaceB.fill(-1);

        // Curve type mapping
        const curveTypeMap: { [key: string]: number } = {
            'UNKNOWN': 0,
            'LINE': 1,
            'CIRCLE': 2,
            'ELLIPSE': 3,
            'B_SPLINE_CURVE': 4,
            'TRIMMED_CURVE': 5
        };

        // Radius grup mapping oluştur
        const radiusToGroupId = new Map<number, number>();
        let groupIdCounter = 0;
        for (const [radius] of topology.radiusGroups) {
            radiusToGroupId.set(radius, groupIdCounter++);
        }

        // Edge'leri eşleştir (pozisyon bazlı)
        const edgeInfoArray = Array.from(topology.edges.values());
        const tolerance = 0.1; // mm
        const parseStepId = (id?: string): number => {
            if (!id) {
                return -1;
            }
            const numeric = parseInt(id.replace('#', ''), 10);
            return isNaN(numeric) ? -1 : numeric;
        };

        // 🔷 DEBUG: Eşleşme sayaçları
        let matchedCircleCount = 0;
        let matchedLineCount = 0;
        let unmatchedCount = 0;

        // 🔶 Koordinat offset hesapla - occt-import-js mesh'i farklı konuma taşıyabilir
        let coordOffset = new THREE.Vector3(0, 0, 0);
        if (edgeCount > 0 && circleEdges.length > 0) {
            // Mesh bounding box merkezi
            let meshMinX = Infinity, meshMinY = Infinity, meshMinZ = Infinity;
            let meshMaxX = -Infinity, meshMaxY = -Infinity, meshMaxZ = -Infinity;
            for (let i = 0; i < vertexCount; i++) {
                const x = positions.getX(i), y = positions.getY(i), z = positions.getZ(i);
                meshMinX = Math.min(meshMinX, x); meshMaxX = Math.max(meshMaxX, x);
                meshMinY = Math.min(meshMinY, y); meshMaxY = Math.max(meshMaxY, y);
                meshMinZ = Math.min(meshMinZ, z); meshMaxZ = Math.max(meshMaxZ, z);
            }
            const meshCenter = new THREE.Vector3(
                (meshMinX + meshMaxX) / 2,
                (meshMinY + meshMaxY) / 2,
                (meshMinZ + meshMaxZ) / 2
            );

            // STEP CIRCLE center'ların ortalaması
            const stepCenterAvg = new THREE.Vector3();
            for (const ce of circleEdges) {
                if (ce.center) stepCenterAvg.add(ce.center);
            }
            stepCenterAvg.divideScalar(circleEdges.length);

            // Offset hesapla: STEP koordinatlarını mesh koordinatlarına taşı
            coordOffset = meshCenter.clone().sub(stepCenterAvg);

            // console.log('🔷 KOORDİNAT OFFSET:', {
            //     offsetLength: coordOffset.length().toFixed(2),
            //     offset: coordOffset.toArray().map(v => v.toFixed(2))
            // });

            // 🔷 DEBUG: Offset sonrası en yakın mesh edge'i bul
            const testCircle = circleEdges[0];
            const adjustedTestCenter = testCircle.center!.clone().add(coordOffset);
            let minDistFound = Infinity;
            for (let testIdx = 0; testIdx < Math.min(200, edgeCount); testIdx++) {
                // ⚡ PERFORMANS: Reuse pre-allocated buffer (avoids 200 allocations)
                _stpTmpVec3A.set(
                    positions.getX(testIdx * 2),
                    positions.getY(testIdx * 2),
                    positions.getZ(testIdx * 2)
                );
                const td1 = Math.abs(_stpTmpVec3A.distanceTo(adjustedTestCenter) - testCircle.radius!);
                if (td1 < minDistFound) {
                    minDistFound = td1;
                }
            }
            // console.log('🔷 OFFSET SONRASI MİN MESAFE:', {
            //     circleRadius: testCircle.radius,
            //     minDistToCircle: minDistFound.toFixed(4),
            //     tolerance: tolerance,
            //     match: minDistFound < tolerance ? 'EŞLEŞME VAR!' : 'Eşleşme yok'
            // });
        }

        for (let edgeIdx = 0; edgeIdx < edgeCount; edgeIdx++) {
            const v1Idx = edgeIdx * 2;
            const v2Idx = edgeIdx * 2 + 1;

            // ⚡ PERFORMANS: Reuse pre-allocated buffers (avoids edgeCount * 2 allocations)
            _stpTmpVec3A.set(
                positions.getX(v1Idx),
                positions.getY(v1Idx),
                positions.getZ(v1Idx)
            );
            _stpTmpVec3B.set(
                positions.getX(v2Idx),
                positions.getY(v2Idx),
                positions.getZ(v2Idx)
            );

            // En yakın topoloji edge'ini bul
            let bestMatch: StepEdgeInfo | null = null;
            let bestDistance = Infinity;

            for (const edgeInfo of edgeInfoArray) {
                if (edgeInfo.startPoint && edgeInfo.endPoint) {
                    // 🔶 Offset uygula: STEP koordinatlarını mesh koordinatlarına taşı
                    const adjustedStart = edgeInfo.startPoint.clone().add(coordOffset);
                    const adjustedEnd = edgeInfo.endPoint.clone().add(coordOffset);
                    // Forward match
                    const d1 = _stpTmpVec3A.distanceTo(adjustedStart) + _stpTmpVec3B.distanceTo(adjustedEnd);
                    // Reverse match
                    const d2 = _stpTmpVec3A.distanceTo(adjustedEnd) + _stpTmpVec3B.distanceTo(adjustedStart);
                    const minDist = Math.min(d1, d2);

                    if (minDist < bestDistance && minDist < tolerance * 2) {
                        bestDistance = minDist;
                        bestMatch = edgeInfo;
                    }
                } else if (edgeInfo.center && edgeInfo.radius) {
                    if (!edgeInfo.adjacentFaces || edgeInfo.adjacentFaces.length === 0) continue;
                    const adjustedCenter = edgeInfo.center.clone().add(coordOffset);
                    const d1 = Math.abs(_stpTmpVec3A.distanceTo(adjustedCenter) - edgeInfo.radius);
                    const d2 = Math.abs(_stpTmpVec3B.distanceTo(adjustedCenter) - edgeInfo.radius);

                    if (d1 < tolerance && d2 < tolerance) {
                        bestMatch = edgeInfo;
                        bestDistance = 0;
                    }
                }
            }

            // Attribute'ları ata
            const curveType = bestMatch ? curveTypeMap[bestMatch.curveType] || 0 : 0;
            const radius = bestMatch?.radius ?? 0;
            let radiusGroup = -1;
            const edgeNumericId = bestMatch ? parseStepId(bestMatch.id) : -1;

            // 🔷 DEBUG: Eşleşme sayaçlarını güncelle
            if (bestMatch) {
                if (bestMatch.curveType === 'CIRCLE') matchedCircleCount++;
                else if (bestMatch.curveType === 'LINE') matchedLineCount++;
            } else {
                unmatchedCount++;
            }
            const adjacentA = bestMatch?.adjacentFaces && bestMatch.adjacentFaces.length > 0
                ? parseStepId(bestMatch.adjacentFaces[0])
                : -1;
            const adjacentB = bestMatch?.adjacentFaces && bestMatch.adjacentFaces.length > 1
                ? parseStepId(bestMatch.adjacentFaces[1])
                : -1;

            if (radius > 0) {
                const roundedRadius = Math.round(radius * 1000) / 1000;
                radiusGroup = radiusToGroupId.get(roundedRadius) ?? -1;
            }

            // Her edge 2 vertex
            curveTypes[v1Idx] = curveType;
            curveTypes[v2Idx] = curveType;
            radii[v1Idx] = radius;
            radii[v2Idx] = radius;
            radiusGroups[v1Idx] = radiusGroup;
            radiusGroups[v2Idx] = radiusGroup;
            edgeIds[v1Idx] = edgeNumericId;
            edgeIds[v2Idx] = edgeNumericId;
            adjacentFaceA[v1Idx] = adjacentA;
            adjacentFaceA[v2Idx] = adjacentA;
            adjacentFaceB[v1Idx] = adjacentB;
            adjacentFaceB[v2Idx] = adjacentB;

            if (groupAttr && edgeNumericId >= 0) {
                groupAttr.setZ(v1Idx, edgeNumericId);
                groupAttr.setZ(v2Idx, edgeNumericId);
            }
        }

        // 🔷 DEBUG: Eşleşme sonuçlarını logla
        // console.log(`🔷 addTopologyToEdges sonuç: ${matchedCircleCount} CIRCLE, ${matchedLineCount} LINE eşleşti, ${unmatchedCount} eşleşmedi`);

        if (groupAttr) {
            groupAttr.needsUpdate = true;
        }

        // Attribute'ları geometry'ye ekle
        edgeGeometry.setAttribute('curveType', new THREE.Float32BufferAttribute(curveTypes, 1));
        edgeGeometry.setAttribute('radius', new THREE.Float32BufferAttribute(radii, 1));
        edgeGeometry.setAttribute('radiusGroup', new THREE.Float32BufferAttribute(radiusGroups, 1));
        edgeGeometry.setAttribute('edgeId', new THREE.Float32BufferAttribute(edgeIds, 1));
        edgeGeometry.setAttribute('adjacentFaceA', new THREE.Float32BufferAttribute(adjacentFaceA, 1));
        edgeGeometry.setAttribute('adjacentFaceB', new THREE.Float32BufferAttribute(adjacentFaceB, 1));

        return edgeGeometry;
    }

    /**
     * Eğrisel kenarları (CIRCLE, ELLIPSE) tessellate eder.
     * Düz 2-vertex line segment yerine parametrik ara noktalar ekleyerek
     * yay/daire kenarlarının pürüzsüz görünmesini sağlar.
     *
     * LineSegments formatı: Her 2 ardışık vertex bir segment oluşturur.
     * Tessellated arc: N segment = N+1 vertex → 2*N vertex (N adet pair)
     *
     * İki katmanlı tessellation stratejisi:
     * 1. Topoloji verisi varsa (center/radius/axis): tam parametrik arc oluştur
     * 2. Topoloji verisi yoksa ama curveType ve radius attribute'u varsa:
     *    geometrik sagitta yaklaşımı ile chord üzerinde yay noktaları üret
     *
     * @param edgeGeometry Edge BufferGeometry (addTopologyToEdges sonrası)
     * @param topology STEP topoloji bilgisi (center/radius/axis için)
     */
    private tessellateCurvedEdges(edgeGeometry: THREE.BufferGeometry, topology: StepTopologyInfo): void {
        const positionAttr = edgeGeometry.getAttribute('position') as THREE.BufferAttribute | undefined;
        const curveTypeAttr = edgeGeometry.getAttribute('curveType') as THREE.BufferAttribute | undefined;
        const radiusAttr = edgeGeometry.getAttribute('radius') as THREE.BufferAttribute | undefined;
        const edgeIdAttr = edgeGeometry.getAttribute('edgeId') as THREE.BufferAttribute | undefined;
        const colorAttr = edgeGeometry.getAttribute('color') as THREE.BufferAttribute | undefined;
        const alphaAttr = edgeGeometry.getAttribute('alpha') as THREE.BufferAttribute | undefined;
        const grupAttr = edgeGeometry.getAttribute('grup') as THREE.BufferAttribute | undefined;
        const radiusGroupAttr = edgeGeometry.getAttribute('radiusGroup') as THREE.BufferAttribute | undefined;
        const faceIdAttr = edgeGeometry.getAttribute('faceId') as THREE.BufferAttribute | undefined;
        const adjacentFaceAAttr = edgeGeometry.getAttribute('adjacentFaceA') as THREE.BufferAttribute | undefined;
        const adjacentFaceBAttr = edgeGeometry.getAttribute('adjacentFaceB') as THREE.BufferAttribute | undefined;

        if (!positionAttr || !curveTypeAttr || positionAttr.count === 0) {
            return;
        }

        // 🚀 WASM hızlandırma deneme: Pozisyon tessellation'ı WASM'da, attribute kopyalama JS'te
        try {
            const wasmBridge = (window as any).__fikoWasmBridge || (FikoWasmBridge as any)?.instance;
            const wasmModule = wasmBridge?.getWasmModule?.() || window.__wasmModule;
            if (wasmModule?.tessellateCircularEdges) {
                const wasmResult = this._tessellateCurvedEdgesWasm(
                    edgeGeometry, topology, wasmModule,
                    positionAttr, curveTypeAttr, radiusAttr, edgeIdAttr,
                    colorAttr, alphaAttr, grupAttr, radiusGroupAttr,
                    faceIdAttr, adjacentFaceAAttr, adjacentFaceBAttr
                );
                if (wasmResult) return; // WASM başarılı, early return
            }
        } catch { /* WASM fallback - devam et */ }

        const vertexCount = positionAttr.count;
        const edgeCount = Math.floor(vertexCount / 2);

        // Topoloji edge'lerini hızlı erişim için ID'ye göre map'le
        const topoEdgeById = new Map<number, StepEdgeInfo>();
        const topoEdgeValues = Array.from(topology.edges.values());
        for (let ti = 0; ti < topoEdgeValues.length; ti++) {
            const edgeInfo = topoEdgeValues[ti];
            if (edgeInfo.id) {
                const numId = parseInt(edgeInfo.id.replace('#', ''), 10);
                if (!isNaN(numId)) {
                    topoEdgeById.set(numId, edgeInfo);
                }
            }
        }

        // Koordinat offset hesapla (addTopologyToEdges ile aynı mantık)
        const allTopoEdges = Array.from(topology.edges.values());
        const circleEdges: StepEdgeInfo[] = [];
        for (let ci = 0; ci < allTopoEdges.length; ci++) {
            const e = allTopoEdges[ci];
            if ((e.curveType === 'CIRCLE' || e.curveType === 'ELLIPSE') && e.center && e.radius) {
                circleEdges.push(e);
            }
        }
        let coordOffsetX = 0, coordOffsetY = 0, coordOffsetZ = 0;
        if (edgeCount > 0 && circleEdges.length > 0) {
            let meshMinX = Infinity, meshMinY = Infinity, meshMinZ = Infinity;
            let meshMaxX = -Infinity, meshMaxY = -Infinity, meshMaxZ = -Infinity;
            for (let i = 0; i < vertexCount; i++) {
                const x = positionAttr.getX(i), y = positionAttr.getY(i), z = positionAttr.getZ(i);
                if (x < meshMinX) meshMinX = x; if (x > meshMaxX) meshMaxX = x;
                if (y < meshMinY) meshMinY = y; if (y > meshMaxY) meshMaxY = y;
                if (z < meshMinZ) meshMinZ = z; if (z > meshMaxZ) meshMaxZ = z;
            }
            const meshCenterX = (meshMinX + meshMaxX) / 2;
            const meshCenterY = (meshMinY + meshMaxY) / 2;
            const meshCenterZ = (meshMinZ + meshMaxZ) / 2;
            let stepAvgX = 0, stepAvgY = 0, stepAvgZ = 0;
            for (let ci = 0; ci < circleEdges.length; ci++) {
                const c = circleEdges[ci].center!;
                stepAvgX += c.x; stepAvgY += c.y; stepAvgZ += c.z;
            }
            stepAvgX /= circleEdges.length;
            stepAvgY /= circleEdges.length;
            stepAvgZ /= circleEdges.length;
            coordOffsetX = meshCenterX - stepAvgX;
            coordOffsetY = meshCenterY - stepAvgY;
            coordOffsetZ = meshCenterZ - stepAvgZ;
        }

        // Eğrisel edge var mı kontrol et
        let hasCurved = false;
        for (let edgeIdx = 0; edgeIdx < edgeCount; edgeIdx++) {
            const ct = curveTypeAttr.getX(edgeIdx * 2);
            if (ct === 2 || ct === 3 || ct === 4 || ct === 5) { // CIRCLE, ELLIPSE, B_SPLINE, TRIMMED
                hasCurved = true;
                break;
            }
        }
        if (!hasCurved) {
            return; // Tessellate edilecek eğrisel edge yok
        }

        // Yeni attribute array'leri
        const newPositions: number[] = [];
        const newCurveTypes: number[] = [];
        const newRadii: number[] = [];
        const newEdgeIds: number[] = [];
        const newColors: number[] = [];
        const newAlphas: number[] = [];
        const newRadiusGroups: number[] = [];
        const newFaceIds: number[] = [];
        const newAdjacentFaceA: number[] = [];
        const newAdjacentFaceB: number[] = [];

        // Grup bilgisi ayrı tutulacak (vertex cursor'a bağlı)
        const groupRanges: { start: number; count: number; groupId: number }[] = [];
        let vertexCursor = 0;

        // Helper: Bir vertex'in scalar attribute değerlerini push et
        const pushScalarAttrs = (srcIdx: number, count: number) => {
            for (let i = 0; i < count; i++) {
                const ctVal = curveTypeAttr ? curveTypeAttr.getX(srcIdx) : 0;
                const r = radiusAttr ? radiusAttr.getX(srcIdx) : 0;
                const eid = edgeIdAttr ? edgeIdAttr.getX(srcIdx) : -1;
                const cr = colorAttr ? colorAttr.getX(srcIdx) : 0;
                const cg = colorAttr ? colorAttr.getY(srcIdx) : 0;
                const cb = colorAttr ? colorAttr.getZ(srcIdx) : 0;
                const a = alphaAttr ? alphaAttr.getX(srcIdx) : 1;
                const rg = radiusGroupAttr ? radiusGroupAttr.getX(srcIdx) : -1;
                const fid = faceIdAttr ? faceIdAttr.getX(srcIdx) : -1;
                const afA = adjacentFaceAAttr ? adjacentFaceAAttr.getX(srcIdx) : -1;
                const afB = adjacentFaceBAttr ? adjacentFaceBAttr.getX(srcIdx) : -1;

                newCurveTypes.push(ctVal);
                newRadii.push(r);
                newEdgeIds.push(eid);
                newColors.push(cr, cg, cb);
                newAlphas.push(a);
                newRadiusGroups.push(rg);
                newFaceIds.push(fid);
                newAdjacentFaceA.push(afA);
                newAdjacentFaceB.push(afB);
            }
        };

        // ⚡ Reusable Vector3 buffers (avoid per-edge allocations)
        const _center = new THREE.Vector3();
        const _axis = new THREE.Vector3();
        const _up = new THREE.Vector3();
        const _u = new THREE.Vector3();
        const _v = new THREE.Vector3();
        const _toP = new THREE.Vector3();
        const _chordDir = new THREE.Vector3();
        const _perpDir = new THREE.Vector3();

        for (let edgeIdx = 0; edgeIdx < edgeCount; edgeIdx++) {
            const v1Idx = edgeIdx * 2;
            const v2Idx = v1Idx + 1;
            const ct = curveTypeAttr.getX(v1Idx);

            if (ct !== 2 && ct !== 3 && ct !== 4 && ct !== 5) {
                // Düz (LINE) veya bilinmeyen edge - olduğu gibi kopyala
                const startVtx = vertexCursor;
                newPositions.push(
                    positionAttr.getX(v1Idx), positionAttr.getY(v1Idx), positionAttr.getZ(v1Idx),
                    positionAttr.getX(v2Idx), positionAttr.getY(v2Idx), positionAttr.getZ(v2Idx)
                );
                pushScalarAttrs(v1Idx, 2);
                vertexCursor += 2;
                const gid = grupAttr ? grupAttr.getZ(v1Idx) : edgeIdx;
                groupRanges.push({ start: startVtx, count: 2, groupId: gid });
                continue;
            }

            // Eğrisel edge - tessellate et
            const p1x = positionAttr.getX(v1Idx), p1y = positionAttr.getY(v1Idx), p1z = positionAttr.getZ(v1Idx);
            const p2x = positionAttr.getX(v2Idx), p2y = positionAttr.getY(v2Idx), p2z = positionAttr.getZ(v2Idx);

            // Topoloji edge info bul
            const numericEdgeId = edgeIdAttr ? edgeIdAttr.getX(v1Idx) : -1;
            const topoEdge = numericEdgeId >= 0 ? topoEdgeById.get(numericEdgeId) : undefined;

            // Arc noktalarını inline hesapla (Vector3 allocation'sız, sonuçlar flat array'de)
            // arcFlat: [x0,y0,z0, x1,y1,z1, ...] formatında N+1 nokta
            let arcFlat: number[] | null = null;

            if (topoEdge && topoEdge.center && topoEdge.radius && topoEdge.radius > 0) {
                // ===== Strateji 1: Tam parametrik arc (topoloji verisi ile) =====
                const radius = topoEdge.radius;
                _center.set(
                    topoEdge.center.x + coordOffsetX,
                    topoEdge.center.y + coordOffsetY,
                    topoEdge.center.z + coordOffsetZ
                );
                if (topoEdge.axis) {
                    _axis.copy(topoEdge.axis).normalize();
                } else {
                    _axis.set(0, 0, 1);
                }

                // Lokal koordinat sistemi oluştur (eksen = normal)
                if (Math.abs(_axis.x * 1 + _axis.y * 0 + _axis.z * 0) < 0.9) {
                    _up.set(1, 0, 0);
                } else {
                    _up.set(0, 1, 0);
                }
                _u.crossVectors(_axis, _up).normalize();
                _v.crossVectors(_axis, _u).normalize();

                // p1 ve p2'nin açılarını hesapla
                _toP.set(p1x - _center.x, p1y - _center.y, p1z - _center.z);
                const angle1 = Math.atan2(_toP.dot(_v), _toP.dot(_u));
                _toP.set(p2x - _center.x, p2y - _center.y, p2z - _center.z);
                const angle2Raw = Math.atan2(_toP.dot(_v), _toP.dot(_u));

                // Açı farkını normalize et (0, 2*PI) aralığında pozitif yönde
                let arcAngle = angle2Raw - angle1;
                if (arcAngle <= 0) arcAngle += 2 * Math.PI;

                // Tam daire kontrolü: eğer start ve end çok yakınsa tam daire
                const dx = p2x - p1x, dy = p2y - p1y, dz = p2z - p1z;
                const p1p2Dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
                if (p1p2Dist < 0.001 * radius) {
                    arcAngle = 2 * Math.PI;
                }

                // Segment sayısı: minimum 8, maximum 32
                const numSegments = Math.min(32, Math.max(8, Math.ceil(arcAngle * radius / 0.5)));

                arcFlat = [];
                for (let i = 0; i <= numSegments; i++) {
                    const t = angle1 + (arcAngle * i) / numSegments;
                    const cosT = Math.cos(t);
                    const sinT = Math.sin(t);
                    arcFlat.push(
                        _center.x + radius * (cosT * _u.x + sinT * _v.x),
                        _center.y + radius * (cosT * _u.y + sinT * _v.y),
                        _center.z + radius * (cosT * _u.z + sinT * _v.z)
                    );
                }
            }

            // ===== Strateji 2: Geometrik sagitta yaklaşımı (topoloji yoksa) =====
            if (!arcFlat && radiusAttr) {
                const radius = radiusAttr.getX(v1Idx);
                if (radius > 0) {
                    const dx = p2x - p1x, dy = p2y - p1y, dz = p2z - p1z;
                    const chordLen = Math.sqrt(dx * dx + dy * dy + dz * dz);

                    if (chordLen > 1e-8 && chordLen < 2 * radius) {
                        const halfChord = chordLen / 2;
                        // h = r - sqrt(r^2 - (chord/2)^2)  (sagitta / yay yüksekliği)
                        const sagitta = radius - Math.sqrt(Math.max(0, radius * radius - halfChord * halfChord));

                        if (sagitta > 1e-6) {
                            // Chord yönü (normalize)
                            const invChord = 1 / chordLen;
                            const cdx = dx * invChord, cdy = dy * invChord, cdz = dz * invChord;

                            // Chord'a dik yön bul (herhangi bir düzlemde)
                            // cross(chord, arbitrary_up) ile perpendicular elde et
                            if (Math.abs(cdx) < 0.9) {
                                _up.set(1, 0, 0);
                            } else {
                                _up.set(0, 1, 0);
                            }
                            _perpDir.set(
                                cdy * _up.z - cdz * _up.y,
                                cdz * _up.x - cdx * _up.z,
                                cdx * _up.y - cdy * _up.x
                            );
                            const perpLen = Math.sqrt(_perpDir.x * _perpDir.x + _perpDir.y * _perpDir.y + _perpDir.z * _perpDir.z);
                            if (perpLen > 1e-10) {
                                _perpDir.x /= perpLen; _perpDir.y /= perpLen; _perpDir.z /= perpLen;
                            }

                            // Arc açısını hesapla
                            const arcAngle = 2 * Math.asin(Math.min(1, halfChord / radius));
                            const numSegments = Math.min(32, Math.max(8, Math.ceil(arcAngle * radius / 0.5)));

                            // Chord merkezi
                            const midX = (p1x + p2x) / 2;
                            const midY = (p1y + p2y) / 2;
                            const midZ = (p1z + p2z) / 2;

                            // Dairenin merkezi: midpoint'ten perpDir yönünde (radius - sagitta) kadar
                            const centerDist = radius - sagitta;
                            const ctrX = midX + _perpDir.x * centerDist;
                            const ctrY = midY + _perpDir.y * centerDist;
                            const ctrZ = midZ + _perpDir.z * centerDist;

                            // Lokal koordinat sistemi oluştur
                            // u: center -> p1 yönü (normalize)
                            const u1x = p1x - ctrX, u1y = p1y - ctrY, u1z = p1z - ctrZ;
                            const u1Len = Math.sqrt(u1x * u1x + u1y * u1y + u1z * u1z);
                            if (u1Len > 1e-10) {
                                const invU1 = 1 / u1Len;
                                _u.set(u1x * invU1, u1y * invU1, u1z * invU1);

                                // v: cross(chordDir, u) (normalize)
                                _v.set(
                                    cdy * _u.z - cdz * _u.y,
                                    cdz * _u.x - cdx * _u.z,
                                    cdx * _u.y - cdy * _u.x
                                );
                                const vLen = Math.sqrt(_v.x * _v.x + _v.y * _v.y + _v.z * _v.z);
                                if (vLen > 1e-10) {
                                    _v.x /= vLen; _v.y /= vLen; _v.z /= vLen;
                                }

                                // Açıları hesapla
                                const startAngle = Math.atan2(
                                    (p1x - ctrX) * _v.x + (p1y - ctrY) * _v.y + (p1z - ctrZ) * _v.z,
                                    (p1x - ctrX) * _u.x + (p1y - ctrY) * _u.y + (p1z - ctrZ) * _u.z
                                );
                                const endAngle = Math.atan2(
                                    (p2x - ctrX) * _v.x + (p2y - ctrY) * _v.y + (p2z - ctrZ) * _v.z,
                                    (p2x - ctrX) * _u.x + (p2y - ctrY) * _u.y + (p2z - ctrZ) * _u.z
                                );

                                let sweepAngle = endAngle - startAngle;
                                // Küçük yay seçimi: sagitta yaklaşımı minor arc üretir
                                if (sweepAngle > Math.PI) sweepAngle -= 2 * Math.PI;
                                if (sweepAngle < -Math.PI) sweepAngle += 2 * Math.PI;

                                arcFlat = [];
                                for (let i = 0; i <= numSegments; i++) {
                                    const t = startAngle + (sweepAngle * i) / numSegments;
                                    const cosT = Math.cos(t);
                                    const sinT = Math.sin(t);
                                    arcFlat.push(
                                        ctrX + radius * (cosT * _u.x + sinT * _v.x),
                                        ctrY + radius * (cosT * _u.y + sinT * _v.y),
                                        ctrZ + radius * (cosT * _u.z + sinT * _v.z)
                                    );
                                }
                            }
                        }
                    }
                }
            }

            // ===== Strateji 3: B-Spline kontrol noktaları ile De Boor tessellation =====
            if (!arcFlat && (ct === 4 || ct === 5) && topoEdge?.controlPoints && topoEdge.controlPoints.length >= 2) {
                const cps = topoEdge.controlPoints;
                const degree = topoEdge.degree ?? Math.min(3, cps.length - 1);

                // Kontrol noktalarını offset'le (STEP -> mesh koordinat dönüşümü)
                const offsetCps: number[][] = [];
                for (let ci = 0; ci < cps.length; ci++) {
                    offsetCps.push([
                        cps[ci].x + coordOffsetX,
                        cps[ci].y + coordOffsetY,
                        cps[ci].z + coordOffsetZ
                    ]);
                }

                // Uniform knot vektörü oluştur (topology'den gelmiyorsa)
                let knots = topoEdge.knots;
                if (!knots || knots.length < cps.length + degree + 1) {
                    // Clamped uniform knot vektörü: [0,0,...,0, 1/(n-p), 2/(n-p), ..., 1,1,...,1]
                    const n = cps.length;
                    const knotCount = n + degree + 1;
                    knots = new Array(knotCount);
                    for (let ki = 0; ki <= degree; ki++) knots[ki] = 0;
                    for (let ki = degree + 1; ki < n; ki++) knots[ki] = (ki - degree) / (n - degree);
                    for (let ki = n; ki < knotCount; ki++) knots[ki] = 1;
                }

                // De Boor algoritması ile B-spline noktaları hesapla
                const numSegments = Math.min(32, Math.max(12, cps.length * 4));
                arcFlat = [];

                for (let si = 0; si <= numSegments; si++) {
                    // t parametresi: [knots[degree], knots[n]] aralığında
                    const n = cps.length;
                    const tMin = knots[degree];
                    const tMax = knots[n];
                    let t = tMin + (tMax - tMin) * si / numSegments;
                    // Son nokta için küçük bir epsilon çıkar (knot sınırı sorunu)
                    if (si === numSegments) t = tMax - 1e-10;

                    // De Boor: t'ye karşılık gelen knot span'ı bul
                    let k = degree;
                    for (let ki = degree; ki < n; ki++) {
                        if (t >= knots[ki] && t < knots[ki + 1]) {
                            k = ki;
                            break;
                        }
                    }

                    // De Boor piramidi (inline, allocation-free flat arrays)
                    // d[j] = control point at level, stored as flat [x,y,z] triplets
                    const dLen = degree + 1;
                    const dFlat = new Array(dLen * 3);
                    for (let j = 0; j <= degree; j++) {
                        const cpIdx = Math.min(Math.max(0, k - degree + j), n - 1);
                        dFlat[j * 3] = offsetCps[cpIdx][0];
                        dFlat[j * 3 + 1] = offsetCps[cpIdx][1];
                        dFlat[j * 3 + 2] = offsetCps[cpIdx][2];
                    }

                    for (let r = 1; r <= degree; r++) {
                        for (let j = degree; j >= r; j--) {
                            const leftKnot = knots[k - degree + j + r] ?? knots[knots.length - 1];
                            const rightKnot = knots[k + j + 1] ?? knots[knots.length - 1];
                            const denom = rightKnot - leftKnot;
                            const alpha = denom > 1e-12 ? (t - leftKnot) / denom : 0;
                            const oneMinusAlpha = 1 - alpha;
                            const j3 = j * 3;
                            const jm3 = (j - 1) * 3;
                            dFlat[j3] = oneMinusAlpha * dFlat[jm3] + alpha * dFlat[j3];
                            dFlat[j3 + 1] = oneMinusAlpha * dFlat[jm3 + 1] + alpha * dFlat[j3 + 1];
                            dFlat[j3 + 2] = oneMinusAlpha * dFlat[jm3 + 2] + alpha * dFlat[j3 + 2];
                        }
                    }

                    const d3 = degree * 3;
                    arcFlat.push(dFlat[d3], dFlat[d3 + 1], dFlat[d3 + 2]);
                }
            }

            // ===== Strateji 4: B-Spline kontrol noktası yoksa, endpoint'ler arası catmull-rom =====
            // Fillet/chamfer edge'leri mesh boundary'den zaten çok-segmentli çıkar,
            // ama tek segment (2 vertex) olarak kalmışsa, düz çizgi olarak bırak
            // (occt-import-js tessellation yeterli)

            if (!arcFlat || arcFlat.length < 6) {
                // Tessellate başarısız veya gereksiz - düz olarak kopyala
                const startVtx = vertexCursor;
                newPositions.push(p1x, p1y, p1z, p2x, p2y, p2z);
                pushScalarAttrs(v1Idx, 2);
                vertexCursor += 2;
                const gid = grupAttr ? grupAttr.getZ(v1Idx) : edgeIdx;
                groupRanges.push({ start: startVtx, count: 2, groupId: gid });
                continue;
            }

            // LineSegments formatı: Her segment = 2 vertex (pair)
            // arcFlat has (N+1)*3 floats → N segments → 2*N vertices
            const numArcPoints = arcFlat.length / 3;
            const numSegments = numArcPoints - 1;
            const startVtx = vertexCursor;
            const gid = grupAttr ? grupAttr.getZ(v1Idx) : edgeIdx;

            for (let s = 0; s < numSegments; s++) {
                const baseA = s * 3;
                const baseB = (s + 1) * 3;
                newPositions.push(
                    arcFlat[baseA], arcFlat[baseA + 1], arcFlat[baseA + 2],
                    arcFlat[baseB], arcFlat[baseB + 1], arcFlat[baseB + 2]
                );
                pushScalarAttrs(v1Idx, 2); // Aynı edge'in attribute'larını paylaş
            }
            vertexCursor += numSegments * 2;
            groupRanges.push({ start: startVtx, count: numSegments * 2, groupId: gid });
        }

        if (vertexCursor === 0) {
            return;
        }

        // Yeni grup attribute'ı oluştur
        const newGrupArray = new Float32Array(vertexCursor * 3);
        for (let ri = 0; ri < groupRanges.length; ri++) {
            const range = groupRanges[ri];
            for (let i = range.start; i < range.start + range.count; i++) {
                const base = i * 3;
                newGrupArray[base] = range.start;
                newGrupArray[base + 1] = range.count;
                newGrupArray[base + 2] = range.groupId;
            }
        }

        // Geometry attribute'larını güncelle
        edgeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
        edgeGeometry.setAttribute('curveType', new THREE.Float32BufferAttribute(newCurveTypes, 1));
        edgeGeometry.setAttribute('radius', new THREE.Float32BufferAttribute(newRadii, 1));
        edgeGeometry.setAttribute('edgeId', new THREE.Float32BufferAttribute(newEdgeIds, 1));
        edgeGeometry.setAttribute('grup', new THREE.Float32BufferAttribute(newGrupArray, 3));
        if (colorAttr) {
            edgeGeometry.setAttribute('color', new THREE.Float32BufferAttribute(newColors, 3));
        }
        if (alphaAttr) {
            edgeGeometry.setAttribute('alpha', new THREE.Float32BufferAttribute(newAlphas, 1));
        }
        if (radiusGroupAttr) {
            edgeGeometry.setAttribute('radiusGroup', new THREE.Float32BufferAttribute(newRadiusGroups, 1));
        }
        if (faceIdAttr) {
            edgeGeometry.setAttribute('faceId', new THREE.Float32BufferAttribute(newFaceIds, 1));
        }
        if (adjacentFaceAAttr) {
            edgeGeometry.setAttribute('adjacentFaceA', new THREE.Float32BufferAttribute(newAdjacentFaceA, 1));
        }
        if (adjacentFaceBAttr) {
            edgeGeometry.setAttribute('adjacentFaceB', new THREE.Float32BufferAttribute(newAdjacentFaceB, 1));
        }
    }

    /**
     * 🚀 WASM ile hızlı eğrisel kenar tessellation
     * Pozisyon hesaplaması WASM'da (10-50x hızlı), attribute kopyalama JS'te
     * @returns true eğer WASM başarıyla uygulandıysa
     */
    private _tessellateCurvedEdgesWasm(
        edgeGeometry: THREE.BufferGeometry,
        topology: StepTopologyInfo,
        wasmModule: any,
        positionAttr: THREE.BufferAttribute,
        curveTypeAttr: THREE.BufferAttribute,
        radiusAttr: THREE.BufferAttribute | undefined,
        edgeIdAttr: THREE.BufferAttribute | undefined,
        colorAttr: THREE.BufferAttribute | undefined,
        alphaAttr: THREE.BufferAttribute | undefined,
        grupAttr: THREE.BufferAttribute | undefined,
        radiusGroupAttr: THREE.BufferAttribute | undefined,
        faceIdAttr: THREE.BufferAttribute | undefined,
        adjacentFaceAAttr: THREE.BufferAttribute | undefined,
        adjacentFaceBAttr: THREE.BufferAttribute | undefined,
    ): boolean {
        const t0 = performance.now();
        const vertexCount = positionAttr.count;
        const edgeCount = Math.floor(vertexCount / 2);
        if (edgeCount === 0) return false;

        // Topoloji edge map oluştur
        const topoEdgeById = new Map<number, any>();
        for (const edgeInfo of topology.edges.values()) {
            if (edgeInfo.id) {
                const numId = parseInt(edgeInfo.id.replace('#', ''), 10);
                if (!isNaN(numId)) topoEdgeById.set(numId, edgeInfo);
            }
        }

        // Koordinat offset hesapla
        const circleEdges = Array.from(topology.edges.values()).filter(
            (e: any) => (e.curveType === 'CIRCLE' || e.curveType === 'ELLIPSE') && e.center && e.radius
        );
        let coordOffX = 0, coordOffY = 0, coordOffZ = 0;
        if (circleEdges.length > 0) {
            let minX = Infinity, minY = Infinity, minZ = Infinity;
            let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
            for (let i = 0; i < vertexCount; i++) {
                const x = positionAttr.getX(i), y = positionAttr.getY(i), z = positionAttr.getZ(i);
                if (x < minX) minX = x; if (x > maxX) maxX = x;
                if (y < minY) minY = y; if (y > maxY) maxY = y;
                if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
            }
            let sx = 0, sy = 0, sz = 0;
            for (const c of circleEdges) { sx += c.center.x; sy += c.center.y; sz += c.center.z; }
            sx /= circleEdges.length; sy /= circleEdges.length; sz /= circleEdges.length;
            coordOffX = (minX + maxX) / 2 - sx;
            coordOffY = (minY + maxY) / 2 - sy;
            coordOffZ = (minZ + maxZ) / 2 - sz;
        }

        // WASM girdilerini hazırla (flat array'ler)
        const positions = new Float32Array(edgeCount * 6);
        const curveTypes = new Float32Array(edgeCount);
        const radii = new Float32Array(edgeCount);
        const centers = new Float32Array(edgeCount * 3);
        const axes = new Float32Array(edgeCount * 3);
        const hasTopologyArr = new Float32Array(edgeCount);

        for (let ei = 0; ei < edgeCount; ei++) {
            const v1 = ei * 2, v2 = v1 + 1, b = ei * 6;
            positions[b] = positionAttr.getX(v1); positions[b+1] = positionAttr.getY(v1); positions[b+2] = positionAttr.getZ(v1);
            positions[b+3] = positionAttr.getX(v2); positions[b+4] = positionAttr.getY(v2); positions[b+5] = positionAttr.getZ(v2);
            curveTypes[ei] = curveTypeAttr.getX(v1);
            radii[ei] = radiusAttr ? radiusAttr.getX(v1) : 0;

            const numEdgeId = edgeIdAttr ? edgeIdAttr.getX(v1) : -1;
            const topoEdge = numEdgeId >= 0 ? topoEdgeById.get(numEdgeId) : undefined;
            if (topoEdge?.center && topoEdge?.radius > 0) {
                const faceA = adjacentFaceAAttr ? adjacentFaceAAttr.getX(v1) : -1;
                const faceB = adjacentFaceBAttr ? adjacentFaceBAttr.getX(v1) : -1;
                if (faceA < 0 && faceB < 0) {
                    curveTypes[ei] = 0;
                    continue;
                }
                hasTopologyArr[ei] = 1.0;
                radii[ei] = topoEdge.radius;
                const cb = ei * 3;
                centers[cb] = topoEdge.center.x + coordOffX;
                centers[cb+1] = topoEdge.center.y + coordOffY;
                centers[cb+2] = topoEdge.center.z + coordOffZ;
                if (topoEdge.axis) { axes[cb] = topoEdge.axis.x; axes[cb+1] = topoEdge.axis.y; axes[cb+2] = topoEdge.axis.z; }
                else { axes[cb+2] = 1.0; }
            }
        }

        // WASM çağrısı
        const wasmResult = wasmModule.tessellateCircularEdges(
            positions, curveTypes, radii, centers, axes, hasTopologyArr, 8, 32
        );

        if (!wasmResult || wasmResult.length < 4) return false;

        const totalVertices = wasmResult[0] as number;
        const tessellatedEdges = wasmResult[1] as number;
        if (totalVertices === 0 || tessellatedEdges === 0) return false;

        // WASM sonuçları: ilk 4 float metadata, gerisi pozisyon verisi
        const wasmPositions = new Float32Array(totalVertices * 3);
        for (let i = 0; i < totalVertices * 3; i++) {
            wasmPositions[i] = wasmResult[4 + i];
        }

        // Attribute kopyalama: edge sınırlarını bul
        const newCurveTypes: number[] = [];
        const newRadii: number[] = [];
        const newEdgeIds: number[] = [];
        const newColors: number[] = [];
        const newAlphas: number[] = [];
        const newRadiusGroups: number[] = [];
        const newFaceIds: number[] = [];
        const newAdjacentFaceA: number[] = [];
        const newAdjacentFaceB: number[] = [];
        const groupRanges: { start: number; count: number; groupId: number }[] = [];

        let vtxCursor = 0;
        for (let ei = 0; ei < edgeCount; ei++) {
            const v1 = ei * 2;
            const ct = curveTypeAttr.getX(v1);

            let edgeVtxCount: number;
            if (ct !== 2 && ct !== 3) {
                edgeVtxCount = 2;
            } else {
                edgeVtxCount = 2;
                if (ei + 1 < edgeCount) {
                    const nextP1x = positions[(ei + 1) * 6];
                    const nextP1y = positions[(ei + 1) * 6 + 1];
                    const nextP1z = positions[(ei + 1) * 6 + 2];
                    for (let vi = vtxCursor + 2; vi < totalVertices; vi += 2) {
                        const px = wasmPositions[vi * 3];
                        const py = wasmPositions[vi * 3 + 1];
                        const pz = wasmPositions[vi * 3 + 2];
                        const dx = px - nextP1x, dy = py - nextP1y, dz = pz - nextP1z;
                        if (dx * dx + dy * dy + dz * dz < 1e-6) {
                            edgeVtxCount = vi - vtxCursor;
                            break;
                        }
                    }
                    if (edgeVtxCount === 2 && (ct === 2 || ct === 3)) {
                        edgeVtxCount = totalVertices - vtxCursor;
                    }
                } else {
                    edgeVtxCount = totalVertices - vtxCursor;
                }
            }

            const startVtx = vtxCursor;
            const gid = grupAttr ? grupAttr.getZ(v1) : ei;

            for (let i = 0; i < edgeVtxCount; i++) {
                newCurveTypes.push(curveTypeAttr.getX(v1));
                newRadii.push(radiusAttr ? radiusAttr.getX(v1) : 0);
                newEdgeIds.push(edgeIdAttr ? edgeIdAttr.getX(v1) : -1);
                if (colorAttr) newColors.push(colorAttr.getX(v1), colorAttr.getY(v1), colorAttr.getZ(v1));
                newAlphas.push(alphaAttr ? alphaAttr.getX(v1) : 1);
                newRadiusGroups.push(radiusGroupAttr ? radiusGroupAttr.getX(v1) : -1);
                newFaceIds.push(faceIdAttr ? faceIdAttr.getX(v1) : -1);
                newAdjacentFaceA.push(adjacentFaceAAttr ? adjacentFaceAAttr.getX(v1) : -1);
                newAdjacentFaceB.push(adjacentFaceBAttr ? adjacentFaceBAttr.getX(v1) : -1);
            }

            vtxCursor += edgeVtxCount;
            groupRanges.push({ start: startVtx, count: edgeVtxCount, groupId: gid });
        }

        // Grup attribute
        const newGrupArray = new Float32Array(totalVertices * 3);
        for (const range of groupRanges) {
            for (let i = range.start; i < range.start + range.count; i++) {
                const b = i * 3;
                newGrupArray[b] = range.start;
                newGrupArray[b + 1] = range.count;
                newGrupArray[b + 2] = range.groupId;
            }
        }

        // Geometry güncelle
        edgeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(wasmPositions, 3));
        edgeGeometry.setAttribute('curveType', new THREE.Float32BufferAttribute(newCurveTypes, 1));
        edgeGeometry.setAttribute('radius', new THREE.Float32BufferAttribute(newRadii, 1));
        edgeGeometry.setAttribute('edgeId', new THREE.Float32BufferAttribute(newEdgeIds, 1));
        edgeGeometry.setAttribute('grup', new THREE.Float32BufferAttribute(newGrupArray, 3));
        if (colorAttr) edgeGeometry.setAttribute('color', new THREE.Float32BufferAttribute(newColors, 3));
        if (alphaAttr) edgeGeometry.setAttribute('alpha', new THREE.Float32BufferAttribute(newAlphas, 1));
        if (radiusGroupAttr) edgeGeometry.setAttribute('radiusGroup', new THREE.Float32BufferAttribute(newRadiusGroups, 1));
        if (faceIdAttr) edgeGeometry.setAttribute('faceId', new THREE.Float32BufferAttribute(newFaceIds, 1));
        if (adjacentFaceAAttr) edgeGeometry.setAttribute('adjacentFaceA', new THREE.Float32BufferAttribute(newAdjacentFaceA, 1));
        if (adjacentFaceBAttr) edgeGeometry.setAttribute('adjacentFaceB', new THREE.Float32BufferAttribute(newAdjacentFaceB, 1));

        const dt = performance.now() - t0;
        console.log(`[WASM] tessellateCurvedEdges: ${tessellatedEdges}/${edgeCount} edges in ${dt.toFixed(2)}ms (${totalVertices} vtx)`);
        return true;
    }

    /**
     * Aynı radius grubuna sahip edge'leri tek bir grup altında birleştirir
     */
    private regroupEdgesByRadius(edgeGeometry: THREE.BufferGeometry): void {
        const radiusAttr = edgeGeometry.getAttribute('radiusGroup') as THREE.BufferAttribute | undefined;
        const positionAttr = edgeGeometry.getAttribute('position') as THREE.BufferAttribute | undefined;
        const groupAttr = edgeGeometry.getAttribute('grup') as THREE.BufferAttribute | undefined;

        if (!radiusAttr || !positionAttr || !groupAttr) {
            return;
        }

        const vertexCount = positionAttr.count;
        if (vertexCount === 0) {
            return;
        }

        const edgeCount = Math.floor(vertexCount / 2);
        if (edgeCount === 0) {
            return;
        }

        const groupedEdges = new Map<number, number[]>();
        const ungroupedEdges: { edgeIdx: number; groupId: number }[] = [];
        let hasGrouped = false;

        for (let edgeIdx = 0; edgeIdx < edgeCount; edgeIdx++) {
            const v1 = edgeIdx * 2;
            const groupValue = Math.round(radiusAttr.getX(v1));
            if (groupValue >= 0) {
                hasGrouped = true;
                if (!groupedEdges.has(groupValue)) {
                    groupedEdges.set(groupValue, []);
                }
                groupedEdges.get(groupValue)!.push(edgeIdx);
            } else {
                ungroupedEdges.push({ edgeIdx, groupId: groupAttr.getZ(v1) });
            }
        }

        if (!hasGrouped) {
            return;
        }

        const colorAttr = edgeGeometry.getAttribute('color') as THREE.BufferAttribute | undefined;
        const alphaAttr = edgeGeometry.getAttribute('alpha') as THREE.BufferAttribute | undefined;
        const curveTypeAttr = edgeGeometry.getAttribute('curveType') as THREE.BufferAttribute | undefined;
        const radiusValueAttr = edgeGeometry.getAttribute('radius') as THREE.BufferAttribute | undefined;
        const edgeIdAttr = edgeGeometry.getAttribute('edgeId') as THREE.BufferAttribute | undefined;
        const faceIdAttr = edgeGeometry.getAttribute('faceId') as THREE.BufferAttribute | undefined;
        const adjacentFaceAAttr = edgeGeometry.getAttribute('adjacentFaceA') as THREE.BufferAttribute | undefined;
        const adjacentFaceBAttr = edgeGeometry.getAttribute('adjacentFaceB') as THREE.BufferAttribute | undefined;

        const newPositions: number[] = [];
        const newColors: number[] = [];
        const newAlphas: number[] = [];
        const newCurveTypes: number[] = [];
        const newRadiusValues: number[] = [];
        const newEdgeIds: number[] = [];
        const newFaceIds: number[] = [];
        const newAdjacentFaceA: number[] = [];
        const newAdjacentFaceB: number[] = [];
        const newRadiusGroups: number[] = [];

        const pushVertex = (idx: number) => {
            newPositions.push(positionAttr.getX(idx), positionAttr.getY(idx), positionAttr.getZ(idx));
            if (colorAttr) {
                newColors.push(colorAttr.getX(idx), colorAttr.getY(idx), colorAttr.getZ(idx));
            }
            if (alphaAttr) {
                newAlphas.push(alphaAttr.getX(idx));
            }
            if (curveTypeAttr) {
                newCurveTypes.push(curveTypeAttr.getX(idx));
            }
            if (radiusValueAttr) {
                newRadiusValues.push(radiusValueAttr.getX(idx));
            }
            if (edgeIdAttr) {
                newEdgeIds.push(edgeIdAttr.getX(idx));
            }
            if (faceIdAttr) {
                newFaceIds.push(faceIdAttr.getX(idx));
            }
            if (adjacentFaceAAttr) {
                newAdjacentFaceA.push(adjacentFaceAAttr.getX(idx));
            }
            if (adjacentFaceBAttr) {
                newAdjacentFaceB.push(adjacentFaceBAttr.getX(idx));
            }
            newRadiusGroups.push(radiusAttr.getX(idx));
        };

        const pushEdge = (edgeIdx: number) => {
            const v1 = edgeIdx * 2;
            const v2 = v1 + 1;
            pushVertex(v1);
            pushVertex(v2);
        };

        const groupRanges: { start: number; count: number; groupId: number }[] = [];
        let vertexCursor = 0;

        groupedEdges.forEach((edges, groupId) => {
            if (edges.length === 0) {
                return;
            }
            const start = vertexCursor;
            for (const edgeIdx of edges) {
                pushEdge(edgeIdx);
                vertexCursor += 2;
            }
            const count = vertexCursor - start;
            if (count > 0) {
                groupRanges.push({ start, count, groupId });
            }
        });

        for (const entry of ungroupedEdges) {
            const start = vertexCursor;
            pushEdge(entry.edgeIdx);
            vertexCursor += 2;
            const count = vertexCursor - start;
            groupRanges.push({ start, count, groupId: entry.groupId });
        }

        if (vertexCursor === 0) {
            return;
        }

        const grupArray = new Float32Array(vertexCursor * 3);
        for (const range of groupRanges) {
            const start = range.start;
            const count = range.count;
            const gid = range.groupId;
            for (let i = start; i < start + count; i++) {
                const base = i * 3;
                grupArray[base] = start;
                grupArray[base + 1] = count;
                grupArray[base + 2] = gid;
            }
        }

        edgeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
        if (colorAttr) {
            edgeGeometry.setAttribute('color', new THREE.Float32BufferAttribute(newColors, 3));
        }
        if (alphaAttr) {
            edgeGeometry.setAttribute('alpha', new THREE.Float32BufferAttribute(newAlphas, 1));
        }
        if (curveTypeAttr) {
            edgeGeometry.setAttribute('curveType', new THREE.Float32BufferAttribute(newCurveTypes, 1));
        }
        if (radiusValueAttr) {
            edgeGeometry.setAttribute('radius', new THREE.Float32BufferAttribute(newRadiusValues, 1));
        }
        if (edgeIdAttr) {
            edgeGeometry.setAttribute('edgeId', new THREE.Float32BufferAttribute(newEdgeIds, 1));
        }
        if (faceIdAttr) {
            edgeGeometry.setAttribute('faceId', new THREE.Float32BufferAttribute(newFaceIds, 1));
        }
        if (adjacentFaceAAttr) {
            edgeGeometry.setAttribute('adjacentFaceA', new THREE.Float32BufferAttribute(newAdjacentFaceA, 1));
        }
        if (adjacentFaceBAttr) {
            edgeGeometry.setAttribute('adjacentFaceB', new THREE.Float32BufferAttribute(newAdjacentFaceB, 1));
        }
        edgeGeometry.setAttribute('radiusGroup', new THREE.Float32BufferAttribute(newRadiusGroups, 1));
        edgeGeometry.setAttribute('grup', new THREE.Float32BufferAttribute(grupArray, 3));
    }

    /**
     * Face geometry'sine topoloji bilgilerini attribute olarak ekler
     * @param meshGeometry Mesh BufferGeometry
     * @param brepFaces OCCT'den gelen brep_faces bilgisi
     * @param topology STEP'ten parse edilen topoloji bilgisi
     * @param stepContent STEP dosya içeriği (face eşleştirme için)
     * @returns Güncellenmiş mesh geometry (topoloji attribute'ları ile)
     */
    private addTopologyToFaces(
        meshGeometry: THREE.BufferGeometry,
        brepFaces: any[],
        topology: StepTopologyInfo,
        stepContent: string
    ): THREE.BufferGeometry {
        if (!meshGeometry.attributes['position'] || topology.faces.size === 0) {
            return meshGeometry;
        }

        const vertexCount = meshGeometry.attributes['position'].count;
        const triCount = Math.floor(vertexCount / 3);

        // Yeni attribute'lar
        // surfaceType: 0=UNKNOWN, 1=PLANE, 2=CYLINDRICAL, 3=SPHERICAL, 4=TOROIDAL, 5=CONICAL, 6=B_SPLINE
        const surfaceTypes = new Float32Array(vertexCount);
        // surfaceRadius: Silindir/küre için radius
        const surfaceRadii = new Float32Array(vertexCount);

        // Surface type mapping
        const surfaceTypeMap: { [key: string]: number } = {
            'UNKNOWN': 0,
            'PLANE': 1,
            'CYLINDRICAL_SURFACE': 2,
            'SPHERICAL_SURFACE': 3,
            'TOROIDAL_SURFACE': 4,
            'CONICAL_SURFACE': 5,
            'B_SPLINE_SURFACE': 6
        };

        // STEP dosyasındaki ADVANCED_FACE'leri sırala
        const advancedFaceRegex = /#(\d+)\s*=\s*ADVANCED_FACE\s*\([^)]*,\s*\(([^)]*)\)\s*,\s*#(\d+)/g;
        const faceToSurface: Map<number, string> = new Map();
        let match;
        let faceIdx = 0;

        while ((match = advancedFaceRegex.exec(stepContent)) !== null) {
            const surfaceId = match[3];
            faceToSurface.set(faceIdx, surfaceId);
            faceIdx++;
        }

        // Triangle → Face mapping oluştur
        const triangleFaceMap = new Uint32Array(triCount);
        triangleFaceMap.fill(0);

        if (brepFaces && brepFaces.length > 0) {
            for (let f = 0; f < brepFaces.length; f++) {
                const face = brepFaces[f];
                const firstTri = Math.max(0, face.first || 0);
                const lastTri = face.last !== undefined ? Math.min(face.last, triCount - 1) : triCount - 1;

                for (let triIdx = firstTri; triIdx <= lastTri; triIdx++) {
                    if (triIdx < triCount) {
                        triangleFaceMap[triIdx] = f;
                    }
                }
            }
        }

        // Her üçgen için surface type ve radius ata
        for (let triIdx = 0; triIdx < triCount; triIdx++) {
            const faceId = triangleFaceMap[triIdx];
            const surfaceId = faceToSurface.get(faceId);

            let surfaceType = 0;
            let surfaceRadius = 0;

            if (surfaceId && topology.faces.has(surfaceId)) {
                const faceInfo = topology.faces.get(surfaceId)!;
                surfaceType = surfaceTypeMap[faceInfo.surfaceType] || 0;
                surfaceRadius = faceInfo.radius ?? 0;
            }

            // Her üçgenin 3 vertex'i
            for (let v = 0; v < 3; v++) {
                const vIdx = triIdx * 3 + v;
                surfaceTypes[vIdx] = surfaceType;
                surfaceRadii[vIdx] = surfaceRadius;
            }
        }

        // Attribute'ları geometry'ye ekle
        meshGeometry.setAttribute('surfaceType', new THREE.Float32BufferAttribute(surfaceTypes, 1));
        meshGeometry.setAttribute('surfaceRadius', new THREE.Float32BufferAttribute(surfaceRadii, 1));

        return meshGeometry;
    }

    /**
     * STEP dosyasını tamamen parse ederek face'ler ve renkler arasındaki ilişkiyi bulur
     * @param stepContent STEP dosyasının içeriği
     * @returns Face ID'leri ve renkleri içeren mapping
     */
    private parseStepColorsAndFaces(stepContent: string): { [faceId: string]: number[] } {
        // Tüm entity'leri parse et
        const entities: { [id: string]: any } = {};
        const faceColorMap: { [faceId: string]: number[] } = {};

        // Satırları ayır ve comment'leri temizle
        const lines = stepContent.split('\n').map(line => line.split('/*')[0].trim()).filter(line => line && !line.startsWith('/*'));

        // Her entity'yi parse et
        for (const line of lines) {
            // Entity ID'sini al
            const entityMatch = line.match(/^#(\d+)\s*=\s*(.+);?\s*$/);
            if (!entityMatch) continue;

            const entityId = entityMatch[1];
            const entityContent = entityMatch[2].trim();

            // Önce her entity'yi temel olarak kaydet
            entities[entityId] = {
                type: 'UNKNOWN',
                content: entityContent
            };

            // Entity türüne göre detaylı parse et
            if (entityContent.includes('COLOUR_RGB')) {
                // RGB color: #123 = COLOUR_RGB('',0.5,0.2,0.8);
                const rgbMatch = entityContent.match(/COLOUR_RGB\s*\(\s*'[^']*'\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)\s*\)/);
                if (rgbMatch) {
                    entities[entityId] = {
                        type: 'COLOR_RGB',
                        r: parseFloat(rgbMatch[1]),
                        g: parseFloat(rgbMatch[2]),
                        b: parseFloat(rgbMatch[3])
                    };
                }
            } else if (entityContent.includes('DRAUGHTING_PRE_DEFINED_COLOUR')) {
                // Pre-defined color: #124 = DRAUGHTING_PRE_DEFINED_COLOUR('red');
                const colorMatch = entityContent.match(/DRAUGHTING_PRE_DEFINED_COLOUR\s*\(\s*'([^']+)'\s*\)/);
                if (colorMatch) {
                    const colorName = colorMatch[1].toLowerCase();
                    const predefinedColors: { [key: string]: [number, number, number] } = {
                        'red': [1.0, 0.0, 0.0], 'green': [0.0, 1.0, 0.0], 'blue': [0.0, 0.0, 1.0],
                        'yellow': [1.0, 1.0, 0.0], 'cyan': [0.0, 1.0, 1.0], 'magenta': [1.0, 0.0, 1.0],
                        'white': [1.0, 1.0, 1.0], 'black': [0.0, 0.0, 0.0], 'grey': [0.5, 0.5, 0.5],
                        'orange': [1.0, 0.5, 0.0], 'brown': [0.6, 0.3, 0.1], 'pink': [1.0, 0.75, 0.8]
                    };
                    if (predefinedColors[colorName]) {
                        entities[entityId] = {
                            type: 'PREDEFINED_COLOR',
                            name: colorName,
                            r: predefinedColors[colorName][0],
                            g: predefinedColors[colorName][1],
                            b: predefinedColors[colorName][2]
                        };
                    }
                }
            } else if (entityContent.includes('FILL_AREA_STYLE_COLOUR')) {
                // Color reference: #125 = FILL_AREA_STYLE_COLOUR('',#123);
                const refMatch = entityContent.match(/FILL_AREA_STYLE_COLOUR\s*\(\s*'[^']*'\s*,\s*#(\d+)\s*\)/);
                if (refMatch) {
                    entities[entityId] = {
                        type: 'COLOR_REFERENCE',
                        reference: refMatch[1]
                    };
                }
            } else if (entityContent.includes('SURFACE_STYLE_FILL_AREA')) {
                // Fill area reference: #126 = SURFACE_STYLE_FILL_AREA(#127);
                const refMatch = entityContent.match(/SURFACE_STYLE_FILL_AREA\s*\(\s*#(\d+)\s*\)/);
                if (refMatch) {
                    entities[entityId] = {
                        type: 'FILL_AREA_REFERENCE',
                        reference: refMatch[1]
                    };
                }
            } else if (entityContent.includes('SURFACE_SIDE_STYLE')) {
                // Side style reference: #127 = SURFACE_SIDE_STYLE('',#128);
                const refMatch = entityContent.match(/SURFACE_SIDE_STYLE\s*\(\s*'[^']*'\s*,\s*#(\d+)\s*\)/);
                if (refMatch) {
                    entities[entityId] = {
                        type: 'SIDE_STYLE_REFERENCE',
                        reference: refMatch[1]
                    };
                }
            } else if (entityContent.includes('SURFACE_STYLE_USAGE')) {
                // Style usage: #128 = SURFACE_STYLE_USAGE(.BOTH.,#129);
                const refMatch = entityContent.match(/SURFACE_STYLE_USAGE\s*\([^,]+,\s*#(\d+)\s*\)/);
                if (refMatch) {
                    entities[entityId] = {
                        type: 'STYLE_USAGE_REFERENCE',
                        reference: refMatch[1]
                    };
                }
            } else if (entityContent.includes('STYLED_ITEM')) {
                // Styled item: #129 = STYLED_ITEM(' ',(#130),#131);
                const styleMatch = entityContent.match(/STYLED_ITEM\s*\(\s*'[^']*'\s*,\s*\(([^)]+)\)\s*,\s*#(\d+)\s*\)/);
                if (styleMatch) {
                    const itemRefs = styleMatch[1].split(',').map((ref: string) => ref.trim().replace('#', ''));
                    entities[entityId] = {
                        type: 'STYLED_ITEM',
                        items: itemRefs,
                        styleReference: styleMatch[2]
                    };
                }
            } else if (entityContent.includes('PRESENTATION_STYLE_ASSIGNMENT')) {
                // Presentation style assignment: #34390 = PRESENTATION_STYLE_ASSIGNMENT((#34380));
                const presentationMatch = entityContent.match(/PRESENTATION_STYLE_ASSIGNMENT\s*\(\s*\(([^)]+)\s*\)/);
                if (presentationMatch) {
                    const refIds = presentationMatch[1].split(',').map((ref: string) => ref.trim().replace('#', ''));
                    entities[entityId] = {
                        type: 'PRESENTATION_STYLE_ASSIGNMENT',
                        references: refIds
                    };
                }
            } else if (entityContent.includes('MANIFOLD_SOLID')) {
                // Manifold solid: #130 = MANIFOLD_SOLID(' ',(#131),#132);
                const manifoldMatch = entityContent.match(/MANIFOLD_SOLID\s*\(\s*'[^']*'\s*,\s*\(([^)]+)\s*\)\s*,\s*#(\d+)\s*\)/);
                if (manifoldMatch) {
                    const shellIds = manifoldMatch[1].split(',').map((ref: string) => ref.trim().replace('#', ''));
                    const otherRef = manifoldMatch[2].trim().replace('#', '');
                    entities[entityId] = {
                        type: 'MANIFOLD_SOLID',
                        shells: shellIds,
                        other: otherRef
                    };
                }
            } else if (entityContent.includes('CLOSED_SHELL')) {
                // Closed shell: #131 = CLOSED_SHELL(' ',(#132,#133,#134,#135,#136,#137,#138,#139,#140,#141,#142,#143,#144,#145,#146,#147,#148));
                const shellMatch = entityContent.match(/CLOSED_SHELL\s*\(\s*'[^']*'\s*,\s*\(([^)]+)\s*\)/);
                if (shellMatch) {
                    const faceIds = shellMatch[1].split(',').map((ref: string) => ref.trim().replace('#', ''));
                    entities[entityId] = {
                        type: 'CLOSED_SHELL',
                        faces: faceIds
                    };
                }
            } else if (entityContent.includes('ADVANCED_FACE') || entityContent.includes('FACE_SURFACE')) {
                // Face entity: #130 = ADVANCED_FACE(' ',(#131),#132,.T.);
                const faceMatch = entityContent.match(/(ADVANCED_FACE|FACE_SURFACE)\s*\(\s*'[^']*'\s*,\s*\(([^)]+)\)/);
                if (faceMatch) {
                    entities[entityId] = {
                        type: 'ADVANCED_FACE',
                        bounds: faceMatch[2].split(',').map((ref: string) => ref.trim().replace('#', ''))
                    };
                }
            }
        }

        // Şimdi face'lerle renkleri ilişkilendir

        // İlk olarak tüm renkleri çözümle
        const resolvedColors: { [id: string]: number[] } = {};

        for (const [id, entity] of Object.entries(entities)) {
            if (entity.type === 'COLOR_RGB' || entity.type === 'PREDEFINED_COLOR') {
                resolvedColors[id] = [entity.r, entity.g, entity.b];
            } else if (entity.type === 'COLOR_REFERENCE') {
                const referencedColor = resolvedColors[entity.reference];
                if (referencedColor) {
                    resolvedColors[id] = referencedColor;
                }
            }
        }

        // Style chain'i takip ederek renkleri bul
        const resolveStyleChain = (entityId: string): number[] | null => {
            const entity = entities[entityId];
            if (!entity) return null;

            if (entity.type === 'COLOR_REFERENCE' || entity.type === 'FILL_AREA_REFERENCE' ||
                entity.type === 'SIDE_STYLE_REFERENCE' || entity.type === 'STYLE_USAGE_REFERENCE') {
                return resolveStyleChain(entity.reference);
            }

            if (entity.type === 'COLOR_RGB' || entity.type === 'PREDEFINED_COLOR') {
                return [entity.r, entity.g, entity.b];
            }

            return null;
        };

        // Yeni entity referans zincirini çözümle
        const resolveEntityChain = (entityId: string): string[] => {
            const entity = entities[entityId];
            if (!entity) {
                return [];
            }

            switch (entity.type) {
                case 'PRESENTATION_STYLE_ASSIGNMENT':
                    // PRESENTATION_STYLE_ASSIGNMENT → ?
                    return entity.references.flatMap((ref: string) => {
                        return resolveEntityChain(ref);
                    });

                case 'MANIFOLD_SOLID':
                    // MANIFOLD_SOLID → CLOSED_SHELL
                    const shellRefs = [...(entity.shells || []), entity.other].filter(Boolean);
                    return shellRefs.flatMap((ref: string) => {
                        return resolveEntityChain(ref);
                    });

                case 'CLOSED_SHELL':
                    // CLOSED_SHELL → ADVANCED_FACE
                    return entity.faces || [];

                case 'ADVANCED_FACE':
                    return [entityId];

                case 'UNKNOWN':
                    // Bilinmeyen entity türü için orijinal içeriği kontrol et
                    if (entity.content.includes('MANIFOLD_SOLID')) {
                        // MANIFOLD_SOLID pattern'ini manuel olarak parse et
                        const manifoldMatch = entity.content.match(/MANIFOLD_SOLID\s*\(\s*'[^']*'\s*,\s*\(([^)]+)\s*\)\s*,\s*#(\d+)\s*\)/);
                        if (manifoldMatch) {
                            const shellIds = manifoldMatch[1].split(',').map((ref: string) => ref.trim().replace('#', ''));
                            const otherRef = manifoldMatch[2].trim().replace('#', '');
                            return [...shellIds, otherRef].flatMap((ref: string) => resolveEntityChain(ref));
                        }
                    }
                    if (entity.content.includes('CLOSED_SHELL')) {
                        // CLOSED_SHELL pattern'ini manuel olarak parse et
                        const shellMatch = entity.content.match(/CLOSED_SHELL\s*\(\s*'[^']*'\s*,\s*\(([^)]+)\s*\)/);
                        if (shellMatch) {
                            const faceIds = shellMatch[1].split(',').map((ref: string) => ref.trim().replace('#', ''));
                            return faceIds;
                        }
                    }
                    break;

                default:
                    return [];
            }
            return [];
        };

        // STYLED_ITEM'ları işle
        for (const [id, entity] of Object.entries(entities)) {
            if (entity.type === 'STYLED_ITEM') {

                const color = resolveStyleChain(entity.styleReference);
                if (color) {
                    // Her item için entity zincirini takip et
                    for (const itemId of entity.items) {
                        const faceIds = resolveEntityChain(itemId);

                        for (const faceId of faceIds) {
                            faceColorMap[faceId] = color;
                        }
                    }
                }
            }
        }

        return faceColorMap;
    }

    /**
     * OCCT face index'i ile STEP entity ID'si arasında ilişki kurar
     * @param faceIndex OCCT mesh brep_faces array index'i
     * @param brepFaces OCCT'den gelen brep_faces array'i
     * @param stepContent STEP dosyasının içeriği
     * @returns STEP entity ID'si veya null
     */
    private findStepFaceIdForIndex(faceIndex: number, brepFaces: any[], stepContent: string): string | null {
        // Bu zor bir problem - OCCT'nin face sıralaması ile STEP entity sıralaması farklı olabilir
        // Şimdilik basit bir yaklaşım deneyelim: face index'i + 1'i STEP entity ID olarak kullan
        // Çünkü STEP entity'leri genellikle 1'den başlar ve sıralıdır

        // STEP dosyasındaki tüm ADVANCED_FACE entity'lerini bul
        const advancedFaceRegex = /#(\d+)\s*=\s*ADVANCED_FACE\s*\(/g;
        const faceIds: string[] = [];
        let match;

        while ((match = advancedFaceRegex.exec(stepContent)) !== null) {
            faceIds.push(match[1]);
        }

        // Face index'i ile STEP face ID'si arasında eşleştirme yap
        if (faceIndex < faceIds.length) {
            return faceIds[faceIndex];
        }

        // Alternatif yaklaşım: brep_faces içindeki triangle sayısına göre tahmin et
        // Daha karmaşık bir mapping algoritması gerekebilir

        return null;
    }

    /**
     * STEP dosyasından tam topoloji bilgisini parse eder
     * Face'lerin surface tiplerini, edge'lerin curve tiplerini ve radius'larını çıkarır
     * @param stepContent STEP dosyasının içeriği
     * @returns Topoloji bilgisi
     */
    public async parseStepTopologyAsync(stepContent: string): Promise<StepTopologyInfo> {
        // WASM'i dene (70% hız artışı)
        try {
            // console.log('🔷 parseStepTopologyAsync: WASM parsing başlıyor...');
            const wasmResult = await parseStepTopologyWasm(stepContent);
            // console.log('🔷 parseStepTopologyAsync: WASM sonucu:', {
            //     facesCount: Object.keys(wasmResult.faces || {}).length,
            //     edgesCount: Object.keys(wasmResult.edges || {}).length,
            //     radiusGroupsCount: Object.keys(wasmResult.radiusGroups || {}).length
            // });

            // WASM sonuçlarını StepTopologyInfo'ye dönüştür
            const topology: StepTopologyInfo = {
                faces: new Map(),
                edges: new Map(),
                radiusGroups: new Map()
            };

            // Faces'i convert et
            for (const [id, face] of Object.entries(wasmResult.faces || {})) {
                const f = face as any;
                const faceInfo: StepFaceInfo = {
                    id,
                    surfaceType: f.surfaceType || 'UNKNOWN',
                    radius: f.radius,
                    // 🔶 Vec3 formatı: { x, y, z } - array değil!
                    normal: f.axis ? new THREE.Vector3(f.axis.x, f.axis.y, f.axis.z) : undefined,
                    center: f.center ? new THREE.Vector3(f.center.x, f.center.y, f.center.z) : undefined,
                    adjacentFaces: []
                };
                topology.faces.set(id, faceInfo);
            }

            // Edges'i convert et - center, startPoint, endPoint bilgisi kritik!
            for (const [id, edge] of Object.entries(wasmResult.edges || {})) {
                const e = edge as any;
                const edgeInfo: StepEdgeInfo = {
                    id,
                    curveType: e.curveType || 'UNKNOWN',
                    radius: e.radius,
                    length: e.length,
                    adjacentFaces: [],
                    // 🔶 CIRCLE eşleştirmesi için center ve axis gerekli
                    center: e.center ? new THREE.Vector3(e.center.x, e.center.y, e.center.z) : undefined,
                    axis: e.axis ? new THREE.Vector3(e.axis.x, e.axis.y, e.axis.z) : undefined,
                    // 🔶 LINE eşleştirmesi için startPoint ve endPoint gerekli
                    startPoint: e.startPoint ? new THREE.Vector3(e.startPoint.x, e.startPoint.y, e.startPoint.z) : undefined,
                    endPoint: e.endPoint ? new THREE.Vector3(e.endPoint.x, e.endPoint.y, e.endPoint.z) : undefined
                };
                topology.edges.set(id, edgeInfo);
            }

            // Radius groups'ı convert et
            for (const [radiusStr, edgeIds] of Object.entries(wasmResult.radiusGroups || {})) {
                const radius = parseFloat(radiusStr);
                topology.radiusGroups.set(radius, edgeIds as string[]);
            }

            // console.log('🔷 parseStepTopologyAsync: Dönüşüm tamamlandı', {
            //     edgesWithCenter: Array.from(topology.edges.values()).filter(e => e.center).length,
            //     edgesWithRadius: Array.from(topology.edges.values()).filter(e => e.radius).length
            // });
            return topology;
        } catch (e) {
            console.warn('🔷 parseStepTopologyAsync: WASM hata, sync fallback kullanılıyor:', e);
            return this.parseStepTopologySync(stepContent);
        }
    }

    /**
     * Synchronous version - backward compatibility
     * WASM fail olursa veya sync gerekirse kullan
     */
    public parseStepTopology(stepContent: string): StepTopologyInfo {
        return this.parseStepTopologySync(stepContent);
    }

    private parseStepTopologySync(stepContent: string): StepTopologyInfo {
        const topology: StepTopologyInfo = {
            faces: new Map(),
            edges: new Map(),
            radiusGroups: new Map()
        };

        // Tüm entity'leri parse et
        const entities: { [id: string]: { type: string; params: string } } = {};

        // Entity pattern: #ID = ENTITY_NAME(params);
        const entityRegex = /#(\d+)\s*=\s*([A-Z_0-9]+)\s*\(([^;]*)\)\s*;/g;
        let match;

        while ((match = entityRegex.exec(stepContent)) !== null) {
            entities[match[1]] = {
                type: match[2],
                params: match[3]
            };
        }

        // Helper: Entity referansını çözümle
        const resolveRef = (ref: string): string | null => {
            const refMatch = ref.trim().match(/^#(\d+)$/);
            return refMatch ? refMatch[1] : null;
        };

        // Helper: Koordinat listesini parse et
        const parseCoords = (coordStr: string): number[] => {
            const nums = coordStr.match(/-?[\d.]+(?:[eE][+-]?\d+)?/g);
            return nums ? nums.map(n => parseFloat(n)) : [];
        };

        // Helper: CARTESIAN_POINT'ten Vector3 oluştur
        const getCartesianPoint = (id: string): THREE.Vector3 | null => {
            const entity = entities[id];
            if (!entity || entity.type !== 'CARTESIAN_POINT') return null;
            const coords = parseCoords(entity.params);
            if (coords.length >= 3) {
                return new THREE.Vector3(coords[0], coords[1], coords[2]);
            }
            return null;
        };

        // Helper: DIRECTION'dan Vector3 oluştur
        const getDirection = (id: string): THREE.Vector3 | null => {
            const entity = entities[id];
            if (!entity || entity.type !== 'DIRECTION') return null;
            const coords = parseCoords(entity.params);
            if (coords.length >= 3) {
                return new THREE.Vector3(coords[0], coords[1], coords[2]).normalize();
            }
            return null;
        };

        // Helper: AXIS2_PLACEMENT_3D'den center ve axis al
        const getAxisPlacement = (id: string): { center: THREE.Vector3 | null; axis: THREE.Vector3 | null } => {
            const entity = entities[id];
            if (!entity || entity.type !== 'AXIS2_PLACEMENT_3D') {
                return { center: null, axis: null };
            }

            // AXIS2_PLACEMENT_3D('',#point,#zAxis,#xAxis)
            const refs = entity.params.match(/#\d+/g) || [];
            let center: THREE.Vector3 | null = null;
            let axis: THREE.Vector3 | null = null;

            if (refs[0]) {
                const pointId = resolveRef(refs[0]);
                if (pointId) center = getCartesianPoint(pointId);
            }
            if (refs[1]) {
                const axisId = resolveRef(refs[1]);
                if (axisId) axis = getDirection(axisId);
            }

            return { center, axis };
        };

        const addRadiusGroup = (radius: number | undefined, edgeId: string) => {
            if (radius === undefined || isNaN(radius)) {
                return;
            }
            const rounded = Math.round(radius * 1000) / 1000;
            if (!topology.radiusGroups.has(rounded)) {
                topology.radiusGroups.set(rounded, []);
            }
            topology.radiusGroups.get(rounded)!.push(edgeId);
        };

        // 1. Surface'leri parse et (PLANE, CYLINDRICAL_SURFACE, vs.)
        for (const [id, entity] of Object.entries(entities)) {
            let faceInfo: StepFaceInfo | null = null;

            switch (entity.type) {
                case 'PLANE': {
                    // PLANE('',#axis_placement)
                    const refs = entity.params.match(/#\d+/g) || [];
                    const placementId = refs[0] ? resolveRef(refs[0]) : null;
                    const placement = placementId ? getAxisPlacement(placementId) : { center: null, axis: null };

                    faceInfo = {
                        id,
                        surfaceType: 'PLANE',
                        center: placement.center ?? undefined,
                        normal: placement.axis ?? undefined
                    };
                    break;
                }

                case 'CYLINDRICAL_SURFACE': {
                    // CYLINDRICAL_SURFACE('',#axis_placement,radius)
                    const refs = entity.params.match(/#\d+/g) || [];
                    const nums = parseCoords(entity.params);
                    const placementId = refs[0] ? resolveRef(refs[0]) : null;
                    const placement = placementId ? getAxisPlacement(placementId) : { center: null, axis: null };

                    faceInfo = {
                        id,
                        surfaceType: 'CYLINDRICAL_SURFACE',
                        radius: nums.length > 0 ? nums[nums.length - 1] : undefined,
                        center: placement.center ?? undefined,
                        axis: placement.axis ?? undefined
                    };
                    break;
                }

                case 'SPHERICAL_SURFACE': {
                    // SPHERICAL_SURFACE('',#axis_placement,radius)
                    const refs = entity.params.match(/#\d+/g) || [];
                    const nums = parseCoords(entity.params);
                    const placementId = refs[0] ? resolveRef(refs[0]) : null;
                    const placement = placementId ? getAxisPlacement(placementId) : { center: null, axis: null };

                    faceInfo = {
                        id,
                        surfaceType: 'SPHERICAL_SURFACE',
                        radius: nums.length > 0 ? nums[nums.length - 1] : undefined,
                        center: placement.center ?? undefined
                    };
                    break;
                }

                case 'TOROIDAL_SURFACE': {
                    // TOROIDAL_SURFACE('',#axis_placement,major_radius,minor_radius)
                    const refs = entity.params.match(/#\d+/g) || [];
                    const nums = parseCoords(entity.params);
                    const placementId = refs[0] ? resolveRef(refs[0]) : null;
                    const placement = placementId ? getAxisPlacement(placementId) : { center: null, axis: null };

                    faceInfo = {
                        id,
                        surfaceType: 'TOROIDAL_SURFACE',
                        majorRadius: nums.length >= 1 ? nums[nums.length - 2] : undefined,
                        minorRadius: nums.length >= 2 ? nums[nums.length - 1] : undefined,
                        center: placement.center ?? undefined,
                        axis: placement.axis ?? undefined
                    };
                    break;
                }

                case 'CONICAL_SURFACE': {
                    // CONICAL_SURFACE('',#axis_placement,radius,semi_angle)
                    const refs = entity.params.match(/#\d+/g) || [];
                    const nums = parseCoords(entity.params);
                    const placementId = refs[0] ? resolveRef(refs[0]) : null;
                    const placement = placementId ? getAxisPlacement(placementId) : { center: null, axis: null };

                    faceInfo = {
                        id,
                        surfaceType: 'CONICAL_SURFACE',
                        radius: nums.length >= 2 ? nums[nums.length - 2] : undefined,
                        semiAngle: nums.length >= 1 ? nums[nums.length - 1] : undefined,
                        center: placement.center ?? undefined,
                        axis: placement.axis ?? undefined
                    };
                    break;
                }

                case 'B_SPLINE_SURFACE_WITH_KNOTS':
                case 'B_SPLINE_SURFACE': {
                    faceInfo = {
                        id,
                        surfaceType: 'B_SPLINE_SURFACE'
                    };
                    break;
                }
            }

            if (faceInfo) {
                topology.faces.set(id, faceInfo);
            }
        }

        const curveData: Record<string, { curveType: StepEdgeInfo['curveType']; radius?: number; center?: THREE.Vector3; axis?: THREE.Vector3; controlPoints?: THREE.Vector3[]; degree?: number; knots?: number[] }> = {};

        for (const [curveId, entity] of Object.entries(entities)) {
            switch (entity.type) {
                case 'LINE':
                    curveData[curveId] = { curveType: 'LINE' };
                    break;
                case 'CIRCLE': {
                    const refs = entity.params.match(/#\d+/g) || [];
                    const placementId = refs[0] ? resolveRef(refs[0]) : null;
                    const placement = placementId ? getAxisPlacement(placementId) : { center: null, axis: null };
                    const nums = parseCoords(entity.params);
                    const radius = nums.length > 0 ? nums[nums.length - 1] : undefined;
                    curveData[curveId] = {
                        curveType: 'CIRCLE',
                        radius,
                        center: placement.center ?? undefined,
                        axis: placement.axis ?? undefined
                    };
                    addRadiusGroup(radius, curveId);
                    break;
                }
                case 'ELLIPSE': {
                    const refs = entity.params.match(/#\d+/g) || [];
                    const placementId = refs[0] ? resolveRef(refs[0]) : null;
                    const placement = placementId ? getAxisPlacement(placementId) : { center: null, axis: null };
                    const nums = parseCoords(entity.params);
                    const radius = nums.length >= 2 ? nums[nums.length - 2] : undefined;
                    curveData[curveId] = {
                        curveType: 'ELLIPSE',
                        radius,
                        center: placement.center ?? undefined,
                        axis: placement.axis ?? undefined
                    };
                    break;
                }
                case 'B_SPLINE_CURVE':
                case 'B_SPLINE_CURVE_WITH_KNOTS': {
                    // B_SPLINE_CURVE_WITH_KNOTS('name', degree, (#cp1,#cp2,...), .UNSPECIFIED., .F., .F., (knot_multiplicities), (knot_values), .UNSPECIFIED.)
                    // B_SPLINE_CURVE('name', degree, (#cp1,#cp2,...), ...)
                    const bspRefs = entity.params.match(/#\d+/g) || [];
                    const bspNums = parseCoords(entity.params);
                    const degree = bspNums.length > 0 ? Math.round(bspNums[0]) : 3;

                    // Kontrol noktalarını çıkar (refs CARTESIAN_POINT'lere işaret eder)
                    const controlPoints: THREE.Vector3[] = [];
                    for (const ref of bspRefs) {
                        const cpId = resolveRef(ref);
                        if (cpId) {
                            const pt = getCartesianPoint(cpId);
                            if (pt) controlPoints.push(pt);
                        }
                    }

                    // Knot vektörünü çıkar (B_SPLINE_CURVE_WITH_KNOTS için)
                    let knots: number[] | undefined;
                    if (entity.type === 'B_SPLINE_CURVE_WITH_KNOTS') {
                        // Knot değerleri genelde parantez içinde son sayı gruplarındadır
                        // Tüm sayıları al: ilk sayı degree, kalanlar knot multiplicities + knot values
                        // Basit yaklaşım: degree'den sonra kontrol noktası sayısı + 1 sayı multiplicity, sonraki sayılar knot değerleri
                        const allNums = bspNums.slice(1); // degree hariç
                        if (controlPoints.length > 0 && allNums.length > controlPoints.length) {
                            // Multiplicities: ilk (controlPoints.length - degree + 1) sayı
                            // Knots: son (controlPoints.length - degree + 1) sayı
                            const numKnots = allNums.length - controlPoints.length + degree - 1;
                            if (numKnots > 0 && numKnots <= allNums.length) {
                                knots = allNums.slice(allNums.length - numKnots);
                            }
                        }
                    }

                    curveData[curveId] = {
                        curveType: 'B_SPLINE_CURVE',
                        controlPoints: controlPoints.length >= 2 ? controlPoints : undefined,
                        degree,
                        knots
                    };
                    break;
                }
                case 'TRIMMED_CURVE': {
                    const refs = entity.params.match(/#\d+/g) || [];
                    const basisId = refs[0] ? resolveRef(refs[0]) : null;
                    const basis = basisId ? curveData[basisId] : undefined;
                    const curveType = basis?.curveType === 'CIRCLE' || basis?.curveType === 'ELLIPSE'
                        ? basis.curveType
                        : basis?.curveType === 'B_SPLINE_CURVE'
                            ? 'B_SPLINE_CURVE'
                            : 'TRIMMED_CURVE';
                    curveData[curveId] = {
                        curveType,
                        radius: basis?.radius,
                        center: basis?.center ? basis.center.clone() : undefined,
                        axis: basis?.axis ? basis.axis.clone() : undefined,
                        controlPoints: basis?.controlPoints?.map(p => p.clone()),
                        degree: basis?.degree,
                        knots: basis?.knots ? [...basis.knots] : undefined
                    };
                    if (basis?.radius !== undefined) {
                        addRadiusGroup(basis.radius, curveId);
                    }
                    break;
                }
                default:
                    break;
            }
        }

        const getVertexPoint = (vertexId: string | null): THREE.Vector3 | null => {
            if (!vertexId) return null;
            const entity = entities[vertexId];
            if (!entity || entity.type !== 'VERTEX_POINT') return null;
            const refs = entity.params.match(/#\d+/g) || [];
            const pointId = refs[0] ? resolveRef(refs[0]) : null;
            return pointId ? getCartesianPoint(pointId) : null;
        };

        for (const [edgeId, entity] of Object.entries(entities)) {
            if (entity.type !== 'EDGE_CURVE') continue;

            const refs = entity.params.match(/#\d+/g) || [];
            if (refs.length < 3) continue;

            const startVertexId = resolveRef(refs[0]);
            const endVertexId = resolveRef(refs[1]);
            const curveRef = resolveRef(refs[2]);

            const startPoint = getVertexPoint(startVertexId);
            const endPoint = getVertexPoint(endVertexId);
            const baseCurve = curveRef ? curveData[curveRef] : undefined;

            const edgeInfo: StepEdgeInfo = {
                id: edgeId,
                curveType: baseCurve?.curveType ?? 'UNKNOWN',
                radius: baseCurve?.radius,
                center: baseCurve?.center ? baseCurve.center.clone() : undefined,
                axis: baseCurve?.axis ? baseCurve.axis.clone() : undefined,
                startPoint: startPoint ?? undefined,
                endPoint: endPoint ?? undefined,
                length: startPoint && endPoint ? startPoint.distanceTo(endPoint) : undefined,
                controlPoints: baseCurve?.controlPoints?.map(p => p.clone()),
                degree: baseCurve?.degree,
                knots: baseCurve?.knots ? [...baseCurve.knots] : undefined,
                adjacentFaces: []
            };

            topology.edges.set(edgeId, edgeInfo);

            if (baseCurve?.radius !== undefined) {
                addRadiusGroup(baseCurve.radius, edgeId);
            }
        }

        const addAdjacentFace = (edgeId: string | null, faceId: string) => {
            if (!edgeId) return;
            if (!topology.edges.has(edgeId)) {
                topology.edges.set(edgeId, {
                    id: edgeId,
                    curveType: 'UNKNOWN',
                    adjacentFaces: []
                });
            }
            const info = topology.edges.get(edgeId)!;
            if (!info.adjacentFaces.includes(faceId)) {
                info.adjacentFaces.push(faceId);
            }
        };

        for (const [faceId, entity] of Object.entries(entities)) {
            if (entity.type !== 'ADVANCED_FACE') continue;

            const refs = entity.params.match(/#\d+/g) || [];
            if (refs.length === 0) continue;

            const surfaceId = resolveRef(refs[refs.length - 1]);
            if (surfaceId && topology.faces.has(surfaceId)) {
                const surfaceInfo = topology.faces.get(surfaceId)!;
                topology.faces.set(faceId, {
                    ...surfaceInfo,
                    id: faceId
                });
            }

            for (let i = 0; i < refs.length - 1; i++) {
                const boundId = resolveRef(refs[i]);
                if (!boundId) continue;
                const boundEntity = entities[boundId];
                if (!boundEntity) continue;
                if (boundEntity.type !== 'FACE_OUTER_BOUND' && boundEntity.type !== 'FACE_BOUND') continue;

                const loopRefs = boundEntity.params.match(/#\d+/g) || [];
                const loopId = loopRefs[0] ? resolveRef(loopRefs[0]) : null;
                if (!loopId) continue;

                const loopEntity = entities[loopId];
                if (!loopEntity || loopEntity.type !== 'EDGE_LOOP') continue;

                const orientedRefs = loopEntity.params.match(/#\d+/g) || [];
                for (const orientedRef of orientedRefs) {
                    const orientedId = resolveRef(orientedRef);
                    if (!orientedId) continue;
                    const orientedEntity = entities[orientedId];
                    if (!orientedEntity || orientedEntity.type !== 'ORIENTED_EDGE') continue;

                    const orientedEdgeRefs = orientedEntity.params.match(/#\d+/g) || [];
                    if (orientedEdgeRefs.length === 0) continue;

                    const edgeCurveId = resolveRef(orientedEdgeRefs[orientedEdgeRefs.length - 1]);
                    addAdjacentFace(edgeCurveId, faceId);
                }
            }
        }

        return topology;
    }

    /**
     * OCCT root nesnesinden renkleri çıkarır
     * @param root OCCT result root nesnesi
     * @returns Face renkleri array'i
     */
    private extractColorsFromRoot(root: any): number[][] {
        const colors: number[][] = [];

        // Root.children'ı tara
        if (root.children && Array.isArray(root.children)) {
            for (let i = 0; i < root.children.length; i++) {
                const child = root.children[i];

                if (child.color && Array.isArray(child.color) && child.color.length >= 3) {
                    colors.push(child.color);
                }
            }
        }

        // Root'un kendisinde renk varsa
        if (root.color && Array.isArray(root.color) && root.color.length >= 3) {
            colors.push(root.color);
        }

        // Diğer olası renk alanlarını kontrol et
        const possibleColorKeys = ['material', 'appearance', 'visual', 'display', 'render'];
        for (const key of possibleColorKeys) {
            if (root[key] && root[key].color) {
                if (Array.isArray(root[key].color) && root[key].color.length >= 3) {
                    colors.push(root[key].color);
                }
            }
        }

        return colors;
    }

    /**
     * HSL renk formatını RGB'ye çevirir
     * @param h Hue (0-360)
     * @param s Saturation (0-1)
     * @param l Lightness (0-1)
     * @returns [r, g, b] (0-1 arası)
     */
    private hslToRgb(h: number, s: number, l: number): number[] {
        h = h / 360;
        let r, g, b;

        if (s === 0) {
            r = g = b = l; // achromatic
        } else {
            const hue2rgb = (p: number, q: number, t: number) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1 / 6) return p + (q - p) * 6 * t;
                if (t < 1 / 2) return q;
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                return p;
            };

            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }

        return [r, g, b];
    }

    /**
     * OCCT-WASM kütüphanesini yükler (lazy loading)
     * Browser'da CDN'den, Node.js'te npm paketinden yükler
     */
    private async initOCCT(): Promise<void> {
        if (this.occtWasm) {
            return; // Zaten yüklü
        }

        try {
            // Browser ortamında CDN'den yükle
            if (typeof window !== 'undefined') {
                // CDN'den occt-import-js yükle
                const cdnUrl = 'https://cdn.jsdelivr.net/npm/occt-import-js@0.0.23/dist/occt-import-js.js';

                // Script zaten yüklü mü kontrol et
                if (!(window as any).occtimportjs) {
                    await new Promise<void>((resolve, reject) => {
                        const script = document.createElement('script');
                        script.src = cdnUrl;
                        script.async = true;
                        script.onload = () => {
                            resolve();
                        };
                        script.onerror = (e) => {
                            reject(new Error('OCCT CDN script yüklenemedi'));
                        };
                        document.head.appendChild(script);
                    });
                }

                // Global fonksiyonu çağır
                const occtimportjs = (window as any).occtimportjs;
                if (!occtimportjs) {
                    throw new Error('occtimportjs global fonksiyonu bulunamadı');
                }

                // WASM dosyalarının URL'sini ayarla
                const wasmUrl = 'https://cdn.jsdelivr.net/npm/occt-import-js@0.0.23/dist/';
                this.occtWasm = await occtimportjs({
                    locateFile: (fileName: string) => {
                        return wasmUrl + fileName;
                    }
                });
            } else {
                // Node.js ortamı desteklenmiyor - sadece browser
                throw new Error('OCCT yalnızca browser ortamında destekleniyor');
            }
        } catch (error) {
            throw new Error('OCCT-WASM yüklenemedi. Lütfen internet bağlantınızı kontrol edin veya npm install occt-import-js komutuyla yükleyin.');
        }
    }

    /**
     * Worker'dan gelen mesh verilerini FikoCizimYalin'e dönüştür
     * @param meshes Mesh listesi
     * @param processedResult İşlenmiş sonuç
     * @param stepString STEP dosya içeriği (BREP edge'ler için)
     * @param stepTopology Parse edilmiş topoloji bilgisi
     */
    private convertMeshesToFikoCizim(meshes: any[], processedResult?: any, stepString?: string, stepTopology?: StepTopologyInfo): FikoCizimYalin[] {
        const cizimler: FikoCizimYalin[] = [];

        // Cache: extractColorsFromRoot'ı bir kez çağır
        const cachedColorsFromRoot = processedResult?.root ? this.extractColorsFromRoot(processedResult.root) : [];

        meshes.forEach((mesh: any, i: number) => {
            let geometry = new THREE.BufferGeometry();

            // Attributes'ı al (Worker'dan gelen veri yapısı)
            const attributes = mesh.attributes || {};

            // Position attribute'ünü ayarla
            if (attributes.position) {
                const posArray = new Float32Array(attributes.position, 0, attributes.positionLength);

                // NaN değerleri temizle
                for (let idx = 0; idx < posArray.length; idx++) {
                    if (isNaN(posArray[idx])) {
                        posArray[idx] = 0;
                    }
                }

                geometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
            }

            // Index'i ayarla
            if (mesh.index) {
                const indexArray = new Uint32Array(mesh.index, 0, mesh.indexLength);
                geometry.setIndex(new THREE.BufferAttribute(indexArray, 1));
            }

            // Normals'ları ayarla
            if (attributes.normal) {
                const normalArray = new Float32Array(attributes.normal, 0, attributes.normalLength);
                geometry.setAttribute('normal', new THREE.BufferAttribute(normalArray, 3));
            } else {
                geometry.computeVertexNormals();
            }

            // UV'yi ayarla
            if (attributes.uv) {
                const uvArray = new Float32Array(attributes.uv, 0, attributes.uvLength);
                geometry.setAttribute('uv', new THREE.BufferAttribute(uvArray, 2));
            }

            // ========================================
            // FikoSecililer Uyumluluğu İçin: toNonIndexed()
            // ========================================
            // FikoSecililer.buildGeometryFromGroup position[strt:strt+syi] aralığını
            // doğrudan kullanıyor. Indexed geometry'de bu çalışmaz çünkü üçgenler
            // index buffer aracılığıyla tanımlanıyor.
            // Non-indexed geometry'de her üçgen 3 ardışık vertex olarak saklanır.
            // ========================================

            // Triangle → Face mapping oluştur (brep_faces'den)
            const triCount = geometry.getIndex()
                ? Math.floor(geometry.getIndex()!.count / 3)
                : Math.floor((geometry.attributes.position?.count || 0) / 3);

            // triangleFaceMap: her üçgen hangi face'e ait
            const triangleFaceMap = new Uint32Array(triCount);
            triangleFaceMap.fill(0); // Varsayılan face 0

            if (mesh.brep_faces && mesh.brep_faces.length > 0) {
                for (let f = 0; f < mesh.brep_faces.length; f++) {
                    const face = mesh.brep_faces[f];
                    const firstTri = Math.max(0, face.first || 0);
                    const lastTri = face.last !== undefined ? Math.min(face.last, triCount - 1) : triCount - 1;

                    for (let triIdx = firstTri; triIdx <= lastTri; triIdx++) {
                        if (triIdx < triCount) {
                            triangleFaceMap[triIdx] = f;
                        }
                    }
                }
            }

            // ⚡ PERFORMANCE: Optimize geometry for GPU cache before conversion
            // This must happen BEFORE toNonIndexed() since optimization requires an index buffer
            if (geometry.getIndex()) {
                try {
                    this.meshOptimizer.optimizeGeometry(geometry);
                } catch (err) {
                    console.warn('Mesh optimization failed, continuing with unoptimized geometry:', err);
                }
            }

            // Indexed geometry'yi non-indexed'e çevir
            if (geometry.getIndex()) {
                geometry = geometry.toNonIndexed();
            }

            // Non-indexed vertex sayısı (her üçgen için 3 vertex)
            const vertexCount = (geometry.attributes.position?.count || 0) as number;

            // OPTIMIZED: Renk ve grup attribute'ları oluştur
            const colors = new Float32Array(vertexCount * 3);
            const grup = new Float32Array(vertexCount * 3);
            const alpha = FikoImporterSTP.getDefaultAlphaArray(vertexCount); // Cached 1.0 array

            let meshR = 0.7, meshG = 0.7, meshB = 0.7; // Varsayılan açık gri

            // Mesh-level color varsa kullan
            if (mesh.color && mesh.color.length >= 3) {
                meshR = mesh.color[0];
                meshG = mesh.color[1];
                meshB = mesh.color[2];
            }

            // Face renkleri cache'le
            const faceColors: number[][] = [];
            if (mesh.brep_faces && mesh.brep_faces.length > 0) {
                for (let f = 0; f < mesh.brep_faces.length; f++) {
                    const face = mesh.brep_faces[f];
                    let r = meshR, g = meshG, b = meshB;

                    if (face.color && face.color.length >= 3) {
                        r = face.color[0];
                        g = face.color[1];
                        b = face.color[2];
                    } else if (f < cachedColorsFromRoot.length && cachedColorsFromRoot[f]) {
                        const rc = cachedColorsFromRoot[f];
                        r = rc[0];
                        g = rc[1];
                        b = rc[2];
                    }
                    faceColors.push([r, g, b]);
                }
            }
            if (faceColors.length === 0) {
                faceColors.push([meshR, meshG, meshB]); // Varsayılan renk
            }

            // Her face için: başlangıç triangle indeksi ve triangle sayısı
            // Non-indexed geometry'de: triangle i → vertex [i*3, i*3+1, i*3+2]
            const faceTriangleRanges: { start: number; count: number }[] = [];
            if (mesh.brep_faces && mesh.brep_faces.length > 0) {
                for (let f = 0; f < mesh.brep_faces.length; f++) {
                    const face = mesh.brep_faces[f];
                    const firstTri = Math.max(0, face.first || 0);
                    const lastTri = face.last !== undefined ? Math.min(face.last, triCount - 1) : triCount - 1;
                    const triangleCount = lastTri - firstTri + 1;

                    // Non-indexed geometry'de vertex indeksleri
                    const startVertex = firstTri * 3;
                    const vertexCountForFace = triangleCount * 3;

                    faceTriangleRanges.push({ start: startVertex, count: vertexCountForFace });
                }
            }
            if (faceTriangleRanges.length === 0) {
                // Tüm mesh tek face
                faceTriangleRanges.push({ start: 0, count: vertexCount });
            }

            // Her üçgen için renk ve grup ata
            const actualTriCount = Math.floor(vertexCount / 3);
            for (let triIdx = 0; triIdx < actualTriCount; triIdx++) {
                const faceId = triIdx < triangleFaceMap.length ? triangleFaceMap[triIdx] : 0;
                const faceColor = faceColors[faceId] || faceColors[0];
                const faceRange = faceTriangleRanges[faceId] || faceTriangleRanges[0];

                // Bu üçgenin 3 vertex'i
                for (let v = 0; v < 3; v++) {
                    const vIdx = triIdx * 3 + v;
                    const cIdx = vIdx * 3;
                    const gIdx = vIdx * 3;

                    // Renk
                    colors[cIdx] = faceColor[0];
                    colors[cIdx + 1] = faceColor[1];
                    colors[cIdx + 2] = faceColor[2];

                    // Alpha (already filled with 1.0 from cached array - no assignment needed)

                    // Grup: [face_start_vertex, face_vertex_count, faceId]
                    // WRL formatı ile uyumlu: tüm face vertex'leri aynı start/count değerine sahip
                    grup[gIdx] = faceRange.start;
                    grup[gIdx + 1] = faceRange.count;
                    grup[gIdx + 2] = faceId;
                }
            }

            geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
            geometry.setAttribute('alpha', new THREE.Float32BufferAttribute(alpha, 1));
            geometry.setAttribute('grup', new THREE.Float32BufferAttribute(grup, 3));

            // Edge'leri al: BREP varsa kullan, yoksa mesh'ten çıkar
            // İlk mesh için BREP edge'leri kullan (tüm edge'ler ortak), diğerleri için boş geometry
            let cizgiler: THREE.BufferGeometry = new THREE.BufferGeometry();
            // if (i === 0 && brepEdges && brepEdges.attributes.position?.count > 0) {
            //     cizgiler = brepEdges;
            // } else if (i > 0 && brepEdges && brepEdges.attributes.position?.count > 0) {
            //     // Diğer mesh'ler için boş edge geometry (BREP edge'ler zaten ilk mesh'te)
            //     cizgiler = new THREE.BufferGeometry();
            // } else {
                // BREP yoksa mesh'ten sharp edge çıkar
                // cizgiler = this.extractEdgesFromMesh(geometry);
            // }

            // FikoCizimYalin oluştur
            const cizim = new FikoCizimYalin(
                this.sahip.sahne,
                mesh.name || `mesh_${i}`,
                geometry,
                cizgiler,
                new THREE.BufferGeometry()
            );

            // brep_faces'i metadata olarak sakla (seçim için)
            if (mesh.brep_faces && mesh.brep_faces.length > 0) {
                (cizim as any).brep_faces = mesh.brep_faces;
            }

            // Topoloji bilgisini metadata olarak sakla
            if (stepTopology) {
                (cizim as any).stepTopology = stepTopology;
            }

            cizimler.push(cizim);
        });

        return cizimler;
    }

    /**
     * Geometrileri organize et (hiyerarşik yapı oluştur)
     */
    private async organizeGeometry(
        cizimler: FikoCizimYalin[],
        dosya: File,
        result: any,
        gruplama: 'yok' | 'hiyerarşik'
    ): Promise<FikoGrup> {
        const baslik = dosya.name.replace('.stp', '').replace('.step', '');
        const grup = new FikoGrup(this.sahip.sahne, baslik);

        // STP dosyası için özel simge
        grup.userData['FikoArayuz'].simge = "./simgeler/stp.png";
        grup.sagMenu = [
            {
                simge: null,
                baslik: "Gizle / Göster",
                tiklandi: () => {
                    grup.visible = !grup.visible;
                    grup.children.forEach(c => c.visible = grup.visible);
                    this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                    this.sahip.sahne.anaGosterim.ciz();
                }
            },
            {
                simge: "trash",
                baslik: "'" + grup.name + "' Sil",
                tiklandi: () => {
                    grup.parent.remove(grup);
                    this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                    this.sahip.sahne.anaGosterim.ciz();
                }
            }
        ];

        if (gruplama === 'hiyerarşik') {
            // Alt grupları oluştur
            const grupMap: { [key: string]: FikoGrup } = {};
            grupMap['root'] = grup;

            const altGrupOlustur = (isim: string) => {
                if (grupMap[isim]) return grupMap[isim];

                const altGrup = new FikoGrup(this.sahip.sahne, isim);
                altGrup.userData['FikoArayuz'].simge = "./simgeler/kupler.png";

                altGrup.sagMenu = [
                    {
                        simge: null,
                        baslik: "Gizle / Göster",
                        tiklandi: () => {
                            altGrup.visible = !altGrup.visible;
                            altGrup.children.forEach(c => c.visible = altGrup.visible);
                            this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                            this.sahip.sahne.anaGosterim.ciz();
                        }
                    },
                    {
                        simge: "trash",
                        baslik: "Sil",
                        tiklandi: () => {
                            altGrup.parent.remove(altGrup);
                            this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                            this.sahip.sahne.anaGosterim.ciz();
                        }
                    }
                ];

                grupMap[isim] = altGrup;
                grup.ekle(altGrup);
                return altGrup;
            };

            // OCCT hiyerarşisinden grupları oluştur
            if (result.root && result.root.children && Array.isArray(result.root.children)) {
                for (const child of result.root.children) {
                    if (child.name) {
                        altGrupOlustur(child.name);
                    }
                }
            }

            if (result.compounds && Array.isArray(result.compounds)) {
                for (const compound of result.compounds) {
                    if (compound.name) {
                        altGrupOlustur(compound.name);
                    }
                }
            }

            // Mesh'leri grupla
            const groupNames = Object.keys(grupMap).filter(k => k !== 'root');

            for (let i = 0; i < cizimler.length; i++) {
                let meshIsim = cizimler[i].name;
                let hedefGrup: FikoGrup | null = null;

                // En uzun prefix eşleşmesini bul
                let bestMatch: string | null = null;
                let bestMatchLen = 0;

                for (const grupAdi of groupNames) {
                    if (meshIsim === grupAdi) {
                        bestMatch = grupAdi;
                        bestMatchLen = grupAdi.length;
                        break;
                    }
                    if (meshIsim.startsWith(grupAdi + '_') || meshIsim.startsWith(grupAdi)) {
                        if (grupAdi.length > bestMatchLen) {
                            bestMatch = grupAdi;
                            bestMatchLen = grupAdi.length;
                        }
                    }
                }

                if (bestMatch) {
                    hedefGrup = grupMap[bestMatch];
                } else {
                    const parts = meshIsim.split('_');
                    if (parts.length > 0) {
                        const meshPrefix = parts[0];
                        if (meshPrefix && meshPrefix !== 'Part' && meshPrefix !== 'Mesh') {
                            hedefGrup = altGrupOlustur(meshPrefix);
                        }
                    }
                }

                if (!hedefGrup) {
                    hedefGrup = grup;
                }

                hedefGrup.ekle(cizimler[i]);
            }
        } else {
            // Gruplama yok - direkt ana gruba ekle
            for (let i = 0; i < cizimler.length; i++) {
                grup.ekle(cizimler[i]);
            }
        }

        return grup;
    }

    /**
     * STP dosyasını OCCT-WASM ile doğrudan tarayıcıda okur (renklerle birlikte) - LEGACY
     * @param dosya STP/STEP dosyası
     * @param gruplama 'yok' = ayrı mesh'ler, 'hiyerarşik' = FikoGrup içinde gruplu
     * @returns Promise<FikoCizimYalin | FikoGrup | FikoCizimYalin[]>
     */
    public async stpDosyasiniOCCTIleYukle(dosya: File, gruplama: 'yok' | 'hiyerarşik' = 'yok'): Promise<FikoCizimYalin | FikoGrup | FikoCizimYalin[]> {
        // Reset cancellation flag at the start of each import
        this._importCancelled = false;

        try {
                this.onStreamingProgress?.({ phase: 'parsing', percent: 5, meshesLoaded: 0, totalMeshes: 0 });

                if (this.onProgress) {
                    this.onProgress('Dosya okunuyor...', 20);
                }

                // Dosyayı ArrayBuffer olarak oku (hash + OCCT için her iki yolda da gerekli)
                let arrayBuffer: ArrayBuffer | null = await dosya.arrayBuffer();
                let uint8Array: Uint8Array | null = new Uint8Array(arrayBuffer);

                // OCCT parametreleri - tessellationKalitesi'nden oku
                const params = {
                    linearUnit: 'millimeter',
                    linearDeflection: this.tessellationKalitesi.linearDeflection,
                    angularDeflection: this.tessellationKalitesi.angularDeflection,
                    include_faces: true,  // Face renk bilgilerini almak için
                    read_colors: true    // Renkleri okumak için
                };

                // Cross-session tesselation cache: aynı dosya + aynı kalite daha önce açıldıysa OCCT'yi atla
                const cacheAnahtar = this.geometriCacheAktif
                    ? await FikoStepCache.anahtarUret(arrayBuffer, params.linearDeflection, params.angularDeflection)
                    : null;

                let result: any = this.geometriCacheAktif ? await FikoStepCache.al(cacheAnahtar) : null;

                if (result) {
                    // CACHE HIT: tesselation hazır, OCCT init/ReadStepFile atlanır
                    this.onProgress?.('Önbellekten yükleniyor...', 60);
                    this.onStreamingProgress?.({ phase: 'parsing', percent: 30, meshesLoaded: 0, totalMeshes: 0 });
                } else {
                    // CACHE MISS: normal OCCT yolu
                    this.onProgress?.('OCCT kütüphanesi başlatılıyor...', 30);
                    await this.initOCCT();

                    console.log(`[FikoImporterSTP] Tessellation kalitesi: linear=${params.linearDeflection}, angular=${params.angularDeflection}`);

                    this.onProgress?.('Geometri çözümleniyor...', 60);
                    this.onStreamingProgress?.({ phase: 'parsing', percent: 30, meshesLoaded: 0, totalMeshes: 0 });
                    result = this.occtWasm.ReadStepFile(uint8Array, params);

                    // Check result immediately before any further processing
                    if (!result || !result.success) {
                        throw new Error('STEP dosyası okunamadı: ' + (result?.error || 'Bilinmeyen hata'));
                    }

                    // Tesselation'ı cache'e yaz (fire-and-forget, hataları yutar)
                    if (cacheAnahtar) FikoStepCache.koy(cacheAnahtar, result);
                }

                if (!result.meshes || result.meshes.length === 0) {
                    throw new Error('STEP dosyasında geometri bulunamadı');
                }

                // 🔶 STEP string'i decode et ve gerçek BREP topoloji parse et
                // Bu sayede edge'ler OpenCascade.js ile aynı şekilde gruplandırılır
                const stepString = new TextDecoder('utf-8').decode(uint8Array);

                // OCCT-tabanlı CAM için ham STEP byte'larını sakla (parça grubuna iliştirilecek)
                const occtStepBytes: Uint8Array | null = uint8Array;

                // arrayBuffer'ı bırak (byte verisi occtStepBytes view'inde yaşamaya devam eder)
                arrayBuffer = null;

                this.onProgress?.('BREP topoloji çözümleniyor...', 65);
                const stepTopology = await this.parseStepTopologyAsync(stepString);

                // Renk bilgilerini OCCT result'undan oluştur
                const faceColorMap: { [faceId: string]: number[] } = {};

                // OCCT result'undan brep_faces ve renk bilgisini çıkar
                for (let meshIdx = 0; meshIdx < result.meshes.length; meshIdx++) {
                    const mesh = result.meshes[meshIdx];
                    if (mesh.brep_faces && mesh.brep_faces.length > 0) {
                        for (let fIdx = 0; fIdx < mesh.brep_faces.length; fIdx++) {
                            const faceId = `mesh_${meshIdx}_face_${fIdx}`;
                            const fColor = mesh.brep_faces[fIdx]?.color || mesh.color || [0.7, 0.7, 0.7];
                            faceColorMap[faceId] = Array.isArray(fColor) ? [fColor[0] || 0.7, fColor[1] || 0.7, fColor[2] || 0.7] : [0.7, 0.7, 0.7];
                        }
                    }
                }

                this.onProgress?.('3D model oluşturuluyor...', 80);

                // Her mesh için ayrı FikoCizim oluştur (merge etmez)
                const cizimler: FikoCizimYalin[] = [];
                const meshByIndex: { [key: number]: FikoCizimYalin } = {};

                // UI blokajını önlemek için mesh'leri async chunk'lar halinde işle
                const totalMeshes = result.meshes.length;
                const MESH_CHUNK_SIZE = Math.max(1, this.progressiveBatchSize); // Progressive batch size

                this.onStreamingProgress?.({ phase: 'geometry', percent: 40, meshesLoaded: 0, totalMeshes });

                // Pre-create the group for progressive rendering (meshes are added in batches)
                const baslik = dosya.name.replace('.stp', '').replace('.step', '');
                const grup = new FikoGrup(this.sahip.sahne, baslik);
                grup.userData['FikoArayuz'].simge = "./simgeler/stp.png";
                if (occtStepBytes) grup.userData['__stepBytes'] = occtStepBytes; // OCCT BREP CAM için
                grup.sagMenu = [
                    {
                        simge: null,
                        baslik: "Gizle / Göster",
                        tiklandi: () => {
                            grup.visible = !grup.visible;
                            grup.children.forEach(c => c.visible = grup.visible);
                            this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                            this.sahip.sahne.anaGosterim.ciz();
                        }
                    },
                    {
                        simge: "expand",
                        baslik: "Patlat / Topla",
                        tiklandi: () => {
                            if (grup.patlamaFaktoru > 0) grup.patlamayiTopla();
                            else grup.patlat(1);
                        }
                    },
                    {
                        simge: "trash",
                        baslik: "'" + baslik + "' Sil",
                        tiklandi: () => {
                            grup.parent.remove(grup);
                            this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                            this.sahip.sahne.anaGosterim.ciz();
                        }
                    }
                ];

                // Cache: extractColorsFromRoot once per file (not per mesh)
                const cachedColorsFromRoot = result.root ? this.extractColorsFromRoot(result.root) : [];

                for (let i = 0; i < totalMeshes; i++) {
                    // Cancel check at the top of each iteration
                    if (this._importCancelled) {
                        console.warn(`[FikoImporterSTP] Import cancelled at mesh ${i}/${totalMeshes}`);
                        break;
                    }

                    const mesh = result.meshes[i];
                    let geometry = new THREE.BufferGeometry();

                    // Vertices
                    const positions = new Float32Array(mesh.attributes.position.array);

                    // NaN değerleri kontrol et ve temizle
                    let hasNaN = false;
                    for (let idx = 0; idx < positions.length; idx++) {
                        if (isNaN(positions[idx])) {
                            positions[idx] = 0; // NaN değerleri 0 ile değiştir
                            hasNaN = true;
                        }
                    }

                    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

                    // Indices - scope'u genişlet
                    let indices: Uint32Array | null = null;
                    if (mesh.index && mesh.index.array && mesh.index.array.length > 0) {
                        indices = new Uint32Array(mesh.index.array);
                        geometry.setIndex(new THREE.BufferAttribute(indices, 1));
                    }

                    // Normals
                    if (mesh.attributes.normal && mesh.attributes.normal.array) {
                        const normals = new Float32Array(mesh.attributes.normal.array);
                        geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
                    } else {
                        geometry.computeVertexNormals();
                    }

                    // ========================================
                    // FikoSecililer Uyumluluğu: toNonIndexed()
                    // ========================================
                    // Triangle → Face mapping oluştur (brep_faces'den)
                    const triCount = indices
                        ? Math.floor(indices.length / 3)
                        : Math.floor(positions.length / 9);

                    const triangleFaceMap = new Uint32Array(triCount);
                    triangleFaceMap.fill(0);

                    if (mesh.brep_faces && mesh.brep_faces.length > 0) {
                        for (let f = 0; f < mesh.brep_faces.length; f++) {
                            const face = mesh.brep_faces[f];
                            const firstTri = Math.max(0, face.first || 0);
                            const lastTri = face.last !== undefined ? Math.min(face.last, triCount - 1) : triCount - 1;

                            for (let triIdx = firstTri; triIdx <= lastTri; triIdx++) {
                                if (triIdx < triCount) {
                                    triangleFaceMap[triIdx] = f;
                                }
                            }
                        }
                    }

                    // Indexed → Non-indexed dönüşüm
                    if (geometry.getIndex()) {
                        geometry = geometry.toNonIndexed();
                    }

                    // Non-indexed vertex sayısı
                    const vertexCount = geometry.attributes.position?.count || 0;

                    // Varsayılan renk
                    let meshR = 0.7, meshG = 0.7, meshB = 0.7;
                    if (mesh.color && mesh.color.length >= 3) {
                        meshR = mesh.color[0];
                        meshG = mesh.color[1];
                        meshB = mesh.color[2];
                    }

                    // Face renkleri cache'le (cachedColorsFromRoot hoisted above loop)
                    const faceColors: number[][] = [];
                    if (mesh.brep_faces && mesh.brep_faces.length > 0) {
                        for (let f = 0; f < mesh.brep_faces.length; f++) {
                            const face = mesh.brep_faces[f];
                            let faceR = meshR, faceG = meshG, faceB = meshB;

                            if (face.color && face.color.length >= 3) {
                                faceR = face.color[0];
                                faceG = face.color[1];
                                faceB = face.color[2];
                            } else if (Object.keys(faceColorMap).length > 0) {
                                // WASM optimized: faceColorMap'ten doğrudan oku
                                const faceId = `mesh_${i}_face_${f}`;
                                if (faceColorMap[faceId]) {
                                    const stepColor = faceColorMap[faceId];
                                    faceR = stepColor[0];
                                    faceG = stepColor[1];
                                    faceB = stepColor[2];
                                }
                            } else if (f < cachedColorsFromRoot.length && cachedColorsFromRoot[f]) {
                                faceR = cachedColorsFromRoot[f][0];
                                faceG = cachedColorsFromRoot[f][1];
                                faceB = cachedColorsFromRoot[f][2];
                            }
                            faceColors.push([faceR, faceG, faceB]);
                        }
                    }
                    if (faceColors.length === 0) {
                        faceColors.push([meshR, meshG, meshB]);
                    }

                    // Face triangle ranges (non-indexed vertex indeksleri)
                    const faceTriangleRanges: { start: number; count: number }[] = [];
                    if (mesh.brep_faces && mesh.brep_faces.length > 0) {
                        for (let f = 0; f < mesh.brep_faces.length; f++) {
                            const face = mesh.brep_faces[f];
                            const firstTri = Math.max(0, face.first || 0);
                            const lastTri = face.last !== undefined ? Math.min(face.last, triCount - 1) : triCount - 1;
                            const triangleCount = lastTri - firstTri + 1;

                            const startVertex = firstTri * 3;
                            const vertexCountForFace = triangleCount * 3;
                            faceTriangleRanges.push({ start: startVertex, count: vertexCountForFace });
                        }
                    }
                    if (faceTriangleRanges.length === 0) {
                        faceTriangleRanges.push({ start: 0, count: vertexCount });
                    }

                    // OPTIMIZED: Attribute array'leri oluştur
                    const colors = new Float32Array(vertexCount * 3);
                    const alphas = FikoImporterSTP.getDefaultAlphaArray(vertexCount); // Cached 1.0 array
                    const groups = new Float32Array(vertexCount * 3);

                    // Her üçgen için renk ve grup ata
                    const actualTriCount = Math.floor(vertexCount / 3);
                    for (let triIdx = 0; triIdx < actualTriCount; triIdx++) {
                        const faceId = triIdx < triangleFaceMap.length ? triangleFaceMap[triIdx] : 0;
                        const faceColor = faceColors[faceId] || faceColors[0];
                        const faceRange = faceTriangleRanges[faceId] || faceTriangleRanges[0];

                        for (let v = 0; v < 3; v++) {
                            const vIdx = triIdx * 3 + v;
                            const cIdx = vIdx * 3;

                            colors[cIdx] = faceColor[0];
                            colors[cIdx + 1] = faceColor[1];
                            colors[cIdx + 2] = faceColor[2];

                            // Alpha (already filled with 1.0 from cached array - no assignment needed)

                            groups[cIdx] = faceRange.start;
                            groups[cIdx + 1] = faceRange.count;
                            groups[cIdx + 2] = faceId;
                        }
                    }

                    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
                    geometry.setAttribute('alpha', new THREE.Float32BufferAttribute(alphas, 1));
                    geometry.setAttribute('grup', new THREE.Float32BufferAttribute(groups, 3));

                    // WASM optimized: Topoloji bilgisi WASM tarafından zaten yapılıyor
                    // addTopologyToFaces() çağrısı kaldırıldı - stepString parse'i atlandı
                    // this.addTopologyToFaces(geometry, mesh.brep_faces || [], stepTopology, stepString);

                    // occt_faceGroups oluştur (surfaceType ile) - geometrik analiz
                    // NOT: Edge topoloji için önce face grupları ve radyüs grupları hazır olmalı
                    const occtFaceGroups = this.createOcctFaceGroupsFromBrepFaces(
                        geometry,
                        mesh.brep_faces || [],
                        actualTriCount
                    );

                    // 🔶 Radyüs grupları oluştur (OpenCascade stili)
                    const radiusGroups = this.buildRadiusGroupsFromFaceGroups(occtFaceGroups);

                    // Edge'leri geometry'den çıkar (async - UI blokajı önlenir)
                    // 🔶 stepTopology geçiriliyor - gerçek BREP edge bilgisi kullanılacak
                    // extractGroupBoundaryEdgesAsync içinde addTopologyToEdges ve regroupEdgesByRadius
                    // otomatik çağrılıyor (OpenCascade.js ile aynı)
                    const hasGroupAttribute = !!geometry.getAttribute('grup');
                    const meshCizgiler = hasGroupAttribute
                        ? await this.extractGroupBoundaryEdgesAsync(geometry, stepTopology)
                        : await this.extractEdgesFromMeshSyncAsync(geometry, this.seciliEdgeThresholdAngle);

                    // Her mesh için ayrı FikoCizim oluştur
                    const cizim = new FikoCizimYalin(
                        this.sahip.sahne,
                        `${dosya.name.replace('.stp', '').replace('.step', '')}_mesh${i}`,
                        geometry,
                        meshCizgiler,
                        new THREE.BufferGeometry()
                    );

                    // brep_faces metadata'sını sakla (seçim için)
                    if (mesh.brep_faces && mesh.brep_faces.length > 0) {
                        (cizim as any).brep_faces = mesh.brep_faces;
                    }

                    // Face gruplarını metadata olarak sakla
                    (cizim as any).occt_faceGroups = occtFaceGroups;
                    (cizim as any).occt_radiusGroups = radiusGroups;

                    // Debug: Radyüs gruplarını logla
                    // if (radiusGroups.size > 0) {
                    //     console.log(`📐 ${cizim.name}: ${radiusGroups.size} farklı radyüs grubu bulundu`);
                    //     this.logRadiusGroups(radiusGroups);
                    // }

                    // Topoloji bilgisini metadata olarak sakla
                    (cizim as any).stepTopology = stepTopology;

                    // Import kaynağı - hacim debug için
                    cizim.userData['importSource'] = 'occt-import-js';

                    cizimler.push(cizim);
                    meshByIndex[i] = cizim;

                    // Progressive rendering: yield to main thread and render batch
                    if ((i + 1) % MESH_CHUNK_SIZE === 0 || i === totalMeshes - 1) {
                        const meshesLoaded = i + 1;
                        const percent = 40 + Math.floor((meshesLoaded / totalMeshes) * 50);
                        this.onProgress?.(`Mesh ${meshesLoaded}/${totalMeshes} işleniyor...`, percent);
                        this.onStreamingProgress?.({ phase: 'geometry', percent, meshesLoaded, totalMeshes });

                        // Add the latest batch to the group for progressive display
                        const batchStart = meshesLoaded - (meshesLoaded % MESH_CHUNK_SIZE || MESH_CHUNK_SIZE);
                        const batchMeshes: FikoCizimYalin[] = [];
                        for (let b = Math.max(0, batchStart); b <= i; b++) {
                            if (cizimler[b] && !cizimler[b].parent) {
                                grup.ekle(cizimler[b]);
                                batchMeshes.push(cizimler[b]);
                            }
                        }

                        // Notify consumer so they can render / fit camera
                        if (batchMeshes.length > 0) {
                            this.onBatchRendered?.(batchMeshes, cizimler);
                        }

                        // Yield to main thread so the browser can paint
                        if (i < totalMeshes - 1) {
                            await new Promise<void>(r => setTimeout(r, 0));
                        }

                        // Check cancellation after yield
                        if (this._importCancelled) {
                            console.warn(`[FikoImporterSTP] Import cancelled at mesh ${meshesLoaded}/${totalMeshes}`);
                            this.onProgress?.(`Import iptal edildi (${meshesLoaded}/${totalMeshes})`, percent);
                            this.onStreamingProgress?.({ phase: 'geometry', percent, meshesLoaded, totalMeshes });
                            break;
                        }
                    }
                }

                // Assembly phase: fix names and organize hierarchy
                this.onStreamingProgress?.({ phase: 'assembly', percent: 92, meshesLoaded: cizimler.length, totalMeshes });

                // Mesh isimleri düzelt
                for (let i = 0; i < cizimler.length; i++) {
                    let meshIsim = result.meshes[i]?.name || `Part_${i}`;
                    cizimler[i].name = meshIsim;
                }

                if (gruplama === 'hiyerarşik') {
                    // Hiyerarşik yapı oluştur - result'tan compound yapısını al
                    const grupMap: { [key: string]: FikoGrup } = {};
                    grupMap['root'] = grup;

                    // Helper function: Alt grup oluştur ve sağ menüyü ekle
                    const altGrupOlustur = (isim: string) => {
                        if (grupMap[isim]) return grupMap[isim];

                        const altGrup = new FikoGrup(this.sahip.sahne, isim);
                        altGrup.userData['FikoArayuz'].simge = "./simgeler/kupler.png";

                        // Sağ menü - göster/gizle, sil
                        altGrup.sagMenu = [
                            {
                                simge: null,
                                baslik: "Gizle / Göster",
                                tiklandi: () => {
                                    altGrup.visible = !altGrup.visible;
                                    altGrup.children.forEach(c => c.visible = altGrup.visible);
                                    this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                    this.sahip.sahne.anaGosterim.ciz();
                                }
                            },
                            {
                                simge: "expand",
                                baslik: "Patlat / Topla",
                                tiklandi: () => {
                                    if (altGrup.patlamaFaktoru > 0) altGrup.patlamayiTopla();
                                    else altGrup.patlat(1);
                                }
                            },
                            {
                                simge: "trash",
                                baslik: "Sil",
                                tiklandi: () => {
                                    altGrup.parent.remove(altGrup);
                                    this.sahip.sahne.anaGosterim.FikoYT.degisti.emit();
                                    this.sahip.sahne.anaGosterim.ciz();
                                }
                            }
                        ];

                        grupMap[isim] = altGrup;
                        grup.ekle(altGrup);
                        return altGrup;
                    };

                    // result.root varsa, root'un children'larından gruplar oluştur
                    if (result.root && result.root.children && Array.isArray(result.root.children)) {
                        for (const child of result.root.children) {
                            if (child.name) {
                                altGrupOlustur(child.name);
                            }
                        }
                    }

                    // result.compounds varsa da alt grupları oluştur
                    if (result.compounds && Array.isArray(result.compounds)) {
                        for (const compound of result.compounds) {
                            if (compound.name) {
                                altGrupOlustur(compound.name);
                            }
                        }
                    }

                    // Mesh'leri isimlerine göre grupla (re-parent from flat grup into sub-groups)
                    const groupNames = Object.keys(grupMap).filter(k => k !== 'root');

                    for (let i = 0; i < cizimler.length; i++) {
                        let meshIsim = cizimler[i].name;
                        let hedefGrup: FikoGrup | null = null;

                        // 1. Tam eşleşme ya da en uzun prefix eşleşmesini bul
                        let bestMatch: string | null = null;
                        let bestMatchLen = 0;

                        for (const grupAdi of groupNames) {
                            // Tam eşleşme
                            if (meshIsim === grupAdi) {
                                bestMatch = grupAdi;
                                bestMatchLen = grupAdi.length;
                                break;
                            }
                            // Prefix eşleşmesi (örn: "Body_Face1" -> "Body")
                            if (meshIsim.startsWith(grupAdi + '_') || meshIsim.startsWith(grupAdi)) {
                                if (grupAdi.length > bestMatchLen) {
                                    bestMatch = grupAdi;
                                    bestMatchLen = grupAdi.length;
                                }
                            }
                        }

                        if (bestMatch) {
                            hedefGrup = grupMap[bestMatch];
                        } else {
                            // 2. Mesh isminden grup adı çıkarmaya çalış
                            const parts = meshIsim.split('_');
                            if (parts.length > 0) {
                                const meshPrefix = parts[0];
                                if (meshPrefix && meshPrefix !== 'Part' && meshPrefix !== 'Mesh') {
                                    hedefGrup = altGrupOlustur(meshPrefix);
                                }
                            }
                        }

                        // 3. Eğer hala grup yoksa ana grupta kalsın (zaten progressive eklenmişti)
                        if (hedefGrup && hedefGrup !== grup) {
                            hedefGrup.ekle(cizimler[i]); // Re-parent from flat grup into sub-group
                        }
                    }

                    // TODO: BatchedMesh optimization disabled - matrixWorld double-transform issue
                    // this.batchedMeshManager.batchAssembly(grup as any, grup as any);
                } else {
                    // Gruplama yok - mesh'ler zaten progressive loop'ta gruba eklendi
                    // Ensure any remaining meshes that weren't added (shouldn't happen) are included
                    for (let i = 0; i < cizimler.length; i++) {
                        if (!cizimler[i].parent) {
                            grup.ekle(cizimler[i]);
                        }
                    }
                    // TODO: BatchedMesh optimization disabled - matrixWorld double-transform issue
                    // this.batchedMeshManager.batchAssembly(grup as any, grup as any);
                }

                this.onStreamingProgress?.({ phase: 'assembly', percent: 100, meshesLoaded: cizimler.length, totalMeshes });
                return grup;

            } catch (error) {
                throw error;
            }
    }

    /**
     * Create and initialize the STP-OCCT worker for off-main-thread STEP parsing.
     * Falls back to main-thread parsing if worker creation fails.
     */
    private createStpOcctWorker(): Worker | null {
        if (this.stpOcctWorker) return this.stpOcctWorker;
        try {
            this.stpOcctWorker = new Worker(
                new URL('./stp-occt.worker.ts', import.meta.url),
                { type: 'module' }
            );
            return this.stpOcctWorker;
        } catch (e) {
            console.warn('[FikoImporterSTP] STP-OCCT worker creation failed, will use main thread:', e);
            return null;
        }
    }

    /**
     * Parse STEP file using off-main-thread worker.
     * Returns raw mesh data from worker without blocking main thread.
     * @param buffer ArrayBuffer of the STEP file
     */
    private parseStepWithWorker(buffer: ArrayBuffer): Promise<{ meshes: any[], totalMeshes: number, elapsed: number }> {
        return new Promise((resolve, reject) => {
            const worker = this.createStpOcctWorker();
            if (!worker) {
                reject(new Error('Worker not available'));
                return;
            }

            const allMeshes: any[] = [];
            let metadata: any = null;

            worker.onmessage = (event: MessageEvent) => {
                const data = event.data;
                switch (data.type) {
                    case 'metadata':
                        metadata = data;
                        this.onProgress?.(`${data.totalMeshes} parça bulundu, işleniyor...`, 40);
                        break;
                    case 'meshChunk':
                        allMeshes.push(...data.meshes);
                        this.onProgress?.(
                            `Mesh ${allMeshes.length}/${metadata?.totalMeshes || '?'} alındı...`,
                            40 + Math.floor((data.progress || 0) * 30)
                        );
                        break;
                    case 'complete':
                        this.terminateStpOcctWorker();
                        resolve({
                            meshes: allMeshes,
                            totalMeshes: data.totalMeshes,
                            elapsed: data.elapsed
                        });
                        break;
                    case 'error':
                        this.terminateStpOcctWorker();
                        reject(new Error(data.error));
                        break;
                }
            };

            worker.onerror = (error) => {
                this.terminateStpOcctWorker();
                reject(new Error(`Worker error: ${error.message}`));
            };

            // Send file buffer to worker (Transferable for zero-copy)
            // Tessellation kalite parametrelerini worker'a ilet
            worker.postMessage(
                {
                    type: 'parseStep',
                    buffer,
                    chunkSize: 5,
                    linearDeflection: this.tessellationKalitesi.linearDeflection,
                    angularDeflection: this.tessellationKalitesi.angularDeflection,
                },
                [buffer]
            );
        });
    }

    /**
     * Terminate the STP-OCCT worker to free resources.
     */
    terminateStpOcctWorker(): void {
        if (this.stpOcctWorker) {
            try { this.stpOcctWorker.postMessage({ type: 'terminate' }); } catch (_) { /* ignore */ }
            this.stpOcctWorker.terminate();
            this.stpOcctWorker = null;
        }
    }

    /**
     * STP dosyasını yükler (OCCT veya FreeCAD API seçimi ile)
     * @param dosya STP/STEP dosyası
     * @param gruplama 'yok' = ayrı mesh'ler, 'hiyerarşik' = FikoGrup içinde gruplu
     * @returns Promise<FikoCizimYalin | FikoGrup | FikoCizimYalin[]>
     */
    public async yukle(
        dosya: File,
        gruplama: 'yok' | 'hiyerarşik' = 'yok',
        opts: Partial<STPImportOptions> = {}
    ): Promise<FikoCizimYalin | FikoGrup | FikoCizimYalin[]> {
        if (dosya.name.toLowerCase().indexOf(".stp") > -1 || dosya.name.toLowerCase().indexOf(".step") > -1) {
            this.seciliEdgeThresholdAngle = opts.edgeThresholdAngle ?? 30;
            const sonuc = await this.stpDosyasiniOCCTIleYukle(dosya, gruplama);
            if (sonuc) {
                if (Array.isArray(sonuc)) {
                    sonuc.forEach(s => this.uygulaImportAyarlari(s, opts));
                } else {
                    this.uygulaImportAyarlari(sonuc, opts);
                }
            }
            return sonuc;
        } else {
            throw new Error('Desteklenmeyen dosya formatı. Sadece .stp ve .step dosyaları desteklenir.');
        }
    }

    /**
     * Geometrileri custom attribute'larla birlikte birleştirir
     * @param geometries Birleştirilecek geometriler
     * @returns Birleştirilmiş geometry (tüm custom attribute'larla)
     */
    private mergeGeometriesWithAttributes(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry {
        if (geometries.length === 0) {
            return new THREE.BufferGeometry();
        }

        if (geometries.length === 1) {
            return geometries[0].clone();
        }

        // Optimized sync merge using TypedArray bulk operations
        let totalVertices = 0;
        for (const geom of geometries) {
            totalVertices += geom.attributes.position?.count || 0;
        }

        // STP Merge completed

        // Pre-allocate all buffers
        const merged = new THREE.BufferGeometry();
        const positions = new Float32Array(totalVertices * 3);
        const normals = new Float32Array(totalVertices * 3);
        const allColors = new Float32Array(totalVertices * 3);
        const allOrjinalRenkler = new Float32Array(totalVertices * 3);
        const allAlphas = new Float32Array(totalVertices);
        const allGroups = new Float32Array(totalVertices * 3);

        let offset = 0;
        for (const geom of geometries) {
            const pos = geom.attributes.position;
            if (!pos) continue;
            const count = pos.count;

            // Bulk copy with TypedArray.set()
            positions.set(pos.array as Float32Array, offset * 3);

            if (geom.attributes.normal) {
                normals.set(geom.attributes.normal.array as Float32Array, offset * 3);
            }
            if (geom.attributes.color) {
                allColors.set(geom.attributes.color.array as Float32Array, offset * 3);
            }
            if (geom.attributes.orjinalRenkler) {
                allOrjinalRenkler.set(geom.attributes.orjinalRenkler.array as Float32Array, offset * 3);
            }
            if (geom.attributes.alpha) {
                allAlphas.set(geom.attributes.alpha.array as Float32Array, offset);
            }
            if (geom.attributes.grup) {
                allGroups.set(geom.attributes.grup.array as Float32Array, offset * 3);
            }

            offset += count;
        }

        // Set all attributes at once
        merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
        merged.setAttribute('color', new THREE.BufferAttribute(allColors, 3));
        merged.setAttribute('orjinalRenkler', new THREE.BufferAttribute(allOrjinalRenkler, 3));
        merged.setAttribute('alpha', new THREE.BufferAttribute(allAlphas, 1));
        merged.setAttribute('grup', new THREE.BufferAttribute(allGroups, 3));

        merged.computeBoundingBox();
        merged.computeBoundingSphere();

        return merged;
    }

    /**
     * Aynı koordinatlardaki duplicate yüzeyleri/faceleri temizler (vertex'leri birleştirmez)
     * @param geometries Yüzey geometrileri array'i
     * @returns Duplicate'leri temizlenmiş geometriler
     */
    private removeDuplicateFaces(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry[] {
        const filteredGeometries: THREE.BufferGeometry[] = [];
        const faceMap = new Map<string, boolean>();

        const getBoundingBoxKey = (geometry: THREE.BufferGeometry): string => {
            if (!geometry.attributes['position']) {
                return 'empty';
            }

            geometry.computeBoundingBox();
            const box = geometry.boundingBox!;

            const tolerance = 1e-3;
            const min = [
                Math.round(box.min.x / tolerance) * tolerance,
                Math.round(box.min.y / tolerance) * tolerance,
                Math.round(box.min.z / tolerance) * tolerance
            ];
            const max = [
                Math.round(box.max.x / tolerance) * tolerance,
                Math.round(box.max.y / tolerance) * tolerance,
                Math.round(box.max.z / tolerance) * tolerance
            ];

            return `${min.join(',')}_${max.join(',')}`;
        };

        geometries.forEach((geometry) => {
            const boundingBoxKey = getBoundingBoxKey(geometry);

            if (!faceMap.has(boundingBoxKey)) {
                faceMap.set(boundingBoxKey, true);
                filteredGeometries.push(geometry);
            }
        });

        return filteredGeometries;
    }

    /**
     * Aynı koordinatlardaki duplicate çizgileri/kenarları temizler
     * @param geometries Çizgi geometrileri array'i
     * @returns Duplicate'leri temizlenmiş geometriler
     */
    private removeDuplicateLines(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry[] {
        const filteredGeometries: THREE.BufferGeometry[] = [];
        const lineMap = new Map<string, boolean>();

        const getLineKey = (geometry: THREE.BufferGeometry): string => {
            if (!geometry.attributes['position']) {
                return 'empty';
            }

            geometry.computeBoundingBox();
            const box = geometry.boundingBox!;
            const tolerance = 1e-3;

            const min = [
                Math.round(box.min.x / tolerance) * tolerance,
                Math.round(box.min.y / tolerance) * tolerance,
                Math.round(box.min.z / tolerance) * tolerance
            ];
            const max = [
                Math.round(box.max.x / tolerance) * tolerance,
                Math.round(box.max.y / tolerance) * tolerance,
                Math.round(box.max.z / tolerance) * tolerance
            ];

            return `${min.join(',')}_${max.join(',')}`;
        };

        geometries.forEach((geometry) => {
            const lineKey = getLineKey(geometry);

            if (!lineMap.has(lineKey)) {
                lineMap.set(lineKey, true);
                filteredGeometries.push(geometry);
            }
        });

        return filteredGeometries;
    }

    /**
     * Aynı koordinatlardaki duplicate vertex/noktaları temizler
     * @param geometries Vertex geometrileri array'i
     * @returns Duplicate'leri temizlenmiş geometriler
     */
    private removeDuplicateVertices(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry[] {
        const filteredGeometries: THREE.BufferGeometry[] = [];
        const vertexMap = new Map<string, boolean>();

        const getVertexKey = (geometry: THREE.BufferGeometry): string => {
            if (!geometry.attributes['position']) {
                return 'empty';
            }

            const positions = geometry.attributes['position'];
            const tolerance = 1e-3;

            // İlk vertex'in koordinatlarını al (tek nokta geometrileri için)
            const x = Math.round(positions.getX(0) / tolerance) * tolerance;
            const y = Math.round(positions.getY(0) / tolerance) * tolerance;
            const z = Math.round(positions.getZ(0) / tolerance) * tolerance;

            return `${x},${y},${z}`;
        };

        geometries.forEach((geometry) => {
            const vertexKey = getVertexKey(geometry);

            if (!vertexMap.has(vertexKey)) {
                vertexMap.set(vertexKey, true);
                filteredGeometries.push(geometry);
            }
        });

        return filteredGeometries;
    }

    /**
     * BREP edge'ler için transform hesapla ve uygula
     */
    private calculateAndApplyTransform(brepEdges: THREE.BufferGeometry, meshes: any[], stepString: string): void {
        if (!brepEdges || !brepEdges.attributes.position || meshes.length === 0) {
            return;
        }

        // Mesh bounding box'ını hesapla
        let meshMinX = Infinity, meshMinY = Infinity, meshMinZ = Infinity;
        let meshMaxX = -Infinity, meshMaxY = -Infinity, meshMaxZ = -Infinity;

        for (const mesh of meshes) {
            const meshPositions = mesh.attributes?.position;
            const meshPosLength = mesh.attributes?.positionLength;

            if (meshPositions && meshPosLength > 0) {
                const meshPosArray = new Float32Array(meshPositions, 0, meshPosLength);

                for (let idx = 0; idx < meshPosArray.length; idx += 3) {
                    const x = meshPosArray[idx], y = meshPosArray[idx + 1], z = meshPosArray[idx + 2];
                    if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
                        meshMinX = Math.min(meshMinX, x); meshMaxX = Math.max(meshMaxX, x);
                        meshMinY = Math.min(meshMinY, y); meshMaxY = Math.max(meshMaxY, y);
                        meshMinZ = Math.min(meshMinZ, z); meshMaxZ = Math.max(meshMaxZ, z);
                    }
                }
            }
        }

        if (meshMinX === Infinity) return;

        const meshSize = new THREE.Vector3(meshMaxX - meshMinX, meshMaxY - meshMinY, meshMaxZ - meshMinZ);
        const meshCenter = new THREE.Vector3((meshMinX + meshMaxX) / 2, (meshMinY + meshMaxY) / 2, (meshMinZ + meshMaxZ) / 2);

        // BREP edge bounding box
        brepEdges.computeBoundingBox();
        const brepBox = brepEdges.boundingBox!;
        const brepSize = new THREE.Vector3();
        brepBox.getSize(brepSize);
        const brepCenter = new THREE.Vector3();
        brepBox.getCenter(brepCenter);

        // Transform'u hesapla ve uygula
        const positions = brepEdges.attributes.position as THREE.BufferAttribute;
        const posArray = positions.array as Float32Array;

        // Basit yaklaşım: Merkezi eşleştir ve ölçekle
        const scale = Math.max(meshSize.x, meshSize.y, meshSize.z) / Math.max(brepSize.x, brepSize.y, brepSize.z);

        for (let idx = 0; idx < posArray.length; idx += 3) {
            // Önce BREP'i merkeze taşı
            posArray[idx] -= brepCenter.x;
            posArray[idx + 1] -= brepCenter.y;
            posArray[idx + 2] -= brepCenter.z;

            // Ölçekle
            posArray[idx] *= scale;
            posArray[idx + 1] *= scale;
            posArray[idx + 2] *= scale;

            // Mesh merkezine taşı
            posArray[idx] += meshCenter.x;
            posArray[idx + 1] += meshCenter.y;
            posArray[idx + 2] += meshCenter.z;
        }

        positions.needsUpdate = true;
        brepEdges.computeBoundingBox();
    }

    // =====================================================================
    // OpenCascade.js ile Bağımsız Import Sistemi
    // =====================================================================

    /** OpenCascade Bridge instance */
    private ocBridge: FikoOpenCascadeBridge | null = null;

    /**
     * OpenCascade.js CAF/XDE renk desteği var mı?
     */
    public async hasOpenCascadeColorSupport(): Promise<boolean> {
        try {
            const bridge = getOpenCascadeBridge();
            await bridge.init();
            return bridge.hasColorSupport();
        } catch (e) {
            return false;
        }
    }

    /**
     * OpenCascade.js Bridge'i al veya oluştur
     */
    private async getOrCreateBridge(): Promise<FikoOpenCascadeBridge> {
        if (!this.ocBridge) {
            this.ocBridge = getOpenCascadeBridge();
            await this.ocBridge.init();
        }
        return this.ocBridge;
    }

    /**
     * OpenCascade.js ile STP dosyasını import et
     *
     * Bu metod eski sistemden (occt-import-js) BAĞIMSIZDIR.
     * Doğrudan OpenCascade.js kullanarak mesh, renk ve topoloji alır.
     *
     * @param dosya STP/STEP dosyası
     * @param options Import seçenekleri
     * @returns Promise<FikoCizimYalin | FikoGrup | FikoCizimYalin[]>
     */
    public async importWithOpenCascade(
        dosya: File,
        options: Partial<STPImportOptions> = {}
    ): Promise<FikoGrup> {
        const defaultOptions: STPImportOptions = {
            importMethod: 'opencascade-js',
            linearDeflection: 0.1,
            angularDeflection: 0.5, // radyan
            relativeMode: false,
            gruplama: 'yok',
            useColors: true
        };

        const opts = { ...defaultOptions, ...options };
        this.seciliEdgeThresholdAngle = opts.edgeThresholdAngle ?? 30;

        // Progress callback
        this.onProgress?.('OpenCascade.js başlatılıyor...', 0);

        try {
            // Bridge al
            const bridge = await this.getOrCreateBridge();

            // Dosya içeriğini oku - ArrayBuffer olarak tut (daha verimli)
            this.onProgress?.('Dosya okunuyor...', 10);
            const arrayBuffer = await dosya.arrayBuffer();

            // Tessellation parametreleri
            const tessParams: TessellationParams = {
                linearDeflection: opts.linearDeflection,
                angularDeflection: opts.angularDeflection,
                relativeMode: opts.relativeMode,
                readParams: opts.readParams  // OpenCascade Interface_Static parametreleri
            };

            // Parse et - ArrayBuffer direkt geçir (worker Uint8Array'e dönüştürür)
            this.onProgress?.('STEP parse ediliyor...', 20);
            const result = await bridge.parseSTEPWithColors(arrayBuffer, tessParams);

            if (!result.success) {
                throw new Error('STEP parse hatası: ' + result.error);
            }

            // Mesh'leri FikoCizimYalin'e dönüştür
            this.onProgress?.('Geometriler oluşturuluyor...', 50);
            const cizimler = await this.convertOCCTMeshesToFikoCizim(result, opts, dosya.name);

            this.onProgress?.('Import tamamlandı', 100);

            // occt-import-js gibi HER ZAMAN grup oluştur (dosya ismiyle)
            // Bu sayede tüm solid'ler tek bir grup altında toplanır
            const dosyaIsmi = dosya.name.replace(/\.(stp|step)$/i, '');
            const grup = new FikoGrup(this.sahip?.sahne, dosyaIsmi);

            // STP dosyası için özel simge (occt-import-js ile aynı)
            grup.userData['FikoArayuz'].simge = "./simgeler/stp.png";
            grup.sagMenu = [
                {
                    simge: null,
                    baslik: "Gizle / Göster",
                    tiklandi: () => {
                        grup.visible = !grup.visible;
                        grup.children.forEach(c => c.visible = grup.visible);
                        this.sahip?.sahne?.anaGosterim?.FikoYT?.degisti?.emit();
                        this.sahip?.sahne?.anaGosterim?.ciz();
                    }
                },
                {
                    simge: "trash",
                    baslik: "'" + grup.name + "' Sil",
                    tiklandi: () => {
                        grup.parent?.remove(grup);
                        this.sahip?.sahne?.anaGosterim?.FikoYT?.degisti?.emit();
                        this.sahip?.sahne?.anaGosterim?.ciz();
                    }
                }
            ];

            if (cizimler.length > 0) {
                cizimler.forEach(c => grup.ekle(c));
            }

            this.uygulaImportAyarlari(grup, opts);

            return grup;

        } catch (error) {
            throw error;
        }
    }

    /**
     * GPU-accelerated normal computation
     * Büyük mesh'ler için WebGPU compute shader kullanarak 50-100x hızlanma
     * @param cizimler FikoCizimYalin listesi
     */
    private async applyGPUNormalCompute(cizimler: FikoCizimYalin[]): Promise<void> {
        const gpuPipeline = getGPUImportPipeline();
        let gpuCount = 0;
        let cpuCount = 0;

        for (const cizim of cizimler) {
            // FikoCizimYalin mesh geometry'sine meshGeom üzerinden erişim
            const geometry = cizim.meshGeom;
            if (!geometry) continue;

            // GPU compute gerekli mi kontrol et
            const needsGPU = (geometry as any)._needsGPUNormalCompute;
            if (!needsGPU) continue;

            // Triangle sayısı kontrolü - küçük mesh'ler için CPU yeterli
            const vertexCount = geometry.attributes.position?.count || 0;
            const triangleCount = Math.floor(vertexCount / 3);

            if (triangleCount >= 1000) {
                // Büyük mesh - GPU compute dene
                try {
                    const success = await gpuPipeline.gpuComputeVertexNormals(geometry);
                    if (success) {
                        gpuCount++;
                    } else {
                        cpuCount++;
                    }
                } catch (e) {
                    // GPU başarısız, CPU fallback zaten yapılmış
                    cpuCount++;
                }
            } else {
                cpuCount++;
            }

            // Flag'i temizle
            delete (geometry as any)._needsGPUNormalCompute;
        }
    }

    /**
     * OCCT mesh verilerini FikoCizimYalin'e dönüştür
     * Worker'dan gelen her mesh bir Solid/Shell'i temsil eder
     */
    private async convertOCCTMeshesToFikoCizim(
        result: OCCTColoredResult,
        options: STPImportOptions,
        dosyaIsmi: string
    ): Promise<FikoCizimYalin[]> {
        const cizimler: FikoCizimYalin[] = [];

        if (result.meshes.length === 0) {
            return cizimler;
        }

        const totalMeshes = result.meshes.length;
        const BATCH = Math.max(1, this.progressiveBatchSize);

        // Her mesh bir Solid veya Shell'i temsil eder
        for (let i = 0; i < totalMeshes; i++) {
            // Cancellation check
            if (this._importCancelled) {
                console.warn(`[FikoImporterSTP] OCCT import cancelled at solid ${i + 1}/${totalMeshes}`);
                break;
            }

            const meshData = result.meshes[i];

            try {
                const cizim = this.convertSolidMeshToFikoCizim(meshData, i, dosyaIsmi, options);
                if (cizim) {
                    cizimler.push(cizim);
                }
            } catch (e) {
                // Dönüştürme hatası - devam et
            }

            // Yield and report progress every batch
            if ((i + 1) % BATCH === 0 || i === totalMeshes - 1) {
                await this.yieldToMain();
                const percent = 50 + Math.floor(((i + 1) / totalMeshes) * 40);
                this.onProgress?.(`Solid ${i + 1}/${totalMeshes}`, percent);
                this.onStreamingProgress?.({ phase: 'geometry', percent, meshesLoaded: cizimler.length, totalMeshes });
                this.onBatchRendered?.(cizimler.slice(-BATCH), cizimler);
            }
        }

        // GPU Normal Compute - WebGPU ile hızlı normal hesaplama
        await this.applyGPUNormalCompute(cizimler);

        return cizimler;
    }

    /**
     * Solid/Shell mesh verisini FikoCizimYalin'e dönüştür
     * Worker'dan gelen meshData artık tüm face'leri içeren birleştirilmiş veri
     * FACE BAZLI RENK DESTEĞİ: meshData.colors per-vertex renk içeriyorsa kullanılır
     */
    private convertSolidMeshToFikoCizim(
        meshData: any, // solidIndex, vertices, normals, colors (per-vertex), faceGroups, edgeVertices, edgeGroups, color, hasFaceColors
        meshIndex: number,
        dosyaIsmi: string,
        options: STPImportOptions
    ): FikoCizimYalin | null {
        const { vertices, normals, colors: meshColors, faceGroups, edgeVertices, edgeGroups, color, solidIndex, nativeVolume, nativeCenterOfMass } = meshData;

        if (!vertices || vertices.length === 0) {
            return null;
        }

        // BufferGeometry oluştur
        const geometry = new THREE.BufferGeometry();

        // Vertices zaten non-indexed olarak geliyor (worker'da expand edildi)
        const vertexArray = vertices instanceof Float32Array ? vertices : new Float32Array(vertices);
        geometry.setAttribute('position', new THREE.BufferAttribute(vertexArray, 3));

        // Normals - GPU compute için geometriyi işaretle
        let needsGPUNormalCompute = false;
        if (normals && normals.length > 0) {
            const normalArray = normals instanceof Float32Array ? normals : new Float32Array(normals);
            geometry.setAttribute('normal', new THREE.BufferAttribute(normalArray, 3));
        } else {
            // GPU compute sonra yapılacak - şimdilik CPU fallback
            geometry.computeVertexNormals();
            needsGPUNormalCompute = true;
        }

        // GPU normal compute'u daha sonra batch olarak yapılacak
        (geometry as any)._needsGPUNormalCompute = needsGPUNormalCompute;

        const vertexCount = geometry.attributes['position'].count;

        // Renk attribute'u - Worker'dan gelen per-vertex renkleri öncelikle kullan
        let colors: Float32Array;
        let orjinalRenkler: Float32Array;

        if (options.useColors && meshColors && meshColors.length === vertexCount * 3) {
            // Worker'dan gelen face bazlı per-vertex renkler
            colors = meshColors instanceof Float32Array ? meshColors : new Float32Array(meshColors);
            orjinalRenkler = new Float32Array(colors);
        } else {
            // Fallback: Solid seviyesi tek renk
            colors = new Float32Array(vertexCount * 3);
            orjinalRenkler = new Float32Array(vertexCount * 3);

            let r = 0.7, g = 0.7, b = 0.7; // Varsayılan gri
            if (options.useColors && color) {
                r = color.r;
                g = color.g;
                b = color.b;
            }

            for (let i = 0; i < vertexCount; i++) {
                colors[i * 3] = r;
                colors[i * 3 + 1] = g;
                colors[i * 3 + 2] = b;
                orjinalRenkler[i * 3] = r;
                orjinalRenkler[i * 3 + 1] = g;
                orjinalRenkler[i * 3 + 2] = b;
            }
        }

        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        geometry.setAttribute('orjinalRenkler', new THREE.BufferAttribute(orjinalRenkler, 3));

        // Alpha attribute'u
        const alphas = new Float32Array(vertexCount);
        alphas.fill(1.0);
        geometry.setAttribute('alpha', new THREE.BufferAttribute(alphas, 1));

        // Grup attribute'u (per-face selection için)
        // faceGroups bilgisi varsa kullan, yoksa triangle bazlı oluştur
        const grupAttr = new Float32Array(vertexCount * 3);

        if (faceGroups && faceGroups.length > 0) {
            // Worker'dan gelen face grup bilgisini kullan
            for (const faceGroup of faceGroups) {
                const { faceIndex, startVertex, vertexCount: faceVertexCount } = faceGroup;
                const grupId = (solidIndex ?? meshIndex) * 100000 + faceIndex;

                for (let v = 0; v < faceVertexCount; v++) {
                    const idx = (startVertex + v) * 3;
                    grupAttr[idx] = startVertex;          // vertex_start_index
                    grupAttr[idx + 1] = faceVertexCount;  // vertex_count
                    grupAttr[idx + 2] = grupId;           // group_id
                }
            }
        } else {
            // Fallback: her triangle ayrı grup
            const triangleCount = Math.floor(vertexCount / 3);
            for (let t = 0; t < triangleCount; t++) {
                const startIdx = t * 3;
                const grupId = (solidIndex ?? meshIndex) * 100000 + t;

                for (let v = 0; v < 3; v++) {
                    const idx = (startIdx + v) * 3;
                    grupAttr[idx] = startIdx;      // vertex_start_index
                    grupAttr[idx + 1] = 3;         // vertex_count (triangle)
                    grupAttr[idx + 2] = grupId;    // group_id
                }
            }
        }
        geometry.setAttribute('grup', new THREE.BufferAttribute(grupAttr, 3));

        // 🔶 BREP Surface Type attribute'ları (OpenCASCADE'den)
        // surfaceType: 0=UNKNOWN, 1=PLANE, 2=CYLINDER, 3=SPHERE, 4=CONE, 5=TORUS, 6=BEZIER, 7=BSPLINE, 8=REVOLUTION, 9=EXTRUSION, 10=OFFSET, 11=OTHER
        // surfaceRadius: Silindir/Küre/Koni için radius
        // surfaceAxis: Silindir/Koni için eksen yönü (x,y,z)
        // surfaceCenter: Silindir/Küre için merkez noktası (x,y,z)
        if (faceGroups && faceGroups.length > 0 && faceGroups[0]?.surfaceType !== undefined) {
            const surfaceTypeMap: { [key: string]: number } = {
                'UNKNOWN': 0, 'PLANE': 1, 'CYLINDER': 2, 'SPHERE': 3, 'CONE': 4,
                'TORUS': 5, 'BEZIER': 6, 'BSPLINE': 7, 'REVOLUTION': 8, 'EXTRUSION': 9,
                'OFFSET': 10, 'OTHER': 11
            };

            const surfaceTypes = new Float32Array(vertexCount);
            const surfaceRadii = new Float32Array(vertexCount);
            const surfaceAxes = new Float32Array(vertexCount * 3);
            const surfaceCenters = new Float32Array(vertexCount * 3);

            for (const faceGroup of faceGroups) {
                const { startVertex, vertexCount: faceVertexCount, surfaceType, radius, axis, center, majorRadius, minorRadius, semiAngle, normal, origin } = faceGroup;

                const typeCode = surfaceTypeMap[surfaceType] ?? 0;
                const faceRadius = radius ?? majorRadius ?? 0;

                // Axis: silindir/koni için axis, düzlem için normal
                const axisX = axis?.x ?? normal?.x ?? 0;
                const axisY = axis?.y ?? normal?.y ?? 0;
                const axisZ = axis?.z ?? normal?.z ?? 1;

                // Center: silindir/küre için center, düzlem için origin
                const centerX = center?.x ?? origin?.x ?? 0;
                const centerY = center?.y ?? origin?.y ?? 0;
                const centerZ = center?.z ?? origin?.z ?? 0;

                for (let v = 0; v < faceVertexCount; v++) {
                    const vIdx = startVertex + v;
                    surfaceTypes[vIdx] = typeCode;
                    surfaceRadii[vIdx] = faceRadius;

                    const idx3 = vIdx * 3;
                    surfaceAxes[idx3] = axisX;
                    surfaceAxes[idx3 + 1] = axisY;
                    surfaceAxes[idx3 + 2] = axisZ;

                    surfaceCenters[idx3] = centerX;
                    surfaceCenters[idx3 + 1] = centerY;
                    surfaceCenters[idx3 + 2] = centerZ;
                }
            }

            geometry.setAttribute('surfaceType', new THREE.BufferAttribute(surfaceTypes, 1));
            geometry.setAttribute('surfaceRadius', new THREE.BufferAttribute(surfaceRadii, 1));
            geometry.setAttribute('surfaceAxis', new THREE.BufferAttribute(surfaceAxes, 3));
            geometry.setAttribute('surfaceCenter', new THREE.BufferAttribute(surfaceCenters, 3));
        }

        // Bounding hesapla
        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();

        // Edge geometrisi - OpenCascade'den gelen BREP edge'lerini kullan
        let edgeGeometry: THREE.BufferGeometry;

        if (edgeVertices && edgeVertices.length > 0) {
            // Worker'dan gelen gerçek BREP edge verileri
            edgeGeometry = new THREE.BufferGeometry();
            const edgeArray = edgeVertices instanceof Float32Array
                ? edgeVertices
                : new Float32Array(edgeVertices);
            edgeGeometry.setAttribute('position', new THREE.BufferAttribute(edgeArray, 3));

            const edgeVertexCount = edgeArray.length / 3;

            // Edge'ler için siyah renk attribute'u
            const edgeColors = new Float32Array(edgeVertexCount * 3);
            for (let i = 0; i < edgeVertexCount; i++) {
                edgeColors[i * 3] = 0;     // R = 0 (siyah)
                edgeColors[i * 3 + 1] = 0; // G = 0
                edgeColors[i * 3 + 2] = 0; // B = 0
            }
            edgeGeometry.setAttribute('color', new THREE.BufferAttribute(edgeColors, 3));

            // Edge'ler için grup attribute'u (per-edge selection)
            // WRL formatı ile uyumlu: [vertex_start_index, vertex_count, group_id]
            // BREP edge bazlı gruplama - aynı edge'e ait tüm segmentler aynı grup ID'sine sahip
            const edgeGrupAttr = new Float32Array(edgeVertexCount * 3);

            if (edgeGroups && edgeGroups.length > 0) {
                // Worker'dan gelen BREP edge grup bilgisini kullan
                for (const edgeGroup of edgeGroups) {
                    const { edgeIndex, startVertex, vertexCount: edgeVCount } = edgeGroup;
                    const grupId = (solidIndex ?? meshIndex) * 100000 + 50000 + edgeIndex;

                    // Bu BREP edge'e ait tüm vertex'lere aynı grup bilgisini yaz
                    for (let v = 0; v < edgeVCount; v++) {
                        const idx = (startVertex + v) * 3;
                        edgeGrupAttr[idx] = startVertex;      // vertex_start_index (edge başlangıcı)
                        edgeGrupAttr[idx + 1] = edgeVCount;   // vertex_count (tüm edge)
                        edgeGrupAttr[idx + 2] = grupId;       // group_id (BREP edge ID)
                    }
                }
            } else {
                // Fallback: her 2 vertex ayrı segment (eski davranış)
                const segmentCount = Math.floor(edgeVertexCount / 2);
                for (let s = 0; s < segmentCount; s++) {
                    const startIdx = s * 2;
                    const edgeId = (solidIndex ?? meshIndex) * 100000 + 50000 + s;

                    for (let v = 0; v < 2; v++) {
                        const idx = (startIdx + v) * 3;
                        edgeGrupAttr[idx] = startIdx;
                        edgeGrupAttr[idx + 1] = 2;
                        edgeGrupAttr[idx + 2] = edgeId;
                    }
                }
            }
            edgeGeometry.setAttribute('grup', new THREE.BufferAttribute(edgeGrupAttr, 3));
        } else {
            // Fallback: mesh'ten sharp edge çıkar
            edgeGeometry = this.extractEdgesFromMesh(geometry, this.seciliEdgeThresholdAngle);
        }

        // FikoCizimYalin oluştur - Solid bazlı isimlendirme
        const idx = solidIndex ?? meshIndex;
        const cizimIsim = `${dosyaIsmi}_solid${idx}`;
        const cizim = new FikoCizimYalin(
            this.sahip?.sahne,    // sahne
            cizimIsim,            // isim
            geometry,             // meshGeom
            edgeGeometry,         // edgesGeom
            undefined,            // vertexGeom
            false,                // kalinCizgiKullan
            2                     // cizgiKalinligi
        );

        // BREP metadata
        (cizim as any).occt_solidIndex = solidIndex ?? meshIndex;
        (cizim as any).occt_faceGroups = faceGroups;
        (cizim as any).occt_edgeGroups = edgeGroups; // Edge BREP bilgisi (curveType, radius, center, axis)
        (cizim as any).occt_color = color;

        // 🔶 Radyüs grupları oluştur (OpenCascade stili)
        let radiusGroups = this.buildRadiusGroupsFromFaceGroups(faceGroups);
        // Edge radyüs bilgilerini de ekle (fillet kenarları için)
        if (edgeGroups && edgeGroups.length > 0) {
            radiusGroups = this.addEdgeRadiusGroups(edgeGroups, radiusGroups);
        }
        (cizim as any).occt_radiusGroups = radiusGroups;

        // Debug: Radyüs gruplarını logla
        // if (radiusGroups.size > 0) {
        //     console.log(`📐 ${cizim.name}: ${radiusGroups.size} farklı radyüs grubu bulundu`);
        //     this.logRadiusGroups(radiusGroups);
        // }

        // Import kaynağı - hacim debug için
        cizim.userData['importSource'] = 'opencascade-js';

        // BRepGProp ile hesaplanan native hacim (mm³ cinsinden)
        // Mesh tabanlı hesaplamadan çok daha doğru!
        if (nativeVolume !== null && nativeVolume !== undefined) {
            cizim.userData['nativeVolume'] = nativeVolume; // mm³
            cizim.userData['nativeVolumeCm3'] = nativeVolume / 1000; // cm³
        }
        if (nativeCenterOfMass) {
            cizim.userData['nativeCenterOfMass'] = nativeCenterOfMass;
        }

        return cizim;
    }

    /**
     * STP dosyası için import seçeneklerini dialog ile göster
     * Bu metod FikoDialogService ile kullanılır
     *
     * @example
     * ```typescript
     * const importer = new FikoImporterSTP(sahne);
     * importer.showImportDialog(dosya, dialogService)
     *     .then(result => console.log('Import tamamlandı:', result));
     * ```
     */
    public async showImportDialog(
        dosya: File,
        dialogService: any
    ): Promise<FikoCizimYalin | FikoGrup | FikoCizimYalin[] | null> {
        // CAF desteğini kontrol et
        const hasColorSupport = await this.hasOpenCascadeColorSupport();

        return new Promise((resolve) => {
            // Dynamic import for dialog component
            import('./stp-import-dialog').then(({ StpImportDialogComponent }) => {
                const dialog = dialogService.yeniAc({
                    bilesen: StpImportDialogComponent,
                    parametreler: {
                        dosya: dosya,
                        hasColorSupport: hasColorSupport
                    },
                    ayarlar: {
                        baslik: 'STP Import Seçenekleri',
                        genislik: '380px',
                        yukseklik: 'auto'
                    }
                });

                // Dialog bileşeninin eventlerini dinle
                // Not: Component instance'a erişim için biraz bekle
                setTimeout(() => {
                    const componentEl = document.querySelector('fikoyt-stp-import-dialog');
                    if (componentEl) {
                        // importBasladi event'ini dinle
                        (componentEl as any).__ngContext__?.forEach((ctx: any) => {
                            if (ctx?.importBasladi) {
                                ctx.importBasladi.subscribe(async (options: STPImportOptions) => {
                                    try {
                                        let result;
                                        if (options.importMethod === 'opencascade-js') {
                                            result = await this.importWithOpenCascade(dosya, options);
                                        } else {
                                            result = await this.yukle(dosya, options.gruplama, options);
                                        }
                                        resolve(result);
                                    } catch (e) {
                                        resolve(null);
                                    }
                                });

                                ctx.kapandi.subscribe(() => {
                                    resolve(null);
                                });
                            }
                        });
                    }
                }, 100);
            });
        });
    }

    /**
     * Geometrik analiz ile yüzey tipini tespit eder
     * STEP string olmadan, sadece mesh geometry'sinden yüzey tipini belirler
     * @param geometry Mesh BufferGeometry
     * @param faceStartVertex Face'in başlangıç vertex indeksi
     * @param faceVertexCount Face'in vertex sayısı
     * @returns Yüzey tipi bilgisi: surfaceType, radius, center, axis
     */
    private detectSurfaceTypeFromGeometry(
        geometry: THREE.BufferGeometry,
        faceStartVertex: number,
        faceVertexCount: number
    ): { surfaceType: string; radius?: number; center?: { x: number; y: number; z: number }; axis?: { x: number; y: number; z: number } } {
        const posAttr = geometry.attributes['position'];
        const normalAttr = geometry.attributes['normal'];

        if (!posAttr || faceVertexCount < 3) {
            return { surfaceType: 'UNKNOWN' };
        }

        // Face'in vertex'lerini ve normal'lerini topla
        const positions: THREE.Vector3[] = [];
        const normals: THREE.Vector3[] = [];
        const endVertex = Math.min(faceStartVertex + faceVertexCount, posAttr.count);

        for (let i = faceStartVertex; i < endVertex; i++) {
            positions.push(new THREE.Vector3(
                posAttr.getX(i),
                posAttr.getY(i),
                posAttr.getZ(i)
            ));
            if (normalAttr) {
                normals.push(new THREE.Vector3(
                    normalAttr.getX(i),
                    normalAttr.getY(i),
                    normalAttr.getZ(i)
                ).normalize());
            }
        }

        if (positions.length < 3) {
            return { surfaceType: 'UNKNOWN' };
        }

        // === PLANE Tespiti ===
        // Tüm normal'ler aynı yönde mi kontrol et
        if (normals.length >= 3) {
            const refNormal = normals[0];
            let isPlane = true;
            const planeTolerance = 0.01; // ~0.5 derece

            for (let i = 1; i < normals.length && i < 50; i++) { // İlk 50 normal yeterli
                const dot = Math.abs(refNormal.dot(normals[i]));
                if (dot < 1 - planeTolerance) {
                    isPlane = false;
                    break;
                }
            }

            if (isPlane) {
                return {
                    surfaceType: 'PLANE',
                    axis: { x: refNormal.x, y: refNormal.y, z: refNormal.z }
                };
            }
        }

        // === SPHERE Tespiti ===
        // Tüm noktalar bir merkezden eşit uzaklıkta mı?
        if (positions.length >= 6) {
            // Merkez tahmini: tüm noktaların ortalaması
            const center = new THREE.Vector3();
            for (const p of positions) {
                center.add(p);
            }
            center.divideScalar(positions.length);

            // Radius: merkezden ilk noktaya mesafe
            const radius = center.distanceTo(positions[0]);

            if (radius > 0.001) { // Çok küçük radius'ları atla
                let isSphere = true;
                const sphereTolerance = radius * 0.02; // %2 tolerans

                for (let i = 1; i < positions.length && i < 50; i++) {
                    const dist = center.distanceTo(positions[i]);
                    if (Math.abs(dist - radius) > sphereTolerance) {
                        isSphere = false;
                        break;
                    }
                }

                if (isSphere) {
                    return {
                        surfaceType: 'SPHERE',
                        radius: radius,
                        center: { x: center.x, y: center.y, z: center.z }
                    };
                }
            }
        }

        // === CYLINDER Tespiti ===
        // Normal'ler bir eksene dik mi? Ve o eksenden eşit uzaklıkta mı?
        if (normals.length >= 6 && positions.length >= 6) {
            // Normal'lerin ortalamasından eksen tahmini
            // Silindir normal'leri eksene diktir, yani normal x eksen = 0
            // Cross product ile eksen yönü bul
            const n1 = normals[0];
            const n2 = normals[Math.min(normals.length - 1, Math.floor(normals.length / 2))];
            const axis = new THREE.Vector3().crossVectors(n1, n2).normalize();

            if (axis.length() > 0.1) { // Geçerli eksen
                // Tüm normal'ler bu eksene dik mi?
                let isCylinder = true;
                const axisTolerance = 0.05;

                for (let i = 0; i < normals.length && i < 30; i++) {
                    const dot = Math.abs(axis.dot(normals[i]));
                    if (dot > axisTolerance) {
                        isCylinder = false;
                        break;
                    }
                }

                if (isCylinder) {
                    // Eksenden uzaklıklar eşit mi? (radius)
                    // Bir noktanın eksene olan uzaklığını hesapla
                    const p0 = positions[0];
                    const center = new THREE.Vector3();
                    for (const p of positions) {
                        center.add(p);
                    }
                    center.divideScalar(positions.length);

                    // Eksene projeksiyon (⚡ PERFORMANS: Reuse buffers)
                    _stpTmpVec3C.subVectors(p0, center);
                    const projLen = _stpTmpVec3C.dot(axis);
                    _stpTmpVec3D.copy(axis).multiplyScalar(projLen);
                    const perpDist = _stpTmpVec3E.subVectors(_stpTmpVec3C, _stpTmpVec3D).length();

                    if (perpDist > 0.001) {
                        // Diğer noktaların da aynı mesafede olup olmadığını kontrol et
                        let cylinderValid = true;
                        const cylTolerance = perpDist * 0.02;

                        for (let i = 1; i < positions.length && i < 30; i++) {
                            // ⚡ PERFORMANS: Reuse buffers in loop (avoids ~30 allocations per cylinder test)
                            _stpTmpVec3C.subVectors(positions[i], center);
                            const pl = _stpTmpVec3C.dot(axis);
                            _stpTmpVec3D.copy(axis).multiplyScalar(pl);
                            const pd = _stpTmpVec3E.subVectors(_stpTmpVec3C, _stpTmpVec3D).length();

                            if (Math.abs(pd - perpDist) > cylTolerance) {
                                cylinderValid = false;
                                break;
                            }
                        }

                        if (cylinderValid) {
                            return {
                                surfaceType: 'CYLINDER',
                                radius: perpDist,
                                center: { x: center.x, y: center.y, z: center.z },
                                axis: { x: axis.x, y: axis.y, z: axis.z }
                            };
                        }
                    }
                }
            }
        }

        // === CONE Tespiti ===
        // Silindir gibi ama radius değişken
        if (normals.length >= 6 && positions.length >= 6) {
            // Normal'ler bir noktada kesişiyor mu? (koni tepe noktası)
            // Basit: z-eksenine göre radius değişiyor mu?
            let minZ = Infinity, maxZ = -Infinity;
            let minR = Infinity, maxR = -Infinity;
            const center = new THREE.Vector3();
            for (const p of positions) {
                center.add(p);
                if (p.z < minZ) minZ = p.z;
                if (p.z > maxZ) maxZ = p.z;
            }
            center.divideScalar(positions.length);

            // Her noktanın XY düzlemindeki mesafesini hesapla
            const radii: { z: number; r: number }[] = [];
            for (const p of positions) {
                const r = Math.sqrt((p.x - center.x) ** 2 + (p.y - center.y) ** 2);
                radii.push({ z: p.z, r });
                if (r < minR) minR = r;
                if (r > maxR) maxR = r;
            }

            // Z ile R arasında lineer ilişki var mı?
            if (maxZ - minZ > 0.1 && maxR - minR > 0.1) {
                // Basit lineer regresyon
                let sumZ = 0, sumR = 0, sumZR = 0, sumZZ = 0;
                for (const { z, r } of radii) {
                    sumZ += z;
                    sumR += r;
                    sumZR += z * r;
                    sumZZ += z * z;
                }
                const n = radii.length;
                const slope = (n * sumZR - sumZ * sumR) / (n * sumZZ - sumZ * sumZ);

                // Slope önemli ölçüde değişiyorsa koni
                if (Math.abs(slope) > 0.01) {
                    // R² hesapla
                    const meanR = sumR / n;
                    let ssRes = 0, ssTot = 0;
                    for (const { z, r } of radii) {
                        const predicted = meanR + slope * (z - sumZ / n);
                        ssRes += (r - predicted) ** 2;
                        ssTot += (r - meanR) ** 2;
                    }
                    const rSquared = ssTot > 0 ? 1 - ssRes / ssTot : 0;

                    if (rSquared > 0.9) { // İyi fit
                        return {
                            surfaceType: 'CONE',
                            radius: (minR + maxR) / 2,
                            center: { x: center.x, y: center.y, z: center.z },
                            axis: { x: 0, y: 0, z: 1 }
                        };
                    }
                }
            }
        }

        // === TORUS Tespiti ===
        // (Kompleks - basit analiz için atla)

        // Tanımlanamadı
        return { surfaceType: 'UNKNOWN' };
    }

    /**
     * occt-import-js için brep_faces'den occt_faceGroups oluşturur
     * Geometrik analiz ile surfaceType bilgisi ekler
     * @param geometry Non-indexed mesh geometry
     * @param brepFaces occt-import-js'den gelen brep_faces array
     * @param triCount Üçgen sayısı
     * @returns occt_faceGroups array'i (surfaceType ile)
     */
    private createOcctFaceGroupsFromBrepFaces(
        geometry: THREE.BufferGeometry,
        brepFaces: any[],
        triCount: number
    ): any[] {
        const faceGroups: any[] = [];

        if (!brepFaces || brepFaces.length === 0) {
            // Tek face varsay
            const surfaceInfo = this.detectSurfaceTypeFromGeometry(geometry, 0, geometry.attributes['position']?.count || 0);
            faceGroups.push({
                faceIndex: 0,
                startVertex: 0,
                vertexCount: geometry.attributes['position']?.count || 0,
                surfaceType: surfaceInfo.surfaceType,
                radius: surfaceInfo.radius,
                center: surfaceInfo.center,
                axis: surfaceInfo.axis
            });
            return faceGroups;
        }

        for (let f = 0; f < brepFaces.length; f++) {
            const face = brepFaces[f];
            const firstTri = Math.max(0, face.first || 0);
            const lastTri = face.last !== undefined ? Math.min(face.last, triCount - 1) : triCount - 1;
            const triangleCount = lastTri - firstTri + 1;

            // Non-indexed geometry için vertex indeksleri
            const startVertex = firstTri * 3;
            const vertexCount = triangleCount * 3;

            // Geometrik analiz ile yüzey tipini tespit et
            const surfaceInfo = this.detectSurfaceTypeFromGeometry(geometry, startVertex, vertexCount);

            faceGroups.push({
                faceIndex: f,
                startVertex: startVertex,
                vertexCount: vertexCount,
                surfaceType: surfaceInfo.surfaceType,
                radius: surfaceInfo.radius,
                center: surfaceInfo.center,
                axis: surfaceInfo.axis,
                color: face.color || null
            });
        }

        return faceGroups;
    }

    /**
     * 🔶 Edge geometry'ye topoloji bilgisi ekler (geometrik connectivity + eğrilik analizi)
     *
     * DOĞRU YAKLAŞIM:
     * 1. Geometrik olarak bağlı edge zincirleri bul (uç noktaları aynı olanlar)
     * 2. Her zincir için eğrilik analizi yap (düz mü, eğri mi?)
     * 3. Eğri zincirler (fillet/arc) aynı radiusGroup'a dahil edilir
     * 4. Eğri zincirin radius'unu geometrik olarak hesapla
     *
     * @param edgeGeometry Edge BufferGeometry
     * @param radiusGroups Face'lerden oluşturulan radyüs grupları (radius referansı için)
     * @returns Güncellenmiş edge geometry
     */
    public addTopologyToEdgesFromGeometry(
        edgeGeometry: THREE.BufferGeometry,
        radiusGroups: Map<number, RadiusGroupInfo>
    ): THREE.BufferGeometry {
        const positionAttr = edgeGeometry.getAttribute('position') as THREE.BufferAttribute | undefined;

        if (!positionAttr || positionAttr.count === 0) {
            return edgeGeometry;
        }

        const vertexCount = positionAttr.count;
        const edgeCount = Math.floor(vertexCount / 2);

        // Curve type: 0=UNKNOWN, 1=LINE, 2=CIRCLE
        const curveTypes = new Float32Array(vertexCount);
        curveTypes.fill(1); // Varsayılan: LINE
        // Radius: Edge radyüsü
        const radii = new Float32Array(vertexCount);
        // RadiusGroup: Bağlı eğri edge'lerin grup ID'si
        const radiusGroupIds = new Float32Array(vertexCount);
        radiusGroupIds.fill(-1);
        // EdgeId: Edge indeksi
        const edgeIds = new Float32Array(vertexCount);

        const groupAttr = edgeGeometry.getAttribute('grup') as THREE.BufferAttribute | undefined;

        // ========================================
        // PHASE 1: Edge bilgilerini çıkar ve connectivity graph oluştur
        // ========================================
        const PRECISION = 10000; // 0.1mm hassasiyet
        const vertexToEdges = new Map<string, number[]>();

        const hashVertex = (x: number, y: number, z: number): string => {
            return `${Math.round(x * PRECISION)},${Math.round(y * PRECISION)},${Math.round(z * PRECISION)}`;
        };

        interface EdgeData {
            p1: THREE.Vector3;
            p2: THREE.Vector3;
            p1Hash: string;
            p2Hash: string;
            direction: THREE.Vector3;
            length: number;
        }
        const edgeDataList: EdgeData[] = [];

        for (let edgeIdx = 0; edgeIdx < edgeCount; edgeIdx++) {
            const v1Idx = edgeIdx * 2;
            const v2Idx = edgeIdx * 2 + 1;

            // ⚡ PERFORMANS: Create p1, p2 (stored in EdgeData), but reuse buffer for direction
            const p1 = new THREE.Vector3(
                positionAttr.getX(v1Idx),
                positionAttr.getY(v1Idx),
                positionAttr.getZ(v1Idx)
            );
            const p2 = new THREE.Vector3(
                positionAttr.getX(v2Idx),
                positionAttr.getY(v2Idx),
                positionAttr.getZ(v2Idx)
            );

            const p1Hash = hashVertex(p1.x, p1.y, p1.z);
            const p2Hash = hashVertex(p2.x, p2.y, p2.z);

            // Vertex → Edge mapping
            if (!vertexToEdges.has(p1Hash)) vertexToEdges.set(p1Hash, []);
            if (!vertexToEdges.has(p2Hash)) vertexToEdges.set(p2Hash, []);
            vertexToEdges.get(p1Hash)!.push(edgeIdx);
            vertexToEdges.get(p2Hash)!.push(edgeIdx);

            // ⚡ PERFORMANS: Reuse buffer for direction calculation, then clone for storage
            const direction = _stpTmpVec3A.subVectors(p2, p1).normalize().clone();
            const length = p1.distanceTo(p2);

            edgeDataList.push({ p1, p2, p1Hash, p2Hash, direction, length });
        }

        // ========================================
        // PHASE 2: Bağlı edge zincirlerini bul (Union-Find)
        // ========================================
        const parent: number[] = [];
        for (let i = 0; i < edgeCount; i++) parent[i] = i;

        const find = (x: number): number => {
            if (parent[x] !== x) parent[x] = find(parent[x]);
            return parent[x];
        };

        const union = (x: number, y: number): void => {
            const px = find(x);
            const py = find(y);
            if (px !== py) parent[px] = py;
        };

        // Aynı vertex'te birleşen edge'leri birleştir
        for (const [, edges] of vertexToEdges) {
            for (let i = 0; i < edges.length; i++) {
                for (let j = i + 1; j < edges.length; j++) {
                    union(edges[i], edges[j]);
                }
            }
        }

        // ========================================
        // PHASE 3: Her zincir için eğrilik analizi
        // ========================================
        // Zincirleri topla
        const chains = new Map<number, number[]>(); // root → edge indices
        for (let i = 0; i < edgeCount; i++) {
            const root = find(i);
            if (!chains.has(root)) chains.set(root, []);
            chains.get(root)!.push(i);
        }

        // Her zincir için: düz mü eğri mi?
        interface ChainInfo {
            isCurved: boolean;
            radius: number;
            edgeIndices: number[];
        }
        const chainInfos = new Map<number, ChainInfo>();

        for (const [root, edgeIndices] of chains) {
            // Tek segment zinciri - eğri olamaz
            if (edgeIndices.length < 2) {
                chainInfos.set(root, { isCurved: false, radius: 0, edgeIndices });
                continue;
            }

            // Yön vektörlerini karşılaştır
            // Düz bir çizgide tüm segment'ler paralel olmalı
            let isCurved = false;
            let totalAngleChange = 0;
            const directions: THREE.Vector3[] = [];

            for (const idx of edgeIndices) {
                directions.push(edgeDataList[idx].direction.clone());
            }

            // Ardışık segment'ler arasındaki açı değişimini kontrol et
            for (let i = 0; i < directions.length - 1; i++) {
                const dot = Math.abs(directions[i].dot(directions[i + 1]));
                if (dot < 0.9999) { // ~0.8° tolerans
                    isCurved = true;
                    totalAngleChange += Math.acos(Math.min(1, dot));
                }
            }

            // Eğri ise radius hesapla
            let radius = 0;
            if (isCurved && totalAngleChange > 0.01) {
                // Toplam yay uzunluğu
                let totalLength = 0;
                for (const idx of edgeIndices) {
                    totalLength += edgeDataList[idx].length;
                }

                // R = L / θ (yay uzunluğu / açı)
                radius = totalLength / totalAngleChange;

                // Bilinen radius'larla eşleştir (daha doğru değer için)
                for (const [knownRadius] of radiusGroups) {
                    if (Math.abs(radius - knownRadius) < knownRadius * 0.2) {
                        radius = knownRadius;
                        break;
                    }
                }
            }

            chainInfos.set(root, { isCurved, radius, edgeIndices });
        }

        // ========================================
        // PHASE 4: Attribute'ları ata
        // ========================================
        let nextGroupId = 0;
        const rootToGroupId = new Map<number, number>();

        for (let edgeIdx = 0; edgeIdx < edgeCount; edgeIdx++) {
            const v1Idx = edgeIdx * 2;
            const v2Idx = edgeIdx * 2 + 1;

            // Edge ID
            const edgeId = groupAttr ? groupAttr.getZ(v1Idx) : edgeIdx;
            edgeIds[v1Idx] = edgeId;
            edgeIds[v2Idx] = edgeId;

            // Zincir bilgisi
            const root = find(edgeIdx);
            const chainInfo = chainInfos.get(root);

            if (chainInfo && chainInfo.isCurved && chainInfo.radius > 0.001) {
                // Eğri zincir - CIRCLE
                curveTypes[v1Idx] = 2;
                curveTypes[v2Idx] = 2;
                radii[v1Idx] = chainInfo.radius;
                radii[v2Idx] = chainInfo.radius;

                // Grup ID
                if (!rootToGroupId.has(root)) {
                    rootToGroupId.set(root, nextGroupId++);
                }
                const gid = rootToGroupId.get(root)!;
                radiusGroupIds[v1Idx] = gid;
                radiusGroupIds[v2Idx] = gid;
            } else {
                // Düz çizgi - LINE
                curveTypes[v1Idx] = 1;
                curveTypes[v2Idx] = 1;
            }
        }

        // Attribute'ları geometry'ye ekle
        edgeGeometry.setAttribute('curveType', new THREE.Float32BufferAttribute(curveTypes, 1));
        edgeGeometry.setAttribute('radius', new THREE.Float32BufferAttribute(radii, 1));
        edgeGeometry.setAttribute('radiusGroup', new THREE.Float32BufferAttribute(radiusGroupIds, 1));
        edgeGeometry.setAttribute('edgeId', new THREE.Float32BufferAttribute(edgeIds, 1));

        // İstatistik logla
        let circularCount = 0;
        const groupSizes = new Map<number, { count: number; radius: number }>();
        for (const [root, info] of chainInfos) {
            if (info.isCurved && info.radius > 0.001) {
                const gid = rootToGroupId.get(root)!;
                circularCount += info.edgeIndices.length;
                groupSizes.set(gid, { count: info.edgeIndices.length, radius: info.radius });
            }
        }

        // if (circularCount > 0) {
        //     console.log(`🔶 Edge topoloji (eğrilik analizi): ${circularCount}/${edgeCount} eğri edge, ${rootToGroupId.size} zincir`);
        //     for (const [gid, info] of groupSizes) {
        //         console.log(`   Zincir ${gid}: ${info.count} segment, R≈${info.radius.toFixed(2)}mm`);
        //     }
        // }

        return edgeGeometry;
    }

    /**
     * Face gruplarından radyüs grupları oluşturur (OpenCascade stili)
     * Aynı radyüse sahip tüm face'leri gruplar
     * @param faceGroups occt_faceGroups array'i
     * @param tolerance Radyüs eşleştirme toleransı (varsayılan 0.01 mm)
     * @returns Radyüs grupları Map'i: radyüs → RadiusGroupInfo
     */
    public buildRadiusGroupsFromFaceGroups(
        faceGroups: any[],
        tolerance: number = 0.01
    ): Map<number, RadiusGroupInfo> {
        const radiusGroups = new Map<number, RadiusGroupInfo>();

        if (!faceGroups || faceGroups.length === 0) {
            return radiusGroups;
        }

        for (const faceGroup of faceGroups) {
            const { faceIndex, surfaceType, radius } = faceGroup;

            // Sadece radyüs bilgisi olan yüzeyleri işle
            if (radius === undefined || radius === null || radius <= 0.001) {
                continue;
            }

            // Radyüsü yuvarla (tolerans dahilinde gruplama için)
            const roundedRadius = Math.round(radius / tolerance) * tolerance;
            const key = parseFloat(roundedRadius.toFixed(4));

            if (!radiusGroups.has(key)) {
                radiusGroups.set(key, {
                    radius: key,
                    faceIndices: [],
                    edgeIndices: [],
                    surfaceType: surfaceType || 'UNKNOWN',
                    color: this.getRadiusGroupColor(radiusGroups.size)
                });
            }

            const group = radiusGroups.get(key)!;
            group.faceIndices.push(faceIndex);
        }

        return radiusGroups;
    }

    /**
     * Edge gruplarından radyüs grupları oluşturur
     * @param edgeGroups Edge grup bilgileri
     * @param existingGroups Varolan face radyüs grupları (birleştirilir)
     * @param tolerance Radyüs eşleştirme toleransı
     * @returns Güncellenmiş radyüs grupları
     */
    public addEdgeRadiusGroups(
        edgeGroups: any[],
        existingGroups: Map<number, RadiusGroupInfo>,
        tolerance: number = 0.01
    ): Map<number, RadiusGroupInfo> {
        if (!edgeGroups || edgeGroups.length === 0) {
            return existingGroups;
        }

        for (const edgeGroup of edgeGroups) {
            const { edgeIndex, curveType, radius } = edgeGroup;

            // Sadece dairesel kenarları işle (fillet, arc vb.)
            if (!radius || radius <= 0.001) {
                continue;
            }

            // CIRCLE türündeki kenarlar fillet olabilir
            if (curveType !== 'CIRCLE' && curveType !== 'ARC') {
                continue;
            }

            const roundedRadius = Math.round(radius / tolerance) * tolerance;
            const key = parseFloat(roundedRadius.toFixed(4));

            if (!existingGroups.has(key)) {
                existingGroups.set(key, {
                    radius: key,
                    faceIndices: [],
                    edgeIndices: [],
                    surfaceType: 'CIRCLE',
                    color: this.getRadiusGroupColor(existingGroups.size)
                });
            }

            const group = existingGroups.get(key)!;
            if (!group.edgeIndices.includes(edgeIndex)) {
                group.edgeIndices.push(edgeIndex);
            }
        }

        return existingGroups;
    }

    /**
     * Radyüs grubu için renk oluşturur (gökkuşağı spektrumu)
     * @param index Grup indeksi
     * @returns RGB renk objesi
     */
    private getRadiusGroupColor(index: number): { r: number; g: number; b: number } {
        // HSL renk modeli ile farklı renkler üret
        const hue = (index * 137.5) % 360; // Altın oran ile dağıt
        const saturation = 0.8;
        const lightness = 0.5;

        // HSL → RGB dönüşümü
        const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
        const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
        const m = lightness - c / 2;

        let r = 0, g = 0, b = 0;
        if (hue < 60) { r = c; g = x; }
        else if (hue < 120) { r = x; g = c; }
        else if (hue < 180) { g = c; b = x; }
        else if (hue < 240) { g = x; b = c; }
        else if (hue < 300) { r = x; b = c; }
        else { r = c; b = x; }

        return {
            r: r + m,
            g: g + m,
            b: b + m
        };
    }

    /**
     * Radyüs gruplarını sıralı liste olarak döndürür
     * @param radiusGroups Radyüs grupları Map'i
     * @returns Sıralı RadiusGroupInfo dizisi (küçükten büyüğe)
     */
    public getSortedRadiusGroups(radiusGroups: Map<number, RadiusGroupInfo>): RadiusGroupInfo[] {
        const groups = Array.from(radiusGroups.values());
        groups.sort((a, b) => a.radius - b.radius);
        return groups;
    }

    /**
     * Belirli bir radyüs grubundaki tüm face'leri seçer/highlight eder
     * @param cizim FikoCizimYalin objesi
     * @param radius Seçilecek radyüs değeri
     * @param highlightColor Highlight rengi
     * @returns Seçilen face indeksleri
     */
    public selectFacesByRadius(
        cizim: FikoCizimYalin,
        radius: number,
        highlightColor?: THREE.Color
    ): number[] {
        const radiusGroups = (cizim as any).occt_radiusGroups as Map<number, RadiusGroupInfo>;
        if (!radiusGroups) {
            console.warn('Radyüs grupları bulunamadı - önce buildRadiusGroupsFromFaceGroups çağrılmalı');
            return [];
        }

        // Toleranslı arama
        let group: RadiusGroupInfo | undefined;
        for (const [key, grp] of radiusGroups) {
            if (Math.abs(key - radius) < 0.01) {
                group = grp;
                break;
            }
        }

        if (!group) {
            return [];
        }

        return group.faceIndices;
    }

    /**
     * Tüm radyüs gruplarını konsola yazdırır (debug için)
     * @param radiusGroups Radyüs grupları Map'i
     */
    public logRadiusGroups(radiusGroups: Map<number, RadiusGroupInfo>): void {
        // console.log('📐 Radyüs Grupları:');
        // console.log('═'.repeat(50));

        // const sorted = this.getSortedRadiusGroups(radiusGroups);
        // for (const group of sorted) {
        //     const faceCount = group.faceIndices.length;
        //     const edgeCount = group.edgeIndices.length;
        //     console.log(`  R${group.radius.toFixed(2)} mm | ${group.surfaceType} | ${faceCount} face, ${edgeCount} edge`);
        // }

        // console.log('═'.repeat(50));
        // console.log(`Toplam: ${sorted.length} farklı radyüs grubu`);
    }

    /**
     * Dispose all resources - terminate workers and clear caches
     */
    dispose(): void {
        // Terminate STP-OCCT worker
        this.terminateStpOcctWorker();

        // Terminate legacy OCCT worker
        if (this.occtWorker) {
            this.occtWorker.terminate();
            this.occtWorker = null;
        }

        // Clear WASM reference
        this.occtWasm = null;

        // Clear static caches
        FikoImporterSTP.clearCaches();

        this.batchedMeshManager?.dispose();
        this.batchedMeshManager = null;
        this.timeSlicedExecutor = null;
        this.meshOptimizer = null;

        // Clear callbacks
        this.onProgress = undefined;
        this.onStreamingProgress = undefined;
    }
}
